import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { Listbox, Transition } from '@headlessui/react';
import { 
  BarChart3,
  MapPin,
  Target,
  Activity,
  ArrowLeft,
  ArrowRight,
  Filter,
  Download,
  X,
  Search,
  TrendingUp,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Calendar,
  Building2,
  MessageSquare,
  Paperclip,
  Users,
  XCircle,
  ChevronDown,
  Check,
  ListOrdered,
  Bell,
  GraduationCap,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import ExcelJS from 'exceljs';

// Base URL for file downloads (uses :5000 only in local dev)
const getFileBaseUrl = () => {
  if (window.location.hostname === 'localhost' && window.location.port === '3000') {
    return `${window.location.protocol}//${window.location.hostname}:5000`;
  }
  return window.location.origin;
};

const FILE_BASE_URL = getFileBaseUrl();

const EPDashboard = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    taskId: '',
    subtaskId: '',
    quarterId: '',
    statusId: '',
    buildingType: '',
    trainingId: '',
    week: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data: tasks, isLoading } = useQuery(
    ['ep-tasks'],
    async () => {
      const response = await axios.get('/api/masters/dropdowns');
      return response.data?.tasks || [];
    }
  );

  // Fetch dropdown data for filters
  const { data: dropdownData } = useQuery(
    ['ep-dropdowns'],
    async () => {
      const response = await axios.get('/api/masters/dropdowns');
      return response.data || { tasks: [], subtasks: [] };
    }
  );

  // Fetch all unique weeks from database
  const { data: allWeeks } = useQuery(
    ['ep-all-weeks'],
    async () => {
      const response = await axios.get('/api/dashboard/weeks');
      return response.data;
    }
  );

  // Fetch filtered tasks - filtered by current user's cell
  const { data: filteredTasks, isLoading: isLoadingTasks, error: tasksError, isFetching: isFetchingEpTasks } = useQuery(
    ['ep-filtered-tasks', filters.taskId, filters.subtaskId, filters.quarterId, filters.statusId, filters.buildingType, filters.trainingId, filters.week, user?.cellId],
    async () => {
      const params = {}; // Don't pass cellId - server will use req.user.cellId for EP users
      if (filters.taskId) params.taskId = filters.taskId;
      if (filters.subtaskId) params.subtaskId = filters.subtaskId;
      if (filters.quarterId) params.quarterId = filters.quarterId;
      if (filters.statusId) params.statusId = filters.statusId;
      if (filters.buildingType) params.buildingType = filters.buildingType;
      if (filters.trainingId) params.trainingId = filters.trainingId;
      if (filters.week) params.week = filters.week;
      
      const response = await axios.get('/api/dashboard/tasks', { params });
      return response.data;
    },
    {
      enabled: !!user && (user.role === 'ECSBC-Professional' || user.role === 'Nodal Officer SDA'),
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0
    }
  );

  const epTasksFetchPending = isLoadingTasks || (isFetchingEpTasks && filteredTasks === undefined);

  // Softer, more muted color scheme for task cards
  const getTaskColor = (taskNo) => {
    const taskColors = {
      1: { bg: '#f97316', light: '#ffedd5', text: '#ea580c', border: '#fb923c' }, // Orange
      2: { bg: '#8b5cf6', light: '#ede9fe', text: '#7c3aed', border: '#a78bfa' }, // Purple
      3: { bg: '#ec4899', light: '#fce7f3', text: '#db2777', border: '#f9a8d4' }, // Pink
      4: { bg: '#06b6d4', light: '#cffafe', text: '#0891b2', border: '#22d3ee' }, // Cyan
      5: { bg: '#3b82f6', light: '#dbeafe', text: '#2563eb', border: '#60a5fa' }, // Blue
      6: { bg: '#14b8a6', light: '#ccfbf1', text: '#0f766e', border: '#2dd4bf' }, // Teal
      7: { bg: '#f43f5e', light: '#ffe4e6', text: '#e11d48', border: '#fb7185' }, // Rose
      8: { bg: '#a855f7', light: '#f3e8ff', text: '#9333ea', border: '#c084fc' }, // Violet
    };
    return taskColors[taskNo] || taskColors[1];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section - Blue to Purple gradient only */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-1 flex items-center gap-2">
              <Target className="h-6 w-6" />
              EP Dashboard
            </h1>
            <p className="text-blue-50 text-sm">Track your assigned tasks and submit updates</p>
          </div>
          <Link 
            to="/task-tracker" 
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-blue-600 rounded-lg text-sm font-semibold hover:bg-blue-50 transition-all shadow-md hover:shadow-lg"
          >
            Submit Updates to BEE
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Task Cards - Always visible below header */}
      <div className="space-y-4">
        {tasks && tasks.length > 0 ? (
          tasks.map((task) => {
            const colors = getTaskColor(task.task_no || task.Task_No);
            const taskNo = task.task_no || task.Task_No;
            const taskDesc = task.task_description || task.Task_Description;
            const taskId = task.task_id || task.Task_ID;
            
          return (
            <Link 
                key={taskId} 
                to={`/task/${taskNo}`}
              className="block group"
            >
              <div 
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 overflow-hidden border border-gray-200"
                style={{ 
                  borderLeftWidth: '4px',
                  borderLeftColor: colors.border
                }}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {/* Task Number Badge */}
                        <div 
                          className="flex items-center justify-center w-12 h-12 rounded-lg shadow-sm"
                            style={{ '--task-bg-color': colors.light, '--task-text-color': colors.text, backgroundColor: 'var(--task-bg-color)' }}
                        >
                          <span 
                            className="text-lg font-bold"
                              style={{ color: 'var(--task-text-color)' }}
                          >
                              {taskNo}
                          </span>
                        </div>
                        
                        {/* Task Title */}
                        <div className="flex-1">
                          <h3 
                            className="text-lg font-bold"
                              style={{ '--task-text-color': colors.text, color: 'var(--task-text-color)' }}
                          >
                              Task {taskNo}
                          </h3>
                        </div>
                      </div>
                      
                      {/* Task Description */}
                      <p className="text-gray-700 text-sm leading-relaxed pl-15">
                          {taskDesc}
                      </p>
                    </div>
                    
                    {/* Arrow Icon */}
                    <div 
                      className="flex items-center justify-center w-10 h-10 rounded-full transition-all group-hover:translate-x-1"
                        style={{ '--task-bg-color': colors.light, '--task-text-color': colors.text, backgroundColor: 'var(--task-bg-color)' }}
                    >
                      <ArrowRight 
                        className="h-5 w-5"
                          style={{ color: 'var(--task-text-color)' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          );
          })
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>Loading tasks...</p>
          </div>
        )}
      </div>

      {/* Cell-wise Task Table - Below Task 5 */}
      <EPTaskTable 
        filters={filters}
        setFilters={setFilters}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        filteredTasks={filteredTasks}
        isLoadingTasks={epTasksFetchPending}
        tasksError={tasksError}
        dropdownData={dropdownData}
        allWeeks={allWeeks}
        user={user}
      />
    </div>
  );
};

// EP Task Table Component
const EPTaskTable = ({
  filters,
  setFilters,
  searchTerm,
  setSearchTerm,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  filteredTasks,
  isLoadingTasks,
  tasksError,
  dropdownData,
  allWeeks,
  user
}) => {
  // Check if any filter is active
  const hasActiveFilters = Object.values(filters).some(val => val !== '') || searchTerm !== '';

  // Helper function to sort subtasks properly (handles numeric and alphanumeric like "1", "2a", "2b", "10", "11")
  const sortSubtasks = (subtasks) => {
    return [...subtasks].sort((a, b) => {
      // First sort by task_id
      const aTaskId = a.task_id || a.Task_ID || 0;
      const bTaskId = b.task_id || b.Task_ID || 0;
      if (aTaskId !== bTaskId) {
        return aTaskId - bTaskId;
      }
      
      // Then sort by subtask_no (handling alphanumeric)
      const aNo = String(a.subtask_no || a.Subtask_No || '');
      const bNo = String(b.subtask_no || b.Subtask_No || '');
      
      // Extract numeric part and letter part (if any)
      const aMatch = aNo.match(/^(\d+)([a-z]?)$/i);
      const bMatch = bNo.match(/^(\d+)([a-z]?)$/i);
      
      if (!aMatch || !bMatch) {
        // Fallback to string comparison if format doesn't match
        return aNo.localeCompare(bNo);
      }
      
      const aNum = parseInt(aMatch[1], 10);
      const bNum = parseInt(bMatch[1], 10);
      const aLetter = aMatch[2] || '';
      const bLetter = bMatch[2] || '';
      
      // First compare by numeric part
      if (aNum !== bNum) {
        return aNum - bNum;
      }
      
      // If numeric parts are equal, compare by letter part
      return aLetter.localeCompare(bLetter);
    });
  };

  // Filter tasks by search term
  const filteredBySearch = (filteredTasks?.filter(task => {
    // If trainingId filter is active, only show Task 3 entries
    if (filters.trainingId && filters.trainingId !== '') {
      const taskNo = task.task_no || task.Task_No;
      if (taskNo !== 3) {
        return false; // Filter out non-Task 3 entries when training filter is active
      }
    }
    
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (task.task_description ? String(task.task_description).toLowerCase() : '').includes(searchLower) ||
      (task.subtask_description ? String(task.subtask_description).toLowerCase() : '').includes(searchLower) ||
      (task.status ? String(task.status).toLowerCase() : '').includes(searchLower) ||
      (task.quarter_name ? String(task.quarter_name).toLowerCase() : '').includes(searchLower)
    );
  }) || []).sort((a, b) => {
    const taskA = a.task_no || a.Task_No || 0;
    const taskB = b.task_no || b.Task_No || 0;
    if (taskA !== taskB) return taskA - taskB;
    
    // Use proper subtask sorting
    const aNo = String(a.subtask_no || a.Subtask_No || '');
    const bNo = String(b.subtask_no || b.Subtask_No || '');
    
    // Extract numeric part and letter part (if any)
    const aMatch = aNo.match(/^(\d+)([a-z]?)$/i);
    const bMatch = bNo.match(/^(\d+)([a-z]?)$/i);
    
    if (!aMatch || !bMatch) {
      // Fallback to string comparison if format doesn't match
      return aNo.localeCompare(bNo);
    }
    
    const aNum = parseInt(aMatch[1], 10);
    const bNum = parseInt(bMatch[1], 10);
    const aLetter = aMatch[2] || '';
    const bLetter = bMatch[2] || '';
    
    // First compare by numeric part
    if (aNum !== bNum) {
      return aNum - bNum;
    }
    
    // If numeric parts are equal, compare by letter part
    return aLetter.localeCompare(bLetter);
  });

  // Helper function to expand tasks with buildings into separate rows (for Task 2 and Task 4)
  const expandTasksWithBuildings = (tasks) => {
    const expanded = [];
    const taskGroups = {};
    
    // Group tasks by task/subtask combination
    tasks.forEach(task => {
      const taskNo = task.task_no || task.Task_No;
      const subtaskNo = task.subtask_no || task.Subtask_No;
      const key = `${taskNo}_${subtaskNo}`;
      
      if (!taskGroups[key]) {
        taskGroups[key] = [];
      }
      taskGroups[key].push(task);
    });
    
    // Expand each group
    Object.keys(taskGroups).sort((a, b) => {
      const [taskA, subtaskA] = a.split('_');
      const [taskB, subtaskB] = b.split('_');
      if (taskA !== taskB) return Number(taskA) - Number(taskB);
      return String(subtaskA).localeCompare(String(subtaskB));
    }).forEach(key => {
      const groupTasks = taskGroups[key];
      const taskNo = Number(groupTasks[0].task_no || groupTasks[0].Task_No);
      const subtaskNo = groupTasks[0].subtask_no || groupTasks[0].Subtask_No;
      
      // For Task 2 and Task 4, expand buildings into separate rows
      if (taskNo === 2 || taskNo === 4) {
        // First, group by quarter to separate buildings by quarter
        const quarterGroups = {};
        const tasksWithoutQuarter = [];
        
        groupTasks.forEach(task => {
          const quarterId = task.quarter_id || task.Quarter_ID;
          if (quarterId) {
            if (!quarterGroups[quarterId]) {
              quarterGroups[quarterId] = [];
            }
            quarterGroups[quarterId].push(task);
          } else {
            tasksWithoutQuarter.push(task);
          }
        });
        
        // Process each quarter group separately
        const quarterKeys = Object.keys(quarterGroups).sort((a, b) => {
          // Sort quarters by ID (assuming lower ID = earlier quarter)
          return Number(a) - Number(b);
        });
        
        quarterKeys.forEach((quarterId) => {
          const quarterTasks = quarterGroups[quarterId];
          
          // Within each quarter, group by building
          const buildingGroups = {};
          const buildingsWithoutId = [];
          
          quarterTasks.forEach(task => {
            const buildingId = task.building_id || task.Building_ID;
            if (buildingId) {
              if (!buildingGroups[buildingId]) {
                buildingGroups[buildingId] = [];
              }
              buildingGroups[buildingId].push(task);
            } else if (task.building_name) {
              // If no building_id but has building_name, use name as key
              const buildingName = task.building_name;
              if (!buildingGroups[buildingName]) {
                buildingGroups[buildingName] = [];
              }
              buildingGroups[buildingName].push(task);
            } else {
              // Tasks without buildings in this quarter
              buildingsWithoutId.push(task);
            }
          });
          
          // Create a row for each unique building in this quarter
          const buildingKeys = Object.keys(buildingGroups);
          
          if (buildingKeys.length > 0) {
            // Sort buildings by building_id or name for consistent ordering
            buildingKeys.sort((a, b) => {
              // Try to sort by ID if both are numeric
              const aNum = parseInt(a);
              const bNum = parseInt(b);
              if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
              }
              // Otherwise sort alphabetically
              return String(a).localeCompare(String(b));
            });
            
            buildingKeys.forEach((buildingKey, buildingIdx) => {
              const buildingTasks = buildingGroups[buildingKey];
              // Use the most recent task for this building in this quarter
              const taskForRow = buildingTasks.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
                return dateB - dateA;
              })[0];
              
              expanded.push({
                ...taskForRow,
                isFirstTaskRow: false,
                taskRowSpan: buildingKeys.length + (buildingsWithoutId.length > 0 ? 1 : 0),
                taskNo: taskNo,
                buildingRowIndex: buildingIdx,
                totalBuildingRows: buildingKeys.length + (buildingsWithoutId.length > 0 ? 1 : 0),
                isFirstSubtaskRow: buildingIdx === 0 // First building row in this quarter is the first subtask row
              });
            });
          }
          
          // Add row for tasks without buildings in this quarter (if any)
          if (buildingsWithoutId.length > 0) {
            const latestNoBuilding = buildingsWithoutId.sort((a, b) => {
              const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
              const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
              return dateB - dateA;
            })[0];
            
            expanded.push({
              ...latestNoBuilding,
              isFirstTaskRow: false,
              taskRowSpan: buildingKeys.length + 1,
              taskNo: taskNo,
              buildingRowIndex: buildingKeys.length,
              totalBuildingRows: buildingKeys.length + 1,
              isFirstSubtaskRow: buildingKeys.length === 0 // Only first if no buildings before it
            });
          }
        });
        
        // Handle tasks without quarter (if any)
        if (tasksWithoutQuarter.length > 0) {
          // Group by building for tasks without quarter
          const buildingGroups = {};
          const buildingsWithoutId = [];
          
          tasksWithoutQuarter.forEach(task => {
            const buildingId = task.building_id || task.Building_ID;
            if (buildingId) {
              if (!buildingGroups[buildingId]) {
                buildingGroups[buildingId] = [];
              }
              buildingGroups[buildingId].push(task);
            } else if (task.building_name) {
              const buildingName = task.building_name;
              if (!buildingGroups[buildingName]) {
                buildingGroups[buildingName] = [];
              }
              buildingGroups[buildingName].push(task);
            } else {
              buildingsWithoutId.push(task);
            }
          });
          
          const buildingKeys = Object.keys(buildingGroups);
          
          if (buildingKeys.length > 0) {
            buildingKeys.sort((a, b) => {
              const aNum = parseInt(a);
              const bNum = parseInt(b);
              if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
              }
              return String(a).localeCompare(String(b));
            });
            
            buildingKeys.forEach((buildingKey, buildingIdx) => {
              const buildingTasks = buildingGroups[buildingKey];
              const taskForRow = buildingTasks.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
                return dateB - dateA;
              })[0];
              
              expanded.push({
                ...taskForRow,
                isFirstTaskRow: false,
                taskRowSpan: buildingKeys.length + (buildingsWithoutId.length > 0 ? 1 : 0),
                taskNo: taskNo,
                buildingRowIndex: buildingIdx,
                totalBuildingRows: buildingKeys.length + (buildingsWithoutId.length > 0 ? 1 : 0),
                isFirstSubtaskRow: buildingIdx === 0
              });
            });
          }
          
          if (buildingsWithoutId.length > 0) {
            const latestNoBuilding = buildingsWithoutId.sort((a, b) => {
              const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
              const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
              return dateB - dateA;
            })[0];
            
            expanded.push({
              ...latestNoBuilding,
              isFirstTaskRow: false,
              taskRowSpan: buildingKeys.length + 1,
              taskNo: taskNo,
              buildingRowIndex: buildingKeys.length,
              totalBuildingRows: buildingKeys.length + 1,
              isFirstSubtaskRow: buildingKeys.length === 0
            });
          }
        }
        
        // If no buildings at all, add one row
        if (quarterKeys.length === 0 && tasksWithoutQuarter.length === 0 && groupTasks.length > 0) {
          expanded.push({
            ...groupTasks[0],
            isFirstTaskRow: false,
            taskRowSpan: 1,
            taskNo: taskNo
          });
        }
      } else {
        // For Task 3, pass through all entries (they will be processed by expandTasksWithTrainings)
        // For other tasks, use the most recent entry
        if (taskNo === 3) {
          // Pass through all Task 3 entries unchanged
          groupTasks.forEach(task => {
            expanded.push({
              ...task,
              isFirstTaskRow: false,
              taskRowSpan: 1,
              taskNo: taskNo
            });
          });
        } else {
          // For other tasks (not 2, 3, or 4), use the most recent entry
          const sortedTasks = [...groupTasks].sort((a, b) => {
            const dateA = new Date(a.submission_date || a.Saved_Date || 0);
            const dateB = new Date(b.submission_date || b.Saved_Date || 0);
            return dateB - dateA;
          });
          expanded.push({
            ...sortedTasks[0],
            isFirstTaskRow: false,
            taskRowSpan: 1,
            taskNo: taskNo
          });
        }
      }
    });
    
    return expanded;
  };

  // Helper function to expand Task 3 trainings into separate rows
  const expandTasksWithTrainings = (tasks) => {
    const expanded = [];
    const taskGroups = {};
    
    // Group tasks by task/subtask combination
    tasks.forEach(task => {
      const taskNo = task.task_no || task.Task_No;
      const subtaskNo = task.subtask_no || task.Subtask_No;
      const key = `${taskNo}_${subtaskNo}`;
      
      if (!taskGroups[key]) {
        taskGroups[key] = [];
      }
      taskGroups[key].push(task);
    });
    
    // Expand each group
    Object.keys(taskGroups).sort((a, b) => {
      const [taskA, subtaskA] = a.split('_');
      const [taskB, subtaskB] = b.split('_');
      if (taskA !== taskB) return Number(taskA) - Number(taskB);
      return String(subtaskA).localeCompare(String(subtaskB));
    }).forEach(key => {
      const groupTasks = taskGroups[key];
      const taskNo = Number(groupTasks[0].task_no || groupTasks[0].Task_No);
      const subtaskNo = groupTasks[0].subtask_no || groupTasks[0].Subtask_No;
      
      // For Task 3, expand trainings into separate rows (for subtasks 2, 3, 4, 5)
      if (taskNo === 3) {
        const subtaskNoStr = String(subtaskNo || '').toLowerCase();
        const shouldExpand = subtaskNoStr.startsWith('2') || subtaskNoStr.startsWith('3') || subtaskNoStr.startsWith('4') || subtaskNoStr.startsWith('5');
        
        if (shouldExpand) {
          // First, group by quarter to separate trainings by quarter
          const quarterGroups = {};
          const tasksWithoutQuarter = [];
          
          groupTasks.forEach(task => {
            const quarterId = task.quarter_id || task.Quarter_ID;
            if (quarterId) {
              if (!quarterGroups[quarterId]) {
                quarterGroups[quarterId] = [];
              }
              quarterGroups[quarterId].push(task);
            } else {
              tasksWithoutQuarter.push(task);
            }
          });
          
          // Process each quarter group separately
          const quarterKeys = Object.keys(quarterGroups).sort((a, b) => {
            // Sort quarters by ID (assuming lower ID = earlier quarter)
            return Number(a) - Number(b);
          });
          
          quarterKeys.forEach((quarterId) => {
            const quarterTasks = quarterGroups[quarterId];
            
            // Within each quarter, group by training
            const trainingGroups = {};
            const trainingsWithoutId = [];
            
            quarterTasks.forEach(task => {
              const trainingId = task.training_id || task.Training_ID;
              if (trainingId) {
                if (!trainingGroups[trainingId]) {
                  trainingGroups[trainingId] = [];
                }
                trainingGroups[trainingId].push(task);
              } else {
                // Tasks without trainings in this quarter
                trainingsWithoutId.push(task);
              }
            });
            
            // Show ALL training entries for this quarter (not just latest per training_id)
            const allTrainingEntries = [];
            Object.keys(trainingGroups).forEach(trainingId => {
              const entries = trainingGroups[trainingId];
              // Sort entries by date (most recent first) but include ALL entries
              entries.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
                return dateB - dateA;
              });
              // Add ALL entries, not just the latest one
              entries.forEach(entry => {
                allTrainingEntries.push({
                  ...entry,
                  trainingGroupId: trainingId
                });
              });
            });
            
            // Sort all trainings by training_id, then by submission date
            allTrainingEntries.sort((a, b) => {
              const aId = parseInt(a.trainingGroupId);
              const bId = parseInt(b.trainingGroupId);
              if (!isNaN(aId) && !isNaN(bId) && aId !== bId) {
                return aId - bId;
              }
              // If same training_id, sort by date (most recent first)
              const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
              const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
              return dateB - dateA;
            });
            
            const totalTrainingRows = allTrainingEntries.length + (trainingsWithoutId.length > 0 ? 1 : 0);
            
            if (allTrainingEntries.length > 0) {
              allTrainingEntries.forEach((taskForRow, trainingIdx) => {
                expanded.push({
                  ...taskForRow,
                  // Normalize: Each row contains all its own data
                  task_no: taskNo,
                  Task_No: taskNo,
                  subtask_no: subtaskNo,
                  Subtask_No: subtaskNo,
                  subtask_description: groupTasks[0].subtask_description || groupTasks[0].Subtask_Description,
                  Subtask_Description: groupTasks[0].subtask_description || groupTasks[0].Subtask_Description,
                  // Remove rowSpan flags - each row is independent
                  isFirstTaskRow: false,
                  isFirstSubtaskRow: false
                });
              });
            }
            
            // Add row for tasks without trainings in this quarter (if any)
            if (trainingsWithoutId.length > 0) {
              const latestNoTraining = trainingsWithoutId.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
                return dateB - dateA;
              })[0];
              
              expanded.push({
                ...latestNoTraining,
                isFirstTaskRow: false,
                taskRowSpan: totalTrainingRows,
                taskNo: taskNo,
                trainingRowIndex: allTrainingEntries.length,
                totalTrainingRows: totalTrainingRows,
                isFirstSubtaskRow: allTrainingEntries.length === 0 // Only first if no trainings before it
              });
            }
          });
          
          // Handle tasks without quarter (if any)
          if (tasksWithoutQuarter.length > 0) {
            // Group by training for tasks without quarter
            const trainingGroups = {};
            const trainingsWithoutId = [];
            
            tasksWithoutQuarter.forEach(task => {
              const trainingId = task.training_id || task.Training_ID;
              if (trainingId) {
                if (!trainingGroups[trainingId]) {
                  trainingGroups[trainingId] = [];
                }
                trainingGroups[trainingId].push(task);
              } else {
                trainingsWithoutId.push(task);
              }
            });
            
            // Create a row for each unique training (latest entry per training_id)
            const uniqueTrainingEntriesNoQuarter = [];
            Object.keys(trainingGroups).forEach(trainingId => {
              const entries = trainingGroups[trainingId];
              // Sort entries by date (most recent first) and take only the latest one
              entries.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
                return dateB - dateA;
              });
              // Take only the latest entry (first one after sorting)
              uniqueTrainingEntriesNoQuarter.push({
                ...entries[0],
                trainingGroupId: trainingId,
                totalInGroup: entries.length
              });
            });
            
            // Sort unique trainings by training_id
            uniqueTrainingEntriesNoQuarter.sort((a, b) => {
              const aId = parseInt(a.trainingGroupId);
              const bId = parseInt(b.trainingGroupId);
              if (!isNaN(aId) && !isNaN(bId)) {
                return aId - bId;
              }
              return 0;
            });
            
            const totalTrainingRowsNoQuarter = uniqueTrainingEntriesNoQuarter.length + (trainingsWithoutId.length > 0 ? 1 : 0);
            
            if (uniqueTrainingEntriesNoQuarter.length > 0) {
              uniqueTrainingEntriesNoQuarter.forEach((taskForRow, trainingIdx) => {
                expanded.push({
                  ...taskForRow,
                  isFirstTaskRow: false,
                  taskRowSpan: totalTrainingRowsNoQuarter,
                  taskNo: taskNo,
                  trainingRowIndex: trainingIdx,
                  totalTrainingRows: totalTrainingRowsNoQuarter,
                  isFirstSubtaskRow: trainingIdx === 0
                });
              });
            }
            
            if (trainingsWithoutId.length > 0) {
              const latestNoTraining = trainingsWithoutId.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
                return dateB - dateA;
              })[0];
              
              expanded.push({
                ...latestNoTraining,
                isFirstTaskRow: false,
                taskRowSpan: totalTrainingRowsNoQuarter,
                taskNo: taskNo,
                trainingRowIndex: uniqueTrainingEntriesNoQuarter.length,
                totalTrainingRows: totalTrainingRowsNoQuarter,
                isFirstSubtaskRow: uniqueTrainingEntriesNoQuarter.length === 0
              });
            }
          }
          
          // If no trainings at all, add one row
          if (quarterKeys.length === 0 && tasksWithoutQuarter.length === 0 && groupTasks.length > 0) {
            expanded.push({
              ...groupTasks[0],
              isFirstTaskRow: false,
              taskRowSpan: 1,
              taskNo: taskNo,
              trainingRowIndex: 0,
              totalTrainingRows: 1
            });
          }
        } else {
          // For Task 3 subtasks that don't need expansion, just add the latest entry
          const latest = groupTasks.sort((a, b) => {
            const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
            const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
            return dateB - dateA;
          })[0];
          
          expanded.push({
            ...latest,
            isFirstTaskRow: false,
            taskRowSpan: 1,
            taskNo: taskNo,
            trainingRowIndex: 0,
            totalTrainingRows: 1
          });
        }
      } else {
        // For non-Task 3 tasks, just add them as-is (they were already processed by expandTasksWithBuildings)
        groupTasks.forEach(task => {
          expanded.push(task);
        });
      }
    });
    
    return expanded;
  };

  // Helper function to group tasks hierarchically
  const groupTasksHierarchically = (tasks, previousPageLastTaskKey = null) => {
    if (!tasks || tasks.length === 0) return [];
    
    // First expand tasks with buildings, then expand tasks with trainings
    const expandedWithBuildings = expandTasksWithBuildings(tasks);
    const expandedTasks = expandTasksWithTrainings(expandedWithBuildings);
    
    const taskGroups = {};
    expandedTasks.forEach(task => {
      const taskNo = task.task_no || task.Task_No;
      if (!taskGroups[taskNo]) {
        taskGroups[taskNo] = [];
      }
      taskGroups[taskNo].push(task);
    });
    
    const result = [];
    let previousTaskKey = previousPageLastTaskKey;
    
    Object.keys(taskGroups).sort((a, b) => Number(a) - Number(b)).forEach(taskNo => {
      // Sort subtasks within each task group using proper numeric/alphanumeric sorting
      const subtasks = sortSubtasks(taskGroups[taskNo]);
      const isNewTask = previousTaskKey !== taskNo;
      previousTaskKey = taskNo;
      
      // Group by subtask to calculate rowspans correctly
      const subtaskGroups = {};
      subtasks.forEach(subtask => {
        const subtaskNo = subtask.subtask_no || subtask.Subtask_No;
        if (!subtaskGroups[subtaskNo]) {
          subtaskGroups[subtaskNo] = [];
        }
        subtaskGroups[subtaskNo].push(subtask);
      });
      
      let taskRowIndex = 0;
      Object.keys(subtaskGroups).sort((a, b) => {
        const aMatch = String(a).match(/^(\d+)([a-z]?)$/i);
        const bMatch = String(b).match(/^(\d+)([a-z]?)$/i);
        if (!aMatch || !bMatch) return String(a).localeCompare(String(b));
        const aNum = parseInt(aMatch[1], 10);
        const bNum = parseInt(bMatch[1], 10);
        if (aNum !== bNum) return aNum - bNum;
        return (aMatch[2] || '').localeCompare(bMatch[2] || '');
      }).forEach(subtaskNo => {
        const subtaskRows = subtaskGroups[subtaskNo];
        const totalRowsForSubtask = subtaskRows.length;
        
        subtaskRows.forEach((subtask, idx) => {
          result.push({
            ...subtask,
            isFirstTaskRow: isNewTask && taskRowIndex === 0,
            taskRowSpan: subtasks.length,
            taskNo: Number(taskNo),
            isFirstSubtaskRow: idx === 0,
            subtaskRowSpan: totalRowsForSubtask
          });
          taskRowIndex++;
        });
      });
    });
    
    return result;
  };

  // Helper function to check if training columns should be shown
  const shouldShowTrainingColumns = (task) => {
    const taskNo = task.task_no || task.Task_No;
    const subtaskNo = task.subtask_no || task.Subtask_No;
    
    if (taskNo === 3) {
      const subtaskNoStr = String(subtaskNo || '').toLowerCase();
      // Show training columns for Subtasks 2, 3, 4, and 5 (Training-A, Training-B, Training-C)
      return subtaskNoStr.startsWith('2') || subtaskNoStr.startsWith('3') || subtaskNoStr.startsWith('4') || subtaskNoStr.startsWith('5');
    }
    return false;
  };

  // Calculate maximum task number from all tasks (not just filtered)
  const maxTaskNo = useMemo(() => {
    if (!dropdownData?.tasks?.length) return 0;
    return Math.max(...(dropdownData.tasks || []).map(t => t.Task_No || t.task_no || 0));
  }, [dropdownData]);

  // Task-aware pagination: Each page shows all subtasks of one task
  const { taskGroups, paginatedTasks, totalPages, startRow, endRow } = useMemo(() => {
    // Group tasks by task number
    const groups = {};
    filteredBySearch.forEach(task => {
      const taskNo = task.task_no || task.Task_No;
      if (!groups[taskNo]) {
        groups[taskNo] = {
          taskNo,
          tasks: []
        };
      }
      groups[taskNo].tasks.push(task);
    });
    
    // Convert to array and sort by task number
    const sortedTaskGroups = Object.values(groups).sort((a, b) => {
      const taskA = a.taskNo || 0;
      const taskB = b.taskNo || 0;
      return taskA - taskB;
    });
    
    // Each task gets its own page (page 1 = Task 1, page 2 = Task 2, etc.)
    const totalPages = Math.max(1, sortedTaskGroups.length);
    
    // Get tasks for current page (one task per page)
    const currentTaskGroup = sortedTaskGroups[currentPage - 1];
    const paginatedTasks = currentTaskGroup ? currentTaskGroup.tasks : [];
    
    // Calculate start and end row numbers for display
    let startRow = 1;
    let endRow = paginatedTasks.length;
    if (currentPage > 1) {
      // Calculate rows before current page
      let rowsBefore = 0;
      for (let i = 0; i < currentPage - 1; i++) {
        rowsBefore += sortedTaskGroups[i].tasks.length;
      }
      startRow = rowsBefore + 1;
      endRow = rowsBefore + paginatedTasks.length;
    }
    
    return {
      taskGroups: sortedTaskGroups,
      paginatedTasks,
      totalPages,
      startRow,
      endRow
    };
  }, [filteredBySearch, currentPage]);
  
  // Get the last task from previous page to check if task continues
  const previousPageLastTask = useMemo(() => {
    if (currentPage > 1 && taskGroups[currentPage - 2]) {
      const previousTaskGroup = taskGroups[currentPage - 2];
      return previousTaskGroup.tasks[previousTaskGroup.tasks.length - 1];
    }
    return null;
  }, [taskGroups, currentPage]);
  
  const previousPageLastTaskKey = previousPageLastTask 
    ? (previousPageLastTask.task_no || previousPageLastTask.Task_No)
    : null;
  
  const hierarchicalTasks = groupTasksHierarchically(paginatedTasks, previousPageLastTaskKey);

  // Check if Task 3 is present
  const hasTask3 = hierarchicalTasks.some(task => {
    const taskNo = task.task_no || task.Task_No;
    return taskNo === 3;
  });

  const hasTrainingType = hasTask3 && hierarchicalTasks.some(task => {
    if (!shouldShowTrainingColumns(task)) return false;
    const trainingType = task.training_type || task.Training_Type;
    return trainingType && String(trainingType).trim() !== '';
  });

  // Helper function to check if building columns should be shown (Task 2 and Task 4)
  const shouldShowBuildingColumns = (task) => {
    const taskNo = task.task_no || task.Task_No;
    // Handle both string and number comparisons
    const taskNum = Number(taskNo);
    return taskNum === 2 || taskNum === 4;
  };

  // Check if Task 2 or Task 4 is present (show building columns for these tasks, similar to training columns for Task 3)
  const hasBuildingData = useMemo(() => {
    return hierarchicalTasks.some(task => {
      const taskNo = task.task_no || task.Task_No;
      // Handle both string and number comparisons
      const taskNum = Number(taskNo);
      return taskNum === 2 || taskNum === 4;
    });
  }, [hierarchicalTasks]);

  // Check if Tasks 2, 3, or 4 are present (for Summary column)
  const hasTask234 = useMemo(() => {
    return hierarchicalTasks.some(task => {
      const taskNo = task.task_no || task.Task_No;
      return taskNo === 2 || taskNo === 3 || taskNo === 4;
    });
  }, [hierarchicalTasks]);

  // Check if Task 1 is present (for Task 1 specific columns)
  const hasTask1 = useMemo(() => {
    return hierarchicalTasks.some(task => {
      const taskNo = task.task_no || task.Task_No;
      return taskNo === 1;
    });
  }, [hierarchicalTasks]);

  const epTableScrollBottomRef = useRef(null);
  const epTableScrollTopRef = useRef(null);
  const epTableScrollSpacerRef = useRef(null);
  const epTableScrollBottomMirrorRef = useRef(null);
  const epTableScrollBottomMirrorSpacerRef = useRef(null);
  const epTableScrollSyncLock = useRef(false);

  const handleEpTableScrollSync = useCallback((e) => {
    const source = e.currentTarget;
    const bottom = epTableScrollBottomRef.current;
    const top = epTableScrollTopRef.current;
    const mirror = epTableScrollBottomMirrorRef.current;
    if (!bottom || epTableScrollSyncLock.current) return;
    epTableScrollSyncLock.current = true;
    const x = source.scrollLeft;
    if (source !== bottom) bottom.scrollLeft = x;
    if (top && source !== top) top.scrollLeft = x;
    if (mirror && source !== mirror) mirror.scrollLeft = x;
    requestAnimationFrame(() => {
      epTableScrollSyncLock.current = false;
    });
  }, []);

  const handleEpTableScrollSyncFromTop = handleEpTableScrollSync;
  const handleEpTableScrollSyncFromBottom = handleEpTableScrollSync;

  useEffect(() => {
    if (isLoadingTasks || tasksError || filteredBySearch.length === 0) return;

    const bottom = epTableScrollBottomRef.current;
    const spacer = epTableScrollSpacerRef.current;
    const top = epTableScrollTopRef.current;
    const mirror = epTableScrollBottomMirrorRef.current;
    const mirrorSpacer = epTableScrollBottomMirrorSpacerRef.current;
    if (!bottom || !spacer) return;

    const tableEl = bottom.querySelector('table');
    let pendingRaf = null;

    const applySpacer = () => {
      const w = `${bottom.scrollWidth}px`;
      spacer.style.width = w;
      if (mirrorSpacer) mirrorSpacer.style.width = w;
      const x = bottom.scrollLeft;
      if (top) top.scrollLeft = x;
      if (mirror) mirror.scrollLeft = x;
    };

    const ro = new ResizeObserver(() => {
      if (pendingRaf != null) cancelAnimationFrame(pendingRaf);
      pendingRaf = requestAnimationFrame(() => {
        pendingRaf = null;
        ro.disconnect();
        applySpacer();
        ro.observe(bottom);
        if (tableEl) ro.observe(tableEl);
      });
    });

    applySpacer();
    const rafId = requestAnimationFrame(() => applySpacer());
    ro.observe(bottom);
    if (tableEl) ro.observe(tableEl);

    return () => {
      if (pendingRaf != null) cancelAnimationFrame(pendingRaf);
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [
    isLoadingTasks,
    tasksError,
    filteredBySearch.length,
    hierarchicalTasks,
    currentPage,
    hasTask1,
    hasTrainingType,
    hasBuildingData,
    hasTask234
  ]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Handler for search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  
  // Handler for clearing search
  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };
  
  // Handler for page navigation
  const handlePagePrev = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };
  
  const handlePageNext = (totalPages) => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      taskId: '',
      subtaskId: '',
      quarterId: '',
      statusId: '',
      buildingType: '',
      trainingId: '',
      week: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Helper function to strip HTML tags and convert to plain text
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // Create a temporary div element
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Get text content and replace multiple spaces/newlines with single space
    let text = tmp.textContent || tmp.innerText || '';
    // Clean up whitespace
    text = text.replace(/\s+/g, ' ').trim();
    return text;
  };

  // Helper function to calculate summary for Tasks 2, 3, and 4
  const calculateSummary = (task, allTasks) => {
    const taskNo = task.task_no || task.Task_No;
    const subtaskNo = task.subtask_no || task.Subtask_No;
    const cellKey = `${task.state_name || ''}_${task.cell_no || ''}`;
    
    // Only calculate summary for Tasks 2, 3, and 4
    if (taskNo !== 2 && taskNo !== 3 && taskNo !== 4) {
      return null;
    }
    
    // Filter tasks for the same cell
    const cellTasks = allTasks.filter(t => 
      `${t.state_name || ''}_${t.cell_no || ''}` === cellKey
    );
    
    if (taskNo === 2) {
      // Task 2: Count commercial projects for subtasks 2 and 3
      const subtaskNoStr = String(subtaskNo || '').toLowerCase();
      
      if (subtaskNoStr.startsWith('2')) {
        // Subtask 2: Total No. of ECSBC Commercial Projects
        // Count unique buildings in Subtask 2
        const uniqueBuildings = new Set();
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          // Must be Task 2, Subtask 2
          if (tTaskNo === 2 && tSubtaskNo.startsWith('2')) {
            const buildingId = t.building_id || t.Building_ID;
            if (buildingId) {
              uniqueBuildings.add(buildingId);
            } else if (t.building_name) {
              // Use building name as fallback identifier
              uniqueBuildings.add(t.building_name);
            }
          }
        });
        const count = uniqueBuildings.size;
        return count > 0 ? `Total ECSBC Commercial Building(s): ${count}` : '-';
      } else if (subtaskNoStr.startsWith('3')) {
        // Subtask 3: Total No. of ECSBC Residential Projects
        // Count unique buildings in Subtask 3
        const uniqueBuildings = new Set();
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          if (tTaskNo === 2 && tSubtaskNo.startsWith('3')) {
            const buildingId = t.building_id || t.Building_ID;
            if (buildingId) {
              uniqueBuildings.add(buildingId);
            } else if (t.building_name) {
              // Use building name as fallback identifier
              uniqueBuildings.add(t.building_name);
            }
          }
        });
        const count = uniqueBuildings.size;
        return count > 0 ? `Total ECSBC Residential Building(s): ${count}` : '-';
      }
    } else if (taskNo === 3) {
      // Task 3: Count unique trainings for subtasks 2, 3, 4, and 5 (Training-A, Training-B, Training-C)
      const subtaskNoStr = String(subtaskNo || '').toLowerCase();
      
      if (subtaskNoStr.startsWith('2') || subtaskNoStr.startsWith('3') || subtaskNoStr.startsWith('4') || subtaskNoStr.startsWith('5')) {
        // Count ALL training entries in this subtask (not just unique training IDs)
        const matchingTasks = [];
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          // Must be Task 3, same subtask
          if (tTaskNo === 3 && tSubtaskNo.startsWith(subtaskNoStr.charAt(0))) {
            const trainingId = t.training_id || t.Training_ID;
            // Only count tasks that have a training_id (actual training entries)
            if (trainingId) {
              matchingTasks.push({
                TaskDetails_ID: t.id || t.ID,
                Training_ID: trainingId,
                Subtask_No: tSubtaskNo,
                Quarter_ID: t.quarter_id || t.Quarter_ID
              });
            }
          }
        });
        // Count all matching training entries, not just unique training IDs
        const count = matchingTasks.length;
        
        // Get the actual training type from the task data
        let trainingTypeName = '';
        const taskWithTraining = cellTasks.find(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          return tTaskNo === 3 && tSubtaskNo.startsWith(subtaskNoStr.charAt(0)) && (t.training_type || t.Training_Type);
        });
        
        if (taskWithTraining && (taskWithTraining.training_type || taskWithTraining.Training_Type)) {
          trainingTypeName = taskWithTraining.training_type || taskWithTraining.Training_Type;
        } else {
          // Fallback to training type names if training type is not available
          if (subtaskNoStr.startsWith('2')) {
            trainingTypeName = 'Awareness Training';
          } else if (subtaskNoStr.startsWith('3')) {
            trainingTypeName = 'Training-A';
          } else if (subtaskNoStr.startsWith('4')) {
            trainingTypeName = 'Training-B';
          } else if (subtaskNoStr.startsWith('5')) {
            trainingTypeName = 'Training-C';
          }
        }
        
        return count > 0 ? `Total ${trainingTypeName}: ${count}` : '-';
      }
    } else if (taskNo === 4) {
      // Task 4: Count star ratings and Shunya labelling
      const subtaskNoStr = String(subtaskNo || '').toLowerCase();
      
      if (subtaskNoStr === '1' || subtaskNoStr.startsWith('1')) {
        // Subtask 1: Total star rating commercial Buildings
        // Count unique buildings in Subtask 1
        const uniqueBuildings = new Set();
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          // Must be Task 4, Subtask 1
          if (tTaskNo === 4 && (tSubtaskNo === '1' || tSubtaskNo.startsWith('1'))) {
            const buildingId = t.building_id || t.Building_ID;
            if (buildingId) {
              uniqueBuildings.add(buildingId);
            } else if (t.building_name) {
              // Use building name as fallback identifier
              uniqueBuildings.add(t.building_name);
            }
          }
        });
        const count = uniqueBuildings.size;
        return count > 0 ? `Total Star Rating Commercial Buildings: ${count}` : '-';
      } else if (subtaskNoStr === '2' || subtaskNoStr.startsWith('2')) {
        // Subtask 2: Total star rating Residential Buildings
        // Count unique buildings in Subtask 2
        const uniqueBuildings = new Set();
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          if (tTaskNo === 4 && (tSubtaskNo === '2' || tSubtaskNo.startsWith('2'))) {
            const buildingId = t.building_id || t.Building_ID;
            if (buildingId) {
              uniqueBuildings.add(buildingId);
            } else if (t.building_name) {
              // Use building name as fallback identifier
              uniqueBuildings.add(t.building_name);
            }
          }
        });
        const count = uniqueBuildings.size;
        return count > 0 ? `Total Star Rating Residential Buildings: ${count}` : '-';
      } else if (subtaskNoStr === '3' || subtaskNoStr.startsWith('3')) {
        // Subtask 3: Total Shunya labelling - count unique buildings
        const uniqueBuildings = new Set();
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          if (tTaskNo === 4 && (tSubtaskNo === '3' || tSubtaskNo.startsWith('3'))) {
            const buildingId = t.building_id || t.Building_ID;
            if (buildingId) {
              uniqueBuildings.add(buildingId);
            } else if (t.building_name) {
              // Use building name as fallback identifier
              uniqueBuildings.add(t.building_name);
            }
          }
        });
        const count = uniqueBuildings.size;
        return count > 0 ? `Total Shunya Labelling (Subtask 3): ${count}` : '-';
      }
    }
    
    return null;
  };

  // Helper function to strip HTML tags while preserving line breaks
  const stripHtmlTagsPreserveLines = (html) => {
    if (!html) return '';
    // Create a temporary div element
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Get text content (this preserves line breaks from block elements)
    let text = tmp.textContent || tmp.innerText || '';
    // Clean up: replace multiple spaces with single space, but preserve newlines
    text = text.replace(/[ \t]+/g, ' '); // Replace multiple spaces/tabs with single space
    text = text.replace(/\n\s*\n/g, '\n'); // Replace multiple newlines with single newline
    return text.trim();
  };

  // Safe React component to render remarks with highlighted stakeholder name
  const RemarksWithStakeholder = ({ remarks }) => {
    if (!remarks) return null;
    
    // Strip HTML tags from remarks
    const cleanRemarks = stripHtmlTagsPreserveLines(remarks);
    
    // Check if remarks contain stakeholder name prefix
    if (cleanRemarks.includes('STAKEHOLDER_NAME:')) {
      // Split by newlines to handle the format
      const lines = cleanRemarks.split('\n');
      const firstLine = lines[0];
      
      if (firstLine.startsWith('STAKEHOLDER_NAME:')) {
        const stakeholderName = firstLine.replace('STAKEHOLDER_NAME:', '').trim();
        const remainingRemarks = lines.slice(1).join('\n').replace(/^\n+/, '');
        
        return (
          <>
            <div className="stakeholder-badge-container">
              <span className="stakeholder-badge">
                <span className="stakeholder-label">Stakeholder:</span>
                <span className="stakeholder-name">{stakeholderName}</span>
          </span>
            </div>
            {remainingRemarks && (
              <div className="whitespace-pre-wrap">{remainingRemarks}</div>
            )}
          </>
        );
      }
    }
    
    // If no stakeholder name, return remarks as is (without HTML tags)
    return <div className="whitespace-pre-wrap">{cleanRemarks}</div>;
  };

  // Export to Excel function for EP Dashboard
  // Helper function to add data to a worksheet
  const addDataToWorksheet = (worksheet, hierarchicalTasks, tasksForSummary, hasTrainingType, hasBuildingData, hasTask234, hasTask1) => {
    // Track merged cell ranges for tasks
    const taskRanges = [];
    let currentTaskStartRow = 2;
    let previousTaskKey = null;
    
    // Track merged cell ranges for subtasks (Task 2, 3, 4)
    const subtaskRanges = [];
    let currentSubtaskStartRow = 2;
    let previousSubtaskKey = null;
    
    // Track merged cell ranges for quarters (Task 2, 3, 4)
    const quarterRanges = [];
    let currentQuarterStartRow = 2;
    let previousQuarterKey = null;
    
    // Track merged cell ranges for summary (Task 2, 3, 4)
    const summaryRanges = [];
    let currentSummaryStartRow = 2;
    let previousSummaryKey = null;

    // Helper function to parse document paths
    const parseDocumentPaths = (documentPath) => {
      if (!documentPath) return [];
      try {
        if (typeof documentPath === 'string') {
          // Try to parse as JSON array
          if (documentPath.startsWith('[') && documentPath.endsWith(']')) {
            return JSON.parse(documentPath);
          }
          // Otherwise, treat as single path
          return [documentPath];
        }
        return [];
      } catch (e) {
        // If parsing fails, treat as single path
        return typeof documentPath === 'string' ? [documentPath] : [];
      }
    };

    // Add data rows
    hierarchicalTasks.forEach((task, index) => {
      const rowIndex = index + 2; // +2 because row 1 is header
      const taskKey = `${task.task_no || task.Task_No || ''}`;
      const taskNo = task.task_no || task.Task_No;
      const subtaskKey = `${taskNo}_${task.subtask_no || task.Subtask_No || ''}`;
      const quarterKey = `${subtaskKey}_${task.quarter_id || task.Quarter_ID || task.quarter_name || ''}`;
      const shouldMergeSubtaskSummary = taskNo === 2 || taskNo === 3 || taskNo === 4;

      // Check if we need to merge previous task
      if (previousTaskKey && previousTaskKey !== taskKey) {
        if (currentTaskStartRow < rowIndex) {
          taskRanges.push({ start: currentTaskStartRow, end: rowIndex - 1 });
        }
        currentTaskStartRow = rowIndex;
      } else if (previousTaskKey === null || previousTaskKey !== taskKey) {
        currentTaskStartRow = rowIndex;
      }
      
      // Track subtask ranges for Task 2, 3, 4
      if (shouldMergeSubtaskSummary) {
        if (previousSubtaskKey && previousSubtaskKey !== subtaskKey) {
          if (currentSubtaskStartRow < rowIndex) {
            subtaskRanges.push({ start: currentSubtaskStartRow, end: rowIndex - 1 });
            summaryRanges.push({ start: currentSummaryStartRow, end: rowIndex - 1 });
          }
          currentSubtaskStartRow = rowIndex;
          currentSummaryStartRow = rowIndex;
          currentQuarterStartRow = rowIndex;
        } else if (previousSubtaskKey === null || previousSubtaskKey !== subtaskKey) {
          currentSubtaskStartRow = rowIndex;
          currentSummaryStartRow = rowIndex;
          currentQuarterStartRow = rowIndex;
        }
        
        // Track quarter ranges within the same subtask
        if (previousQuarterKey && previousQuarterKey !== quarterKey && previousSubtaskKey === subtaskKey) {
          if (currentQuarterStartRow < rowIndex) {
            quarterRanges.push({ start: currentQuarterStartRow, end: rowIndex - 1 });
          }
          currentQuarterStartRow = rowIndex;
        } else if (previousQuarterKey === null || (previousQuarterKey !== quarterKey && previousSubtaskKey === subtaskKey)) {
          currentQuarterStartRow = rowIndex;
        }
      }
      
      // Combine Task No with Task Description
      const taskDescription = task.isFirstTaskRow 
        ? ((task.task_no || task.Task_No) ? `Task ${task.task_no || task.Task_No}: ${task.task_description || ''}` : (task.task_description || ''))
        : '';
      
      // Combine Subtask No with Subtask Description
      // For Task 2, 3, 4: only show subtask description on first row of subtask group
      const subtaskDescription = (shouldMergeSubtaskSummary && !task.isFirstSubtaskRow) 
        ? '' 
        : ((task.subtask_no || task.Subtask_No) 
          ? `Subtask ${task.subtask_no || task.Subtask_No}: ${task.subtask_description || ''}`
          : (task.subtask_description || ''));

      // For Task 2, 3, 4: only show quarter on first row of quarter group within same subtask
      const quarterName = (shouldMergeSubtaskSummary && previousQuarterKey && previousQuarterKey === quarterKey && previousSubtaskKey === subtaskKey)
        ? ''
        : (task.quarter_name || '');

      // Parse document paths
      const documentPaths = parseDocumentPaths(task.document_path || task.Document_Path);
      const attachmentsText = documentPaths.length > 0 ? documentPaths.join('; ') : '';

      const rowData = [
        index + 1,
        taskDescription,
        subtaskDescription,
        quarterName,
        task.status || '',
        ...(hasTask1 ? [
          (taskNo === 1 && task.is_notified !== null && task.is_notified !== undefined) ? (task.is_notified ? 'Yes' : 'No') : '',
          (taskNo === 1 && task.notification_date) ? new Date(task.notification_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '',
          (taskNo === 1 && task.incorporation_in_by_laws !== null && task.incorporation_in_by_laws !== undefined) ? (task.incorporation_in_by_laws ? 'Yes' : 'No') : ''
        ] : []),
        ...(hasTrainingType ? [
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '') ? (task.training_type || '') : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && task.training_mode) ? (task.training_mode || '') : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && task.training_start_date) ? (
            task.training_end_date && task.training_end_date !== task.training_start_date
              ? `${new Date(task.training_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${new Date(task.training_end_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
              : new Date(task.training_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
          ) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_district_name || task.training_district)) ? (task.training_district_name || task.training_district || '') : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_total_attendees !== null && task.training_total_attendees !== undefined)) ? (task.training_total_attendees || 0) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_government_sector_attendees !== null && task.training_government_sector_attendees !== undefined)) ? (task.training_government_sector_attendees || 0) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_total_women !== null && task.training_total_women !== undefined)) ? (task.training_total_women || 0) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_architects_count !== null && task.training_architects_count !== undefined)) ? (task.training_architects_count || 0) : ''
        ] : []),
        ...(hasBuildingData ? [
          (shouldShowBuildingColumns(task) && task.building_name) ? (task.building_name || '') : '',
          (shouldShowBuildingColumns(task) && task.building_address) ? (task.building_address || '') : '',
          (shouldShowBuildingColumns(task) && task.building_typology) ? (task.building_typology || '') : '',
          (shouldShowBuildingColumns(task) && task.building_status) ? (task.building_status || '') : '',
          (shouldShowBuildingColumns(task) && task.building_nodal_contact_name) ? (task.building_nodal_contact_name || '') : '',
          (shouldShowBuildingColumns(task) && task.building_nodal_email) ? (task.building_nodal_email || '') : '',
          (shouldShowBuildingColumns(task) && task.building_nodal_department) ? (task.building_nodal_department || '') : '',
          (shouldShowBuildingColumns(task) && task.building_epi_value_proposed !== null && task.building_epi_value_proposed !== undefined) ? parseFloat(task.building_epi_value_proposed).toFixed(4) : '',
          (shouldShowBuildingColumns(task) && task.building_epi_ratio !== null && task.building_epi_ratio !== undefined) ? parseFloat(task.building_epi_ratio).toFixed(4) : ''
        ] : []),
        (task.task_date || task.Task_Date) ? new Date(task.task_date || task.Task_Date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '',
        ...(hasTask234 ? [(shouldMergeSubtaskSummary && task.isFirstSubtaskRow) ? (calculateSummary(task, tasksForSummary) || '-') : (shouldMergeSubtaskSummary ? '' : (calculateSummary(task, tasksForSummary) || '-'))] : []),
        (() => {
          // Format remarks for Excel with highlighted stakeholder name
          let remarksText = stripHtmlTags(task.remarks || '');
          if (remarksText.includes('STAKEHOLDER_NAME:')) {
            const lines = remarksText.split('\n');
            const firstLine = lines[0];
            if (firstLine.startsWith('STAKEHOLDER_NAME:')) {
              const stakeholderName = firstLine.replace('STAKEHOLDER_NAME:', '').trim();
              const remainingRemarks = lines.slice(1).join('\n').replace(/^\n+/, '');
              // Format with stakeholder name highlighted (using bold formatting in Excel)
              remarksText = `STAKEHOLDER: ${stakeholderName}${remainingRemarks ? '\n\n' + remainingRemarks : ''}`;
            }
          }
          return remarksText;
        })(),
        attachmentsText,
        task.submission_date ? new Date(task.submission_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : (task.saved_date ? new Date(task.saved_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '')
      ];

      const row = worksheet.addRow(rowData);
      // Calculate dynamic row height based on content length and column widths
      let maxLines = 1;
      rowData.forEach((cellValue, colIndex) => {
        const cellText = String(cellValue || '');
        if (cellText.length > 0) {
          // Get column width (default estimates based on column index)
          const col = worksheet.getColumn(colIndex + 1);
          const colWidth = col.width || (colIndex === 0 ? 8 : colIndex === 1 ? 50 : colIndex === 2 ? 50 : colIndex === 3 ? 15 : colIndex === 4 ? 15 : 20);
          // Estimate characters per line: Excel column width is roughly in character units
          const charsPerLine = Math.floor(colWidth * 0.85); // Use 85% of width for text
          const estimatedLines = Math.ceil(cellText.length / Math.max(charsPerLine, 15));
          maxLines = Math.max(maxLines, estimatedLines);
        }
      });
      // Excel row height: ~15-18 points per line, minimum 25, add extra for padding
      row.height = Math.max(25, maxLines * 20);
      row.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };

      // Apply colors based on row type
      if (task.isFirstTaskRow) {
        // Task rows - light green background
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE8F5E9' } // Light green
        };
        row.getCell(2).font = { bold: true, size: 11 }; // Bold task description (with task no)
      } else {
        // Subtask rows - white/light gray background
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFFFF' } // White
        };
        // Indent subtask description
        row.getCell(3).alignment = { vertical: 'top', horizontal: 'left', wrapText: true, indent: 1 };
      }

      // Color code status column
      const statusColIndex = 5;
      const statusCell = row.getCell(statusColIndex);
      if (task.status === 'Completed' || task.status === 'completed') {
        statusCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF4CAF50' } // Green
        };
        statusCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      } else if (task.status === 'In Progress' || task.status === 'In-Progress') {
        statusCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF2196F3' } // Blue
        };
        statusCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      } else if (task.status === 'On Hold') {
        statusCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF9800' } // Orange
        };
        statusCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      }

      // Color code training mode column if training type exists and should show
      if (hasTrainingType && shouldShowTrainingColumns(task) && task.training_type) {
        const trainingModeColIndex = 7 + (hasTask1 ? 3 : 0); // after S. No., Task, Subtask, Quarter, Status, [Task 1 cols], Training Type
        const trainingModeCell = row.getCell(trainingModeColIndex);
        if (task.training_mode) {
          if (task.training_mode.toLowerCase() === 'online') {
            trainingModeCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FF4CAF50' } // Green for online
            };
            trainingModeCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          } else {
            trainingModeCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FF757575' } // Gray for offline
            };
            trainingModeCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          }
        }
      }

      // Calculate remarks column index (1-based Excel columns)
      let remarksColIndex = 1; // S. No.
      remarksColIndex += 1; // Task Description
      remarksColIndex += 1; // Subtask Description
      remarksColIndex += 1; // Quarter
      remarksColIndex += 1; // Status
      if (hasTask1) remarksColIndex += 3;
      if (hasTrainingType) remarksColIndex += 8; // Training columns (including District)
      if (hasBuildingData) remarksColIndex += 8; // Building columns (matches rowData)
      remarksColIndex += 1; // Task Completion Date
      if (hasTask234) remarksColIndex += 1; // Summary
      // next column is Remarks

      // Format remarks cell to highlight stakeholder name
      const remarksCell = row.getCell(remarksColIndex);
      const remarksValue = remarksCell.value;
      if (remarksValue && typeof remarksValue === 'string' && remarksValue.includes('STAKEHOLDER:')) {
        // Use rich text to highlight stakeholder name
        const lines = remarksValue.split('\n');
        const firstLine = lines[0];
        if (firstLine.startsWith('STAKEHOLDER:')) {
          const stakeholderName = firstLine.replace('STAKEHOLDER:', '').trim();
          const remainingText = lines.slice(1).join('\n').replace(/^\n+/, '');
          
          // Create rich text with highlighted stakeholder name
          remarksCell.value = {
            richText: [
              {
                text: 'STAKEHOLDER: ',
                font: { bold: true, color: { argb: 'FF92400E' } }
              },
              {
                text: stakeholderName,
                font: { bold: true, color: { argb: 'FF78350F' }, size: 12 }
              },
              ...(remainingText ? [
                { text: '\n\n' },
                { text: remainingText, font: { color: { argb: 'FF000000' } } }
              ] : [])
            ]
          };
          
          // Also apply yellow background to the cell
          remarksCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFEF3C7' } // Light yellow
          };
        }
      }

      // Add borders and enable text wrapping for all cells
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
        };
        // Enable text wrapping for all cells
        if (!cell.alignment) {
          cell.alignment = {};
        }
        cell.alignment.wrapText = true;
        cell.alignment.vertical = 'top';
        cell.alignment.horizontal = 'left';
      });

      previousTaskKey = taskKey;
      if (shouldMergeSubtaskSummary) {
        previousSubtaskKey = subtaskKey;
        previousSummaryKey = subtaskKey;
        previousQuarterKey = quarterKey;
      }
    });

    // Merge the last task group
    const lastRow = hierarchicalTasks.length + 1;
    if (currentTaskStartRow < lastRow) {
      taskRanges.push({ start: currentTaskStartRow, end: lastRow });
    }
    
    // Merge the last subtask group (Task 2, 3, 4)
    const lastTask = hierarchicalTasks[hierarchicalTasks.length - 1];
    const lastTaskNo = lastTask?.task_no || lastTask?.Task_No;
    if ((lastTaskNo === 2 || lastTaskNo === 3 || lastTaskNo === 4) && currentSubtaskStartRow < lastRow) {
      subtaskRanges.push({ start: currentSubtaskStartRow, end: lastRow });
      summaryRanges.push({ start: currentSummaryStartRow, end: lastRow });
    }
    
    // Merge the last quarter group (Task 2, 3, 4)
    if ((lastTaskNo === 2 || lastTaskNo === 3 || lastTaskNo === 4) && currentQuarterStartRow < lastRow) {
      quarterRanges.push({ start: currentQuarterStartRow, end: lastRow });
    }

    // Apply all task merges (Task column ? column B after S. No.)
    taskRanges.forEach(range => {
      if (range.end > range.start) {
        worksheet.mergeCells(`B${range.start}:B${range.end}`);
      }
    });
    
    // Calculate Summary column index (0-based A=0)
    let summaryColIndex = 2; // S. No. + Task
    summaryColIndex += 1; // Subtask Description
    summaryColIndex += 1; // Quarter
    summaryColIndex += 1; // Status
    if (hasTask1) summaryColIndex += 3;
    if (hasTrainingType) summaryColIndex += 8; // Training columns
    if (hasBuildingData) summaryColIndex += 8; // Building columns (matches rowData)
    summaryColIndex += 1; // Task Date
    // Summary column is at summaryColIndex
    
    // Apply subtask merges for Task 2, 3, 4 (Subtask column ? column C)
    subtaskRanges.forEach(range => {
      if (range.end > range.start) {
        worksheet.mergeCells(`C${range.start}:C${range.end}`);
        // Set alignment for merged subtask cells
        const cell = worksheet.getCell(`C${range.start}`);
        cell.alignment = { ...cell.alignment, vertical: 'top', horizontal: 'left', wrapText: true };
      }
    });
    
    // Apply quarter merges for Task 2, 3, 4 (Quarter column ? column D)
    quarterRanges.forEach(range => {
      if (range.end > range.start) {
        worksheet.mergeCells(`D${range.start}:D${range.end}`);
        // Set alignment for merged quarter cells
        const cell = worksheet.getCell(`D${range.start}`);
        cell.alignment = { ...cell.alignment, vertical: 'top', horizontal: 'left', wrapText: true };
      }
    });
    
    // Apply summary merges for Task 2, 3, 4
    const summaryColLetter = String.fromCharCode(65 + summaryColIndex); // Convert to letter (A=65)
    summaryRanges.forEach(range => {
      if (range.end > range.start) {
        worksheet.mergeCells(`${summaryColLetter}${range.start}:${summaryColLetter}${range.end}`);
        // Set alignment for merged summary cells
        const cell = worksheet.getCell(`${summaryColLetter}${range.start}`);
        cell.alignment = { ...cell.alignment, vertical: 'top', horizontal: 'left', wrapText: true };
      }
    });
  };

  const exportToExcel = async () => {
    if (!filteredBySearch.length) return;

    // Create a new workbook
    const workbook = new ExcelJS.Workbook();

    // Check if filters are active
    if (!hasActiveFilters) {
      // Group tasks by task number
      const tasksByTaskNo = {};
      filteredBySearch.forEach(task => {
        const taskNo = task.task_no || task.Task_No;
        if (!tasksByTaskNo[taskNo]) {
          tasksByTaskNo[taskNo] = [];
        }
        tasksByTaskNo[taskNo].push(task);
      });

      // Sort task numbers
      const sortedTaskNos = Object.keys(tasksByTaskNo).sort((a, b) => Number(a) - Number(b));

      // Create a sheet for each task
      sortedTaskNos.forEach(taskNo => {
        const taskData = tasksByTaskNo[taskNo];
        const hierarchicalTasksForSheet = groupTasksHierarchically(taskData, null);

        // Check which columns to show for this task
        const hasTrainingType = taskData.some(task => {
          const tTaskNo = task.task_no || task.Task_No;
          const tTaskNum = Number(tTaskNo);
          if (tTaskNum !== 3) return false;
          const subtaskNo = task.subtask_no || task.Subtask_No;
          const subtaskNoStr = String(subtaskNo || '').toLowerCase();
          if (!subtaskNoStr.startsWith('2') && !subtaskNoStr.startsWith('3') && !subtaskNoStr.startsWith('4')) return false;
          const trainingType = task.training_type || task.Training_Type;
          return trainingType && String(trainingType).trim() !== '';
        });

        const hasBuildingData = taskData.some(task => {
          const tTaskNo = task.task_no || task.Task_No;
          const tTaskNum = Number(tTaskNo);
          return tTaskNum === 2 || tTaskNum === 4;
        });

        const hasTask234 = taskData.some(task => {
          const tTaskNo = task.task_no || task.Task_No;
          return tTaskNo === 2 || tTaskNo === 3 || tTaskNo === 4;
        });

        const hasTask1 = taskData.some(task => {
          const tTaskNo = task.task_no || task.Task_No;
          return tTaskNo === 1;
        });

        // Create worksheet for this task
        const worksheet = workbook.addWorksheet(`Task ${taskNo}`);

        // Define headers
        const headers = [
          'S. No.',
          'Task Description',
          'Subtask Description',
          'Quarter',
          'Status',
          ...(hasTask1 ? ['Is Notified?', 'Date of Notification', 'Incorporation in Building By-Laws'] : []),
          ...(hasTrainingType ? ['Training Type', 'Training Mode', 'Date of Training', 'District', 'Total Participants', 'Govt. Officials', 'Female Participants', 'No. of Architects'] : []),
          ...(hasBuildingData ? ['Building Name', 'Building Address', 'Typology', 'Building Status', 'Nodal Contact Name', 'Nodal Contact Email', 'Department', 'EPI Value Proposed', 'EPI Ratio'] : []),
          'Task Completion Date',
          ...(hasTask234 ? ['Summary'] : []),
          'Remarks',
          'Attachments',
          'Submission Date'
        ];

        // Add header row with styling
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        headerRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1E40AF' } // Dark blue
        };
        headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        headerRow.height = 25;
        
        // Set column widths
        worksheet.getColumn(1).width = 8; // S. No.
        worksheet.getColumn(2).width = 50; // Task Description with Task No
        worksheet.getColumn(3).width = 50; // Subtask Description with Subtask No
        worksheet.getColumn(4).width = 15; // Quarter
        worksheet.getColumn(5).width = 15; // Status
        let colIndex = 6;
        if (hasTask1) {
          worksheet.getColumn(colIndex++).width = 15; // Is Notified?
          worksheet.getColumn(colIndex++).width = 20; // Date of Notification
          worksheet.getColumn(colIndex++).width = 30; // Incorporation in Building By-Laws
        }
        if (hasTrainingType) {
          worksheet.getColumn(colIndex++).width = 20; // Training Type
          worksheet.getColumn(colIndex++).width = 15; // Training Mode
          worksheet.getColumn(colIndex++).width = 20; // Date of Training
          worksheet.getColumn(colIndex++).width = 18; // District
          worksheet.getColumn(colIndex++).width = 18; // Total Participants
          worksheet.getColumn(colIndex++).width = 18; // Govt. Officials
          worksheet.getColumn(colIndex++).width = 20; // Female Participants
          worksheet.getColumn(colIndex++).width = 18; // No. of Architects
        }
        if (hasBuildingData) {
          worksheet.getColumn(colIndex++).width = 25; // Building Name
          worksheet.getColumn(colIndex++).width = 30; // Building Address
          worksheet.getColumn(colIndex++).width = 15; // Typology
          worksheet.getColumn(colIndex++).width = 18; // Building Status
          worksheet.getColumn(colIndex++).width = 20; // Nodal Contact Name
          worksheet.getColumn(colIndex++).width = 25; // Nodal Contact Email
          worksheet.getColumn(colIndex++).width = 20; // Department
          worksheet.getColumn(colIndex++).width = 18; // EPI Value Proposed
          worksheet.getColumn(colIndex++).width = 15; // EPI Ratio
        }
        worksheet.getColumn(colIndex++).width = 18; // Task Completion Date
        if (hasTask234) {
          worksheet.getColumn(colIndex++).width = 40; // Summary
        }
        worksheet.getColumn(colIndex++).width = 30; // Remarks
        worksheet.getColumn(colIndex++).width = 20; // Attachments
        worksheet.getColumn(colIndex++).width = 18; // Submission Date

        // Add data to worksheet
        addDataToWorksheet(worksheet, hierarchicalTasksForSheet, taskData, hasTrainingType, hasBuildingData, hasTask234, hasTask1);

        // Freeze header row
        worksheet.views = [{ state: 'frozen', ySplit: 1 }];
      });
    } else {
      // Filters are active - use single sheet (existing behavior)
      const hierarchicalTasksForExport = groupTasksHierarchically(filteredBySearch, null);

      // Create a new workbook and worksheet
      const worksheet = workbook.addWorksheet('Task Tracker');

      // Check if Tasks 2, 3, or 4 are present (for Summary column)
      const hasTask234 = filteredBySearch.some(task => {
        const taskNo = task.task_no || task.Task_No;
        return taskNo === 2 || taskNo === 3 || taskNo === 4;
      });

      const hasTask1 = filteredBySearch.some(task => {
        const taskNo = task.task_no || task.Task_No;
        return taskNo === 1;
      });

      // Define headers - based on what columns are shown in the table
      const headers = [
      'S. No.',
      'Task Description',
      'Subtask Description',
      'Quarter',
      'Status',
      ...(hasTask1 ? ['Is Notified?', 'Date of Notification', 'Incorporation in Building By-Laws'] : []),
      ...(hasTrainingType ? ['Training Type', 'Training Mode', 'Date of Training', 'District', 'Total Participants', 'Govt. Officials', 'Female Participants', 'No. of Architects'] : []),
      ...(hasBuildingData ? ['Building Name', 'Building Address', 'Typology', 'Building Status', 'Nodal Contact Name', 'Nodal Contact Email', 'Department', 'EPI Value Proposed', 'EPI Ratio'] : []),
      'Task Completion Date',
      ...(hasTask234 ? ['Summary'] : []),
      'Remarks',
      'Attachments',
      'Submission Date'
    ];

    // Add header row with styling
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1E40AF' } // Dark blue
    };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    headerRow.height = 25;
    
    // Set column widths
    worksheet.getColumn(1).width = 8; // S. No.
    worksheet.getColumn(2).width = 50; // Task Description with Task No
    worksheet.getColumn(3).width = 50; // Subtask Description with Subtask No
    worksheet.getColumn(4).width = 15; // Quarter
    worksheet.getColumn(5).width = 15; // Status
    let colIndex = 6;
    if (hasTask1) {
      worksheet.getColumn(colIndex++).width = 15; // Is Notified?
      worksheet.getColumn(colIndex++).width = 20; // Date of Notification
      worksheet.getColumn(colIndex++).width = 30; // Incorporation in Building By-Laws
    }
    if (hasTrainingType) {
      worksheet.getColumn(colIndex++).width = 20; // Training Type
      worksheet.getColumn(colIndex++).width = 15; // Training Mode
      worksheet.getColumn(colIndex++).width = 20; // Date of Training
      worksheet.getColumn(colIndex++).width = 18; // District
      worksheet.getColumn(colIndex++).width = 18; // Total Participants
      worksheet.getColumn(colIndex++).width = 18; // Govt. Officials
      worksheet.getColumn(colIndex++).width = 20; // Female Participants
      worksheet.getColumn(colIndex++).width = 18; // No. of Architects
    }
    if (hasBuildingData) {
      worksheet.getColumn(colIndex++).width = 25; // Building Name
      worksheet.getColumn(colIndex++).width = 30; // Building Address
      worksheet.getColumn(colIndex++).width = 15; // Typology
      worksheet.getColumn(colIndex++).width = 18; // Building Status
      worksheet.getColumn(colIndex++).width = 20; // Nodal Contact Name
      worksheet.getColumn(colIndex++).width = 25; // Nodal Contact Email
          worksheet.getColumn(colIndex++).width = 20; // Department
          worksheet.getColumn(colIndex++).width = 18; // EPI Value Proposed
          worksheet.getColumn(colIndex++).width = 15; // EPI Ratio
    }
    worksheet.getColumn(colIndex++).width = 18; // Task Completion Date
    if (hasTask234) {
      worksheet.getColumn(colIndex++).width = 40; // Summary
    }
    worksheet.getColumn(colIndex++).width = 30; // Remarks
    worksheet.getColumn(colIndex++).width = 20; // Attachments
    worksheet.getColumn(colIndex++).width = 18; // Submission Date

    // Track merged cell ranges for tasks
    const taskRanges = [];
    let currentTaskStartRow = 2;
    let previousTaskKey = null;

    // Helper function to parse document paths
    const parseDocumentPaths = (documentPath) => {
      if (!documentPath) return [];
      try {
        if (typeof documentPath === 'string') {
          // Try to parse as JSON array
          if (documentPath.startsWith('[') && documentPath.endsWith(']')) {
            return JSON.parse(documentPath);
          }
          // Otherwise, treat as single path
          return [documentPath];
        }
        return [];
      } catch (e) {
        // If parsing fails, treat as single path
        return typeof documentPath === 'string' ? [documentPath] : [];
      }
    };

    // Add data rows
    hierarchicalTasksForExport.forEach((task, index) => {
      const rowIndex = index + 2; // +2 because row 1 is header
      const taskKey = `${task.task_no || task.Task_No || ''}`;

      // Check if we need to merge previous task
      if (previousTaskKey && previousTaskKey !== taskKey) {
        if (currentTaskStartRow < rowIndex) {
          taskRanges.push({ start: currentTaskStartRow, end: rowIndex - 1 });
        }
        currentTaskStartRow = rowIndex;
      } else if (previousTaskKey === null || previousTaskKey !== taskKey) {
        currentTaskStartRow = rowIndex;
      }
      
      // Combine Task No with Task Description
      const taskDescription = task.isFirstTaskRow 
        ? ((task.task_no || task.Task_No) ? `Task ${task.task_no || task.Task_No}: ${task.task_description || ''}` : (task.task_description || ''))
        : '';
      
      // Combine Subtask No with Subtask Description
      const subtaskDescription = (task.subtask_no || task.Subtask_No) 
        ? `Subtask ${task.subtask_no || task.Subtask_No}: ${task.subtask_description || ''}`
        : (task.subtask_description || '');

      // Parse document paths
      const documentPaths = parseDocumentPaths(task.document_path || task.Document_Path);
      const attachmentsText = documentPaths.length > 0 ? documentPaths.join('; ') : '';

      const rowData = [
        index + 1,
        taskDescription,
        subtaskDescription,
        task.quarter_name || '',
        task.status || '',
        ...(hasTask1 ? [
          ((task.task_no || task.Task_No) === 1 && task.is_notified !== null && task.is_notified !== undefined) ? (task.is_notified ? 'Yes' : 'No') : '',
          ((task.task_no || task.Task_No) === 1 && task.notification_date) ? new Date(task.notification_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '',
          ((task.task_no || task.Task_No) === 1 && task.incorporation_in_by_laws !== null && task.incorporation_in_by_laws !== undefined) ? (task.incorporation_in_by_laws ? 'Yes' : 'No') : ''
        ] : []),
        ...(hasTrainingType ? [
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '') ? (task.training_type || '') : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && task.training_mode) ? (task.training_mode || '') : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && task.training_start_date) ? (
            task.training_end_date && task.training_end_date !== task.training_start_date
              ? `${new Date(task.training_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${new Date(task.training_end_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
              : new Date(task.training_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
          ) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_district_name || task.training_district)) ? (task.training_district_name || task.training_district || '') : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_total_attendees !== null && task.training_total_attendees !== undefined)) ? (task.training_total_attendees || 0) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_government_sector_attendees !== null && task.training_government_sector_attendees !== undefined)) ? (task.training_government_sector_attendees || 0) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_total_women !== null && task.training_total_women !== undefined)) ? (task.training_total_women || 0) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_architects_count !== null && task.training_architects_count !== undefined)) ? (task.training_architects_count || 0) : ''
        ] : []),
        ...(hasBuildingData ? [
          (shouldShowBuildingColumns(task) && task.building_name) ? (task.building_name || '') : '',
          (shouldShowBuildingColumns(task) && task.building_address) ? (task.building_address || '') : '',
          (shouldShowBuildingColumns(task) && task.building_typology) ? (task.building_typology || '') : '',
          (shouldShowBuildingColumns(task) && task.building_status) ? (task.building_status || '') : '',
          (shouldShowBuildingColumns(task) && task.building_nodal_contact_name) ? (task.building_nodal_contact_name || '') : '',
          (shouldShowBuildingColumns(task) && task.building_nodal_email) ? (task.building_nodal_email || '') : '',
          (shouldShowBuildingColumns(task) && task.building_nodal_department) ? (task.building_nodal_department || '') : '',
          (shouldShowBuildingColumns(task) && task.building_epi_value_proposed !== null && task.building_epi_value_proposed !== undefined) ? parseFloat(task.building_epi_value_proposed).toFixed(4) : '',
          (shouldShowBuildingColumns(task) && task.building_epi_ratio !== null && task.building_epi_ratio !== undefined) ? parseFloat(task.building_epi_ratio).toFixed(4) : ''
        ] : []),
        (task.task_date || task.Task_Date) ? new Date(task.task_date || task.Task_Date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '',
        ...(hasTask234 ? [calculateSummary(task, filteredBySearch) || '-'] : []),
        (() => {
          // Format remarks for Excel with highlighted stakeholder name
          let remarksText = stripHtmlTags(task.remarks || '');
          if (remarksText.includes('STAKEHOLDER_NAME:')) {
            const lines = remarksText.split('\n');
            const firstLine = lines[0];
            if (firstLine.startsWith('STAKEHOLDER_NAME:')) {
              const stakeholderName = firstLine.replace('STAKEHOLDER_NAME:', '').trim();
              const remainingRemarks = lines.slice(1).join('\n').replace(/^\n+/, '');
              // Format with stakeholder name highlighted (using bold formatting in Excel)
              remarksText = `STAKEHOLDER: ${stakeholderName}${remainingRemarks ? '\n\n' + remainingRemarks : ''}`;
            }
          }
          return remarksText;
        })(),
        attachmentsText,
        task.submission_date ? new Date(task.submission_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : (task.saved_date ? new Date(task.saved_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '')
      ];

      const row = worksheet.addRow(rowData);
      // Calculate dynamic row height based on content length and column widths
      let maxLines = 1;
      rowData.forEach((cellValue, colIndex) => {
        const cellText = String(cellValue || '');
        if (cellText.length > 0) {
          // Get column width (default estimates based on column index)
          const col = worksheet.getColumn(colIndex + 1);
          const colWidth = col.width || (colIndex === 0 ? 8 : colIndex === 1 ? 50 : colIndex === 2 ? 50 : colIndex === 3 ? 15 : colIndex === 4 ? 15 : 20);
          // Estimate characters per line: Excel column width is roughly in character units
          const charsPerLine = Math.floor(colWidth * 0.85); // Use 85% of width for text
          const estimatedLines = Math.ceil(cellText.length / Math.max(charsPerLine, 15));
          maxLines = Math.max(maxLines, estimatedLines);
        }
      });
      // Excel row height: ~15-18 points per line, minimum 25, add extra for padding
      row.height = Math.max(25, maxLines * 20);
      row.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };

      // Apply colors based on row type
      if (task.isFirstTaskRow) {
        // Task rows - light green background
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE8F5E9' } // Light green
        };
        row.getCell(2).font = { bold: true, size: 11 }; // Bold task description (with task no)
      } else {
        // Subtask rows - white/light gray background
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFFFF' } // White
        };
        // Indent subtask description
        row.getCell(3).alignment = { vertical: 'top', horizontal: 'left', wrapText: true, indent: 1 };
      }

      // Color code status column
      const statusColIndex = 5;
      const statusCell = row.getCell(statusColIndex);
      if (task.status === 'Completed' || task.status === 'completed') {
        statusCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF4CAF50' } // Green
        };
        statusCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      } else if (task.status === 'In Progress' || task.status === 'In-Progress') {
        statusCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF2196F3' } // Blue
        };
        statusCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      } else if (task.status === 'On Hold') {
        statusCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF9800' } // Orange
        };
        statusCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      }

      // Color code training mode column if training type exists and should show
      if (hasTrainingType && shouldShowTrainingColumns(task) && task.training_type) {
        const trainingModeColIndex = 7 + (hasTask1 ? 3 : 0);
        const trainingModeCell = row.getCell(trainingModeColIndex);
        if (task.training_mode) {
          if (task.training_mode.toLowerCase() === 'online') {
            trainingModeCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FF4CAF50' } // Green for online
            };
            trainingModeCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          } else {
            trainingModeCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FF757575' } // Gray for offline
            };
            trainingModeCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          }
        }
      }

      // Calculate remarks column index (1-based)
      let remarksColIndex = 1; // S. No.
      remarksColIndex += 1; // Task
      remarksColIndex += 1; // Subtask
      remarksColIndex += 1; // Quarter
      remarksColIndex += 1; // Status
      if (hasTask1) remarksColIndex += 3;
      if (hasTrainingType) remarksColIndex += 8;
      if (hasBuildingData) remarksColIndex += 8;
      remarksColIndex += 1; // Task Completion Date
      if (hasTask234) remarksColIndex += 1; // Summary

      // Format remarks cell to highlight stakeholder name
      const remarksCell = row.getCell(remarksColIndex);
      const remarksValue = remarksCell.value;
      if (remarksValue && typeof remarksValue === 'string' && remarksValue.includes('STAKEHOLDER:')) {
        // Use rich text to highlight stakeholder name
        const lines = remarksValue.split('\n');
        const firstLine = lines[0];
        if (firstLine.startsWith('STAKEHOLDER:')) {
          const stakeholderName = firstLine.replace('STAKEHOLDER:', '').trim();
          const remainingText = lines.slice(1).join('\n').replace(/^\n+/, '');
          
          // Create rich text with highlighted stakeholder name
          remarksCell.value = {
            richText: [
              {
                text: 'STAKEHOLDER: ',
                font: { bold: true, color: { argb: 'FF92400E' } }
              },
              {
                text: stakeholderName,
                font: { bold: true, color: { argb: 'FF78350F' }, size: 12 }
              },
              ...(remainingText ? [
                { text: '\n\n' },
                { text: remainingText, font: { color: { argb: 'FF000000' } } }
              ] : [])
            ]
          };
          
          // Also apply yellow background to the cell
          remarksCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFEF3C7' } // Light yellow
          };
        }
      }

      // Add borders and enable text wrapping for all cells
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
        };
        // Enable text wrapping for all cells
        if (!cell.alignment) {
          cell.alignment = {};
        }
        cell.alignment.wrapText = true;
        cell.alignment.vertical = 'top';
        cell.alignment.horizontal = 'left';
      });

      previousTaskKey = taskKey;
    });

    // Merge the last task group
    const lastRow = hierarchicalTasksForExport.length + 1;
    if (currentTaskStartRow < lastRow) {
      taskRanges.push({ start: currentTaskStartRow, end: lastRow });
    }

      // Apply all task merges (Task column ? column B after S. No.)
      taskRanges.forEach(range => {
        if (range.end > range.start) {
          worksheet.mergeCells(`B${range.start}:B${range.end}`);
        }
      });

      // Freeze header row
      worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    }

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ep-task-tracker-${new Date().toISOString().split('T')[0]}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get filtered subtasks and sort them properly
  const filteredSubtasks = useMemo(() => {
    if (!dropdownData) return [];
    let subtasks = filters.taskId
      ? dropdownData?.subtasks?.filter(subtask => 
          (subtask.task_id === parseInt(filters.taskId)) || 
          (subtask.Task_ID === parseInt(filters.taskId))
        ) || []
      : dropdownData?.subtasks || [];
    
    // Sort subtasks properly (by task_id first, then by subtask_no)
    return sortSubtasks(subtasks);
  }, [dropdownData, filters.taskId]);

  // Building Type options from Task 2 and Task 4 subtasks
  const buildingTypeOptions = useMemo(() => {
    if (!dropdownData?.subtasks) return [];
    const options = [];

    // Task 2, Subtask 2: ECSBC Commercial
    const task2Subtask2 = dropdownData.subtasks.find(s =>
      (s.task_id === 2 || s.Task_ID === 2) &&
      (s.subtask_no === 2 || s.Subtask_No === 2 || String(s.subtask_no || s.Subtask_No || '').startsWith('2'))
    );
    if (task2Subtask2) {
      options.push({
        value: task2Subtask2.subtask_id || task2Subtask2.Subtask_ID,
        label: 'ECSBC Commercial'
      });
    }

    // Task 2, Subtask 3: ECSBC Residential
    const task2Subtask3 = dropdownData.subtasks.find(s =>
      (s.task_id === 2 || s.Task_ID === 2) &&
      (s.subtask_no === 3 || s.Subtask_No === 3 || String(s.subtask_no || s.Subtask_No || '').startsWith('3'))
    );
    if (task2Subtask3) {
      options.push({
        value: task2Subtask3.subtask_id || task2Subtask3.Subtask_ID,
        label: 'ECSBC Residential'
      });
    }

    // Task 4, Subtask 1: Star Rating- Commercial Projects
    const task4Subtask1 = dropdownData.subtasks.find(s =>
      (s.task_id === 4 || s.Task_ID === 4) &&
      (s.subtask_no === 1 || s.Subtask_No === 1 || String(s.subtask_no || s.Subtask_No || '').startsWith('1'))
    );
    if (task4Subtask1) {
      options.push({
        value: task4Subtask1.subtask_id || task4Subtask1.Subtask_ID,
        label: 'Star Rating- Commercial Projects'
      });
    }

    // Task 4, Subtask 2: Star Rating- Residential Projects
    const task4Subtask2 = dropdownData.subtasks.find(s =>
      (s.task_id === 4 || s.Task_ID === 4) &&
      (s.subtask_no === 2 || s.Subtask_No === 2 || String(s.subtask_no || s.Subtask_No || '').startsWith('2'))
    );
    if (task4Subtask2) {
      options.push({
        value: task4Subtask2.subtask_id || task4Subtask2.Subtask_ID,
        label: 'Star Rating- Residential Projects'
      });
    }

    // Task 4, Subtask 3: Shunya labeling
    const task4Subtask3 = dropdownData.subtasks.find(s =>
      (s.task_id === 4 || s.Task_ID === 4) &&
      (s.subtask_no === 3 || s.Subtask_No === 3 || String(s.subtask_no || s.Subtask_No || '').startsWith('3'))
    );
    if (task4Subtask3) {
      options.push({
        value: task4Subtask3.subtask_id || task4Subtask3.Subtask_ID,
        label: 'Shunya labeling'
      });
    }

    return options;
  }, [dropdownData]);

  // Get task info for display
  const getTaskInfo = (subtask) => {
    const taskId = subtask.task_id || subtask.Task_ID;
    const task = dropdownData?.tasks?.find(t => t.task_id === taskId || t.Task_ID === taskId);
    return task ? `Task ${task.task_no || task.Task_No}` : '';
  };

  // Get status color
  const getStatusColor = (status) => {
    if (!status) return 'text-gray-700 bg-gray-100 border border-gray-200';
    const statusLower = status.toLowerCase().trim().replace(/-/g, ' ');
    
    if (statusLower.includes('completed')) {
      return 'text-green-700 bg-green-50 border border-green-200';
    } else if (statusLower.includes('in progress') || statusLower.includes('in-progress')) {
      return 'text-purple-700 bg-purple-50 border border-purple-200';
    } else if (statusLower.includes('on hold') || statusLower.includes('on-hold')) {
      return 'text-orange-700 bg-orange-50 border border-orange-200';
    } else if (statusLower.includes('not started') || statusLower.includes('not-started')) {
      return 'text-gray-700 bg-gray-100 border border-gray-200';
    } else if (statusLower.includes('awaiting') || statusLower.includes('pending')) {
      return 'text-blue-700 bg-blue-50 border border-blue-200';
    } else if (statusLower.includes('submitted')) {
      return 'text-indigo-700 bg-indigo-50 border border-indigo-200';
    }
    return 'text-gray-700 bg-gray-100 border border-gray-200';
  };

  // Parse document paths
  const parseDocumentPaths = (documentPath) => {
    if (!documentPath) return [];
    try {
      const parsed = JSON.parse(documentPath);
      return Array.isArray(parsed) ? parsed : [documentPath];
    } catch (e) {
      const trimmed = String(documentPath).trim();
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const manualParse = trimmed.slice(1, -1).split(',').map(p => p.trim().replace(/^["']|["']$/g, ''));
          return manualParse.filter(p => p.length > 0);
        } catch (e2) {
          return [documentPath];
        }
      }
      return [documentPath];
    }
  };

  // Use weeks from database
  const uniqueWeeks = allWeeks || [];

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 min-w-0 max-w-full mt-8">
      {/* Filters Header */}
      <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-blue-50 p-6 border-b border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Filter className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Your Cell Task Details
              </h2>
              <p className="text-xs text-gray-500 mt-1 font-medium">
                Only the latest entries are displayed here
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white rounded-xl hover:bg-gray-50 transition-all shadow-sm hover:shadow-md border border-gray-200"
              >
                <X className="h-4 w-4" />
                Clear Filters
              </button>
            )}
            <button
              onClick={exportToExcel}
              disabled={!filteredBySearch.length}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg transform hover:scale-105 disabled:hover:scale-100"
            >
              <Download className="h-4 w-4" />
              Export Excel
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
            <input
              type="text"
              placeholder="Search by task, subtask, status, or quarter..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {/* Filter Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
          {/* Task Filter */}
          <div className="group min-w-0">
  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1 whitespace-nowrap">
              <Target className="h-3.5 w-3.5" />
              Task
            </label>

  <Listbox
              value={filters.taskId}
    onChange={(value) => {
      handleFilterChange('taskId', value);
                setFilters(prev => ({ ...prev, subtaskId: '' }));
              }}
            >
    <div className="relative">
      <Listbox.Button
        className="
          w-full
          px-4 py-2.5
          border-2 border-gray-200
          rounded-xl
          focus:ring-2 focus:ring-blue-500
          focus:border-blue-500
          bg-white
          shadow-sm
          hover:shadow-md
          focus:shadow-lg
          text-sm font-medium
          text-left
          flex items-center justify-between
          transition-all
        "
      >
        <span className="block truncate flex-1 min-w-0 pr-2">
          {filters.taskId
            ? (() => {
                const task = dropdownData?.tasks?.find(t => t.task_id === parseInt(filters.taskId));
                if (!task) return 'All Tasks';
                return `Task ${task.task_no}: ${task.task_description || ''}`;
              })()
            : 'All Tasks'}
        </span>
        <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
      </Listbox.Button>
      <Transition
        as={React.Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Listbox.Options 
          className="absolute z-[9999] mt-1 w-full min-w-[400px] max-w-[600px] max-h-96 overflow-auto rounded-xl bg-white py-1 text-sm shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none border-2 border-gray-200 modal-width-responsive"
        >
          <Listbox.Option
            value=""
            className={({ active }) =>
              `relative cursor-pointer select-none py-2 px-4 ${
                active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
              }`
            }
          >
            {({ selected }) => (
              <>
                <span className={`block ${selected ? 'font-medium' : 'font-normal'}`}>
                  All Tasks
                </span>
                {selected && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                    <Check className="h-4 w-4" aria-hidden="true" />
                  </span>
                )}
              </>
            )}
          </Listbox.Option>
          {dropdownData?.tasks?.map((task) => {
            const text = `Task ${task.task_no}: ${task.task_description || ''}`;
            return (
              <Listbox.Option
                key={task.task_id}
                value={String(task.task_id)}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-2 px-4 ${
                    active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                  }`
                }
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block break-words whitespace-normal ${selected ? 'font-medium' : 'font-normal'}`}
                      style={{ 
                        wordWrap: 'break-word', 
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                        lineHeight: '1.5'
                      }}
                    >
                      {text}
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                        <Check className="h-4 w-4" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </Listbox.Option>
            );
          })}
        </Listbox.Options>
      </Transition>
    </div>
  </Listbox>
          </div>

          {/* Subtask Filter */}
          <div className="group">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
              <FileText className="h-3.5 w-3.5" />
              Subtask
            </label>
            <Listbox
              value={filters.subtaskId}
              onChange={(value) => handleFilterChange('subtaskId', value)}
            >
              <div className="relative">
                <Listbox.Button
                  className="
                    w-full
                    px-4 py-2.5
                    border-2 border-gray-200
                    rounded-xl
                    focus:ring-2 focus:ring-blue-500
                    focus:border-blue-500
                    bg-white
                    shadow-sm
                    hover:shadow-md
                    focus:shadow-lg
                    text-sm font-medium
                    text-left
                    flex items-center justify-between
                    transition-all
                  "
                >
                  <span className="block truncate flex-1 min-w-0 pr-2">
                    {filters.subtaskId
                      ? (() => {
                          const subtask = filteredSubtasks.find(s => 
                            (s.subtask_id || s.Subtask_ID) === parseInt(filters.subtaskId)
                          );
                          if (!subtask) return 'All Subtasks';
                          const subtaskNo = subtask.subtask_no || subtask.Subtask_No || '';
                          const subtaskDesc = subtask.subtask_description || subtask.Subtask_Description || '';
                          const taskInfo = getTaskInfo(subtask);
                          return taskInfo ? `${taskInfo} - Subtask ${subtaskNo}: ${subtaskDesc}` : `Subtask ${subtaskNo}: ${subtaskDesc}`;
                        })()
                      : 'All Subtasks'}
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                </Listbox.Button>
                <Transition
                  as={React.Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options 
                    className="absolute z-[9999] mt-1 w-full min-w-[400px] max-w-[600px] max-h-96 overflow-auto rounded-xl bg-white py-1 text-sm shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none border-2 border-gray-200 modal-width-responsive"
                  >
                    <Listbox.Option
                      value=""
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 px-4 ${
                          active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                        }`
                      }
            >
                      {({ selected }) => (
                        <>
                          <span className={`block ${selected ? 'font-medium' : 'font-normal'}`}>
                            All Subtasks
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                              <Check className="h-4 w-4" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
              {filteredSubtasks.map((subtask) => {
                const subtaskNo = subtask.subtask_no || subtask.Subtask_No || '';
                const subtaskDesc = subtask.subtask_description || subtask.Subtask_Description || '';
                const taskInfo = getTaskInfo(subtask);
                      const text = taskInfo ? `${taskInfo} - Subtask ${subtaskNo}: ${subtaskDesc}` : `Subtask ${subtaskNo}: ${subtaskDesc}`;
                return (
                        <Listbox.Option
                          key={subtask.subtask_id || subtask.Subtask_ID}
                          value={String(subtask.subtask_id || subtask.Subtask_ID)}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 px-4 ${
                              active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span
                                className={`block word-break-safe ${selected ? 'font-medium' : 'font-normal'}`}
                              >
                                {text}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                                  <Check className="h-4 w-4" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                );
              })}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>

          {/* Quarter Filter */}
          <div className="group">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              Quarter
            </label>
            <select
              value={filters.quarterId}
              onChange={(e) => handleFilterChange('quarterId', e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg text-sm font-medium"
            >
              <option value="">All Quarters</option>
              {dropdownData?.quarters?.map((quarter) => (
                <option key={quarter.id} value={quarter.id}>
                  {quarter.quarter_name}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="group">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Activity className="h-3.5 w-3.5" />
              Status
            </label>
            <select
              value={filters.statusId}
              onChange={(e) => handleFilterChange('statusId', e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg text-sm font-medium"
            >
              <option value="">All Status</option>
              {dropdownData?.statuses?.map((status) => (
                <option key={status.status_id} value={status.status_id}>
                  {status.status}
                </option>
              ))}
            </select>
          </div>

          {/* Building Type Filter */}
          <div className="group">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" />
              Building Type
            </label>
            <select
              value={filters.buildingType}
              onChange={(e) => handleFilterChange('buildingType', e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg text-sm font-medium"
            >
              <option value="">All Building Types</option>
              {buildingTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Training Type Filter */}
          <div className="group">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" />
              Training Type
            </label>
            <select
              value={filters.trainingId}
              onChange={(e) => handleFilterChange('trainingId', e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg text-sm font-medium"
            >
              <option value="">All Training Types</option>
              {dropdownData?.trainings?.map((training) => (
                <option key={training.training_id || training.Training_ID} value={training.training_id || training.Training_ID}>
                  {training.training_type || training.Training_Type || 'Training'} {training.training_mode ? `(${training.training_mode})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Week Filter */}
          <div className="group">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              Week
            </label>
            <select
              value={filters.week}
              onChange={(e) => handleFilterChange('week', e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg text-sm font-medium"
            >
              <option value="">All Weeks</option>
              {uniqueWeeks.map((week) => {
                const [year, weekNum] = week.split('-W');
                return (
                  <option key={week} value={week}>
                    Week {weekNum}, {year}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>

      {/* Pagination - Outside scrollable container */}
      {filteredBySearch.length > 0 && (
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-6 py-4 border-b-2 border-gray-200 flex items-center justify-between w-full mb-0 rounded-t-lg shadow-sm">
          <div className="flex items-center gap-4">
            {taskGroups[currentPage - 1] && (
              <div className="text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 rounded-lg shadow-md">
                Task {taskGroups[currentPage - 1].taskNo} of {maxTaskNo || totalPages}
              </div>
            )}
          </div>
          {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
                onClick={handlePagePrev}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-blue-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
                onClick={() => handlePageNext(totalPages)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-purple-500 hover:text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          )}
        </div>
      )}

      {/* Table: dual horizontal scroll (top + bottom), synced */}
      <div id="table-container" className="w-full min-w-0 max-w-full table-container rounded-b-2xl">
        {isLoadingTasks ? (
          <div className="flex flex-col items-center justify-center py-16 min-h-[220px]">
            <Loader2 className="h-10 w-10 animate-spin text-blue-600" aria-hidden />
            <p className="mt-4 text-gray-600 font-medium">Loading tasks...</p>
          </div>
        ) : tasksError ? (
          <div className="text-center py-16">
            <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Error loading tasks</h3>
            <p className="text-gray-500 mb-4">{tasksError.message || 'Failed to fetch tasks. Please try again.'}</p>
          </div>
        ) : filteredBySearch.length === 0 ? (
          <div className="text-center py-16">
            <Search className="h-10 w-10 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-500 mb-4">Try adjusting your filters or search criteria</p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <X className="h-4 w-4" />
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="dashboard-table-scroll-viewport w-full min-w-0 rounded-b-2xl bg-white">
            <div
              ref={epTableScrollTopRef}
              className="dashboard-table-scroll dashboard-table-scroll--top w-full min-w-0 max-w-full border-b border-gray-200 bg-white"
              onScroll={handleEpTableScrollSync}
            >
              <div ref={epTableScrollSpacerRef} className="h-px pointer-events-none" aria-hidden />
            </div>
            <div
              ref={epTableScrollBottomRef}
              className="dashboard-table-scroll-body w-full min-w-0 max-w-full bg-white"
              onScroll={handleEpTableScrollSync}
            >
            <table
              className="table-min-1400 min-w-[1480px]"
              style={{ tableLayout: 'auto', borderCollapse: 'collapse', width: 'max-content', minWidth: '1480px' }}
            >
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap w-14 min-w-[3.5rem] border-r border-gray-300">
                    <div className="flex items-center justify-center gap-2">
                      <ListOrdered className="h-4 w-4 text-gray-600" />
                      S. No.
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-ep-task">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-green-600" />
                      Task
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-ep-subtask">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-purple-600" />
                      Subtask
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-120">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-orange-600" />
                      Quarter
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-150">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-red-600" />
                      Status
                    </div>
                  </th>
                  {hasTask1 && (
                    <>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-150">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-blue-600" />
                          Is Notified?
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-180">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-green-600" />
                          Date of Notification
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-200">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-purple-600" />
                          Incorporation in Building By-Laws
                        </div>
                      </th>
                    </>
                  )}
                  {hasTrainingType && (
                    <>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-180">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-teal-600" />
                          Training Type
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-150">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-blue-600" />
                          Date of Training
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-150">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-green-600" />
                          District
                        </div>
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-140">
                        <div className="flex items-center justify-center gap-2">
                          <Users className="h-4 w-4 text-indigo-600" />
                          Total Participants
                        </div>
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-300 table-col-140 max-w-[9rem]">
                        <div className="flex items-center justify-center gap-2 whitespace-normal leading-tight text-center">
                          <Users className="h-4 w-4 text-blue-600 shrink-0" />
                          Govt. Officials
                        </div>
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-150">
                        <div className="flex items-center justify-center gap-2">
                          <Users className="h-4 w-4 text-pink-600" />
                          Female
                        </div>
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-150">
                        <div className="flex items-center justify-center gap-2">
                          <Building2 className="h-4 w-4 text-amber-600" />
                          Architects
                        </div>
                      </th>
                    </>
                  )}
                  {hasBuildingData && (
                    <>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-200">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-indigo-600" />
                          Building Name
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-250">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-blue-600" />
                          Building Address
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-150">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-purple-600" />
                          Typology
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-150">
                        <div className="flex items-center gap-2">
                          <Activity className="h-4 w-4 text-orange-600" />
                          Building Status
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-180">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-green-600" />
                          Nodal Contact Name
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-200">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 text-teal-600" />
                          Nodal Contact Email
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-200">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-purple-600" />
                          Department
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-180">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-blue-600" />
                          EPI Value Proposed
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-150">
                        <div className="flex items-center gap-2">
                          <Activity className="h-4 w-4 text-green-600" />
                          EPI Ratio
                        </div>
                      </th>
                    </>
                  )}
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-150">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-violet-600" />
                      Task Completion Date
                    </div>
                  </th>
                  {hasTask234 && (
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-200">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-indigo-600" />
                        Summary
                      </div>
                    </th>
                  )}
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-250">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-cyan-600" />
                      Remarks
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300 table-col-150">
                    <div className="flex items-center gap-2">
                      <Paperclip className="h-4 w-4 text-amber-600" />
                      Attachments
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap table-col-150">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-pink-600" />
                      Submission Date
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {hierarchicalTasks.map((task, index) => {
                  const taskNo = task.task_no || task.Task_No;
                  const subtaskNo = task.subtask_no || task.Subtask_No;
                  
                  return (
                    <tr 
                      key={`${task.id || task.ID || index}-${taskNo}-${subtaskNo}-${task.quarter_id || task.Quarter_ID || ''}`} 
                      className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 group"
                    >
                      <td className="px-4 py-4 text-center align-top border-r border-gray-200 text-sm font-semibold text-gray-600 tabular-nums w-14 min-w-[3.5rem] sticky left-0 z-20 bg-white group-hover:bg-blue-50/80">
                        {startRow + index}
                      </td>
                      {/* Task Column - Always render, no rowSpan */}
                      <td className="px-6 py-4 align-top border-r-2 border-gray-300 bg-gradient-to-br from-green-50 to-emerald-50 sticky left-14 z-10 table-col-ep-task word-break-safe whitespace-normal break-words">
                        <div className="flex items-center gap-2 max-w-full">
                          <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></div>
                          <span className="font-bold text-green-700 text-sm">
                            Task {taskNo}
                          </span>
                        </div>
                      </td>
                      
                      {/* Subtask - Always render, no rowSpan */}
                      <td className="px-6 py-4 whitespace-normal break-words align-top border-r border-gray-200 table-col-ep-subtask word-break-safe">
                        <div className="text-sm text-gray-900 leading-relaxed max-w-full">
                          <span className="font-semibold text-purple-600">
                            Subtask {subtaskNo}:
                          </span>{' '}
                          <span className="text-gray-700">{task.subtask_description || task.Subtask_Description || <span className="text-gray-400">-</span>}</span>
                        </div>
                      </td>
                      
                      {/* Quarter - Always render, no rowSpan */}
                      <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200">
                        <span className="text-sm font-medium text-gray-700">
                          {task.quarter_name || task.Quarter_Name || <span className="text-gray-400">-</span>}
                        </span>
                      </td>
                      
                      {/* Status - Always render, no rowSpan */}
                      <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold break-words max-w-full ${getStatusColor(task.status)}`}>
                          {task.status === 'Completed' && <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />}
                          {task.status === 'In Progress' && <Clock className="h-3.5 w-3.5 flex-shrink-0" />}
                          {task.status === 'On Hold' && <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />}
                          {task.status === 'Not Started' && <XCircle className="h-3.5 w-3.5 flex-shrink-0" />}
                          <span className="break-words">{task.status}</span>
                        </span>
                      </td>
                      
                      {/* Task 1 Specific Columns - Always render, no rowSpan */}
                      {hasTask1 && (
                        <>
                          {taskNo === 1 ? (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200">
                                {task.is_notified !== null && task.is_notified !== undefined ? (
                                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                    task.is_notified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {task.is_notified ? 'Yes' : 'No'}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200">
                                {task.notification_date ? (
                                  <span className="text-sm text-gray-700">
                                    {new Date(task.notification_date).toLocaleDateString('en-GB', { 
                                      day: '2-digit', 
                                      month: '2-digit', 
                                      year: 'numeric' 
                                    })}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200">
                                {task.incorporation_in_by_laws !== null && task.incorporation_in_by_laws !== undefined ? (
                                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                    task.incorporation_in_by_laws ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {task.incorporation_in_by_laws ? 'Yes' : 'No'}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200">
                                <span className="text-gray-400 text-sm">-</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200">
                                <span className="text-gray-400 text-sm">-</span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200">
                                <span className="text-gray-400 text-sm">-</span>
                              </td>
                            </>
                          )}
                        </>
                      )}
                      
                      {/* Training Columns (conditional) */}
                      {hasTrainingType && (
                        <>
                          <td className="px-6 py-4 whitespace-normal break-words align-top border-r border-gray-200 table-col-180 table-col-max-500">
                            {shouldShowTrainingColumns(task) && task.training_type ? (
                              <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {task.training_type}
                                </span>
                                {task.training_mode && (
                                  <span className="text-xs text-gray-500 italic">
                                    ({task.training_mode})
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200 table-col-150">
                            {shouldShowTrainingColumns(task) && task.training_start_date ? (
                              <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {new Date(task.training_start_date).toLocaleDateString('en-GB', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric' 
                                  })}
                                </span>
                                {task.training_end_date && task.training_end_date !== task.training_start_date && (
                                  <span className="text-xs text-gray-500">
                                    to {new Date(task.training_end_date).toLocaleDateString('en-GB', { 
                                      day: '2-digit', 
                                      month: '2-digit', 
                                      year: 'numeric' 
                                    })}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-normal break-words align-top border-r border-gray-200 table-col-150">
                            {shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' ? (
                              (() => {
                                const nameFromApi = task.training_district_name;
                                const idFromApi = task.training_district;
                                const cellStateId = task.cell_state_id;
                                let resolvedName = nameFromApi;

                                // Only use fallback lookup if name is missing AND district belongs to cell's state
                                if (!resolvedName && idFromApi && cellStateId && dropdownData?.districts) {
                                  const match = dropdownData.districts.find(
                                    (d) => String(d.district_id) === String(idFromApi) && 
                                           String(d.state_id) === String(cellStateId)
                                  );
                                  resolvedName = match?.district_name || null;
                                }

                                return resolvedName ? (
                                  <span className="text-sm text-gray-700">{resolvedName}</span>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                                );
                              })()
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center align-top border-r border-gray-200 table-col-140">
                            {shouldShowTrainingColumns(task) && (task.training_total_attendees !== null && task.training_total_attendees !== undefined) ? (
                              <span className="inline-block text-sm font-semibold text-gray-900 bg-blue-50 px-3 py-1.5 rounded-md border border-blue-200">
                                {task.training_total_attendees || 0}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center align-top border-r border-gray-200 table-col-140">
                            {shouldShowTrainingColumns(task) && (task.training_government_sector_attendees !== null && task.training_government_sector_attendees !== undefined) ? (
                              <span className="text-sm font-medium text-blue-700">
                                {task.training_government_sector_attendees || 0}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center align-top border-r border-gray-200 table-col-150">
                            {shouldShowTrainingColumns(task) && (task.training_total_women !== null && task.training_total_women !== undefined) ? (
                              <span className="text-sm font-medium text-pink-700">
                                {task.training_total_women || 0}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center align-top border-r border-gray-200 table-col-150">
                            {shouldShowTrainingColumns(task) && (task.training_architects_count !== null && task.training_architects_count !== undefined) ? (
                              <span className="text-sm font-medium text-amber-700">
                                {task.training_architects_count || 0}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                        </>
                      )}
                      
                      {/* Building Columns (conditional - Task 2 and Task 4) */}
                      {hasBuildingData && (
                        <>
                          <td className="px-6 py-4 whitespace-normal break-words align-top border-r border-gray-200 table-col-200">
                            {shouldShowBuildingColumns(task) && task.building_name ? (
                              <span className="text-sm font-medium text-gray-900">
                                {task.building_name}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-normal break-words align-top border-r border-gray-200 table-col-250">
                            {shouldShowBuildingColumns(task) && task.building_address ? (
                              <span className="text-sm text-gray-700">
                                {task.building_address}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-normal break-words align-top border-r border-gray-200 table-col-150">
                            {shouldShowBuildingColumns(task) && task.building_typology ? (
                              <span className="text-sm text-gray-700">
                                {task.building_typology}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200 table-col-150">
                            {shouldShowBuildingColumns(task) && task.building_status ? (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200">
                                {task.building_status}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-normal break-words align-top border-r border-gray-200 table-col-180">
                            {shouldShowBuildingColumns(task) && task.building_nodal_contact_name ? (
                              <span className="text-sm text-gray-700">
                                {task.building_nodal_contact_name}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-normal break-words align-top border-r border-gray-200 table-col-200">
                            {shouldShowBuildingColumns(task) && task.building_nodal_email ? (
                              <a 
                                href={`mailto:${task.building_nodal_email}`}
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {task.building_nodal_email}
                              </a>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-normal break-words align-top border-r border-gray-200 table-col-200">
                            {shouldShowBuildingColumns(task) && task.building_nodal_department ? (
                              <span className="text-sm text-gray-700">
                                {task.building_nodal_department}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200 table-col-180">
                            {shouldShowBuildingColumns(task) && task.building_epi_value_proposed !== null && task.building_epi_value_proposed !== undefined ? (
                              <span className="text-sm font-medium text-gray-700">
                                {parseFloat(task.building_epi_value_proposed).toFixed(4)}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200 table-col-150">
                            {shouldShowBuildingColumns(task) && task.building_epi_ratio !== null && task.building_epi_ratio !== undefined ? (
                              <span className="text-sm font-semibold text-green-700 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200">
                                {parseFloat(task.building_epi_ratio).toFixed(4)}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                        </>
                      )}
                      
                      {/* Task Completion Date */}
                      <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200 table-col-150">
                        <span className="text-sm text-gray-700">
                          {task.task_date || task.Task_Date
                            ? new Date(task.task_date || task.Task_Date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : <span className="text-gray-400">-</span>}
                        </span>
                      </td>
                      
                      {/* Summary */}
                      {hasTask234 && (
                        (() => {
                          const taskNo = task.task_no || task.Task_No;
                          const hasBuildingData = (taskNo === 2 || taskNo === 4) && task.totalBuildingRows && task.totalBuildingRows > 1;
                          const hasTrainingData = taskNo === 3 && task.totalTrainingRows && task.totalTrainingRows > 1;
                          const shouldShowRowSpan = task.isFirstSubtaskRow !== false && 
                            ((hasBuildingData && (task.buildingRowIndex === 0 || task.buildingRowIndex === undefined)) ||
                             (hasTrainingData && (task.trainingRowIndex === 0 || task.trainingRowIndex === undefined)) ||
                             (!hasBuildingData && !hasTrainingData));
                          const rowSpanValue = hasBuildingData ? task.totalBuildingRows : (hasTrainingData ? task.totalTrainingRows : (task.subtaskRowSpan || 1));
                          
                          return shouldShowRowSpan ? (
                            <td 
                              rowSpan={rowSpanValue}
                              className="px-6 py-4 whitespace-normal break-words align-top border-r border-gray-200 table-col-200"
                            >
                              {(() => {
                                const summary = calculateSummary(task, filteredBySearch);
                                return summary ? (
                                  <span className="text-sm text-gray-700 font-medium">{summary}</span>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                );
                              })()}
                            </td>
                          ) : null;
                        })()
                      )}
                      
                      {/* Remarks */}
                      <td className="px-6 py-4 whitespace-normal break-words align-top border-r border-gray-200 table-col-300 table-col-max-500">
                        {task.remarks ? (
                          <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none word-break-safe">
                            <RemarksWithStakeholder remarks={task.remarks} />
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      
                      {/* Attachments */}
                      <td className="px-6 py-4 whitespace-nowrap align-top border-r border-gray-200">
                        {(() => {
                          const documentPaths = parseDocumentPaths(task.document_path);
                          if (documentPaths.length === 0) {
                            return <span className="text-gray-400 text-sm">-</span>;
                          } else if (documentPaths.length === 1) {
                            const docPath = documentPaths[0].startsWith('/') ? documentPaths[0] : `/${documentPaths[0]}`;
                            return (
                              <a
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md"
                                href={`${FILE_BASE_URL}${docPath}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Download className="h-4 w-4" />
                                View
                              </a>
                            );
                          } else {
                            return (
                              <div className="flex flex-col gap-1.5">
                                {documentPaths.map((path, idx) => {
                                  const pathParts = path.split('/');
                                  const fileName = pathParts[pathParts.length - 1] || `Document ${idx + 1}`;
                                  const docPath = path.startsWith('/') ? path : `/${path}`;
                                  return (
                                    <a
                                      key={idx}
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors shadow-sm"
                                      href={`${FILE_BASE_URL}${docPath}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      title={fileName}
                                    >
                                      <Download className="h-3.5 w-3.5" />
                                      View
                                    </a>
                                  );
                                })}
                              </div>
                            );
                          }
                        })()}
                      </td>
                      
                      {/* Submission Date */}
                      <td className="px-6 py-4 whitespace-nowrap align-top">
                        <span className="text-sm text-gray-700">
                          {task.submission_date 
                            ? new Date(task.submission_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : task.saved_date
                            ? new Date(task.saved_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : <span className="text-gray-400">-</span>}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <div
              ref={epTableScrollBottomMirrorRef}
              className="dashboard-table-scroll dashboard-table-scroll--bottom w-full min-w-0 max-w-full bg-white"
              onScroll={handleEpTableScrollSync}
            >
              <div ref={epTableScrollBottomMirrorSpacerRef} className="h-px pointer-events-none" aria-hidden />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    cellId: '',
    taskId: '',
    subtaskId: '',
    quarterId: '',
    statusId: '',
    buildingType: '',
    trainingId: '',
    week: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [taskDetailsTab, setTaskDetailsTab] = useState('all'); // 'all' | 'notification' | 'trainings' | 'buildings'
  const itemsPerPage = 10;

  const { isLoading } = useQuery(
    ['dashboard-summary'],
    async () => {
      const response = await axios.get('/api/dashboard/summary');
      return response.data;
    },
    {
      enabled: user?.role === 'BEE-Admin' || user?.role === 'Super-Admin'
    }
  );

  // Fetch dropdown data for filters
  const { data: dropdownData } = useQuery(
    ['dropdowns'],
    async () => {
      const response = await axios.get('/api/masters/dropdowns');
      return response.data;
    },
    {
      enabled: user?.role === 'BEE-Admin' || user?.role === 'Super-Admin'
    }
  );

  // Fetch all unique weeks from database
  const { data: allWeeks } = useQuery(
    ['all-weeks'],
    async () => {
      const response = await axios.get('/api/dashboard/weeks');
      return response.data;
    },
    {
      enabled: user?.role === 'BEE-Admin' || user?.role === 'Super-Admin'
    }
  );

  // Parse document paths
  const parseDocumentPaths = (documentPath) => {
    if (!documentPath) return [];
    try {
      const parsed = JSON.parse(documentPath);
      return Array.isArray(parsed) ? parsed : [documentPath];
    } catch (e) {
      const trimmed = String(documentPath).trim();
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const manualParse = trimmed.slice(1, -1).split(',').map(p => p.trim().replace(/^["']|["']$/g, ''));
          return manualParse.filter(p => p.length > 0);
        } catch (e2) {
          return [documentPath];
        }
      }
      return [documentPath];
    }
  };

  // Fetch filtered tasks
  const { data: filteredTasks, isLoading: isLoadingTasks, isFetching: isFetchingTasks } = useQuery(
    ['filtered-tasks', filters],
    async () => {
      const params = {};
      if (filters.cellId) params.cellId = filters.cellId;
      if (filters.taskId) params.taskId = filters.taskId;
      if (filters.subtaskId) params.subtaskId = filters.subtaskId;
      if (filters.quarterId) params.quarterId = filters.quarterId;
      if (filters.statusId) params.statusId = filters.statusId;
      if (filters.buildingType) params.buildingType = filters.buildingType;
      if (filters.trainingId) params.trainingId = filters.trainingId;
      if (filters.week) params.week = filters.week;
      
      const response = await axios.get('/api/dashboard/tasks', { params });
      return response.data;
    },
    {
      enabled: user?.role === 'BEE-Admin' || user?.role === 'Super-Admin'
    }
  );

  const beeTasksFetchPending = isLoadingTasks || (isFetchingTasks && filteredTasks === undefined);

  // Check if any filter is active
  const hasActiveFilters =
    Object.values(filters).some((val) => val !== '') ||
    searchTerm !== '' ||
    taskDetailsTab === 'notification' ||
    taskDetailsTab === 'trainings' ||
    taskDetailsTab === 'buildings';

  // Filter tasks by search term and week (client-side week filter for additional filtering if needed)
  const filteredBySearch = (filteredTasks?.filter(task => {
    // If trainingId filter is active, only show Task 3 entries
    if (filters.trainingId && filters.trainingId !== '') {
      const taskNo = task.task_no || task.Task_No;
      if (taskNo !== 3) {
        return false; // Filter out non-Task 3 entries when training filter is active
      }
    }
    
    // Week filter is handled server-side, but we can add client-side filtering here if needed
    // For now, week filtering is done on the server
    
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (task.cell_no ? String(task.cell_no).toLowerCase() : '').includes(searchLower) ||
      (task.state_name ? String(task.state_name).toLowerCase() : '').includes(searchLower) ||
      (task.task_description ? String(task.task_description).toLowerCase() : '').includes(searchLower) ||
      (task.subtask_description ? String(task.subtask_description).toLowerCase() : '').includes(searchLower) ||
      (task.status ? String(task.status).toLowerCase() : '').includes(searchLower) ||
      (task.quarter_name ? String(task.quarter_name).toLowerCase() : '').includes(searchLower)
    );
  }) || []).sort((a, b) => {
    // When filters are active, sort by state name alphabetically
    if (hasActiveFilters) {
      const stateA = (a.state_name || '').toLowerCase();
      const stateB = (b.state_name || '').toLowerCase();
      if (stateA !== stateB) {
        return stateA.localeCompare(stateB);
      }
      // If states are the same, sort by cell number
      const cellA = a.cell_no || 0;
      const cellB = b.cell_no || 0;
      return cellA - cellB;
    }
    // When no filters are active, backend already returns sorted by cell number
    // Just maintain that order (or sort by cell number as fallback)
    const cellA = a.cell_no || 0;
    const cellB = b.cell_no || 0;
    return cellA - cellB;
  });

  const notificationSubtaskIdBee = useMemo(() => {
    if (!dropdownData?.subtasks) return null;
    const match = dropdownData.subtasks.find((s) => {
      const tid = s.task_id ?? s.Task_ID;
      if (Number(tid) !== 1) return false;
      const no = String(s.subtask_no ?? s.Subtask_No ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
      return no === '2b';
    });
    if (!match) return null;
    return match.subtask_id ?? match.Subtask_ID ?? null;
  }, [dropdownData]);

  const isBeeNotificationTaskRow = useCallback(
    (task) => {
      const taskNo = Number(task.task_no ?? task.Task_No);
      if (taskNo !== 1) return false;
      if (notificationSubtaskIdBee != null) {
        const sid = task.subtask_id ?? task.Subtask_ID;
        if (sid != null && String(sid) === String(notificationSubtaskIdBee)) return true;
      }
      const raw = String(task.subtask_no ?? task.Subtask_No ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
      return raw === '2b';
    },
    [notificationSubtaskIdBee]
  );

  const tasksForTaskDetailsView = useMemo(() => {
    if (taskDetailsTab !== 'notification') return filteredBySearch;
    return filteredBySearch.filter(isBeeNotificationTaskRow);
  }, [filteredBySearch, taskDetailsTab, isBeeNotificationTaskRow]);

  const beeTrainingsQueryParams = useMemo(() => {
    const p = {};
    if (filters.cellId) p.cellId = filters.cellId;
    if (filters.quarterId) p.quarterId = filters.quarterId;
    if (filters.statusId) p.statusId = filters.statusId;
    if (filters.week) p.week = filters.week;
    if (filters.trainingId) p.trainingId = filters.trainingId;
    return p;
  }, [filters.cellId, filters.quarterId, filters.statusId, filters.week, filters.trainingId]);

  const { data: beeTrainingsData, isLoading: isLoadingBeeTrainings, isFetching: isFetchingBeeTrainings } = useQuery(
    ['bee-trainings-table', beeTrainingsQueryParams],
    async () => {
      const { data } = await axios.get('/api/dashboard/bee-trainings-table', { params: beeTrainingsQueryParams });
      return data;
    },
    {
      enabled:
        (user?.role === 'BEE-Admin' || user?.role === 'Super-Admin') && taskDetailsTab === 'trainings'
    }
  );

  const beeTrainingsFetchPending =
    isLoadingBeeTrainings || (isFetchingBeeTrainings && beeTrainingsData === undefined);
  const beeTrainingsRaw = Array.isArray(beeTrainingsData) ? beeTrainingsData : [];

  const beeTrainingsFiltered = useMemo(() => {
    const list = beeTrainingsRaw;
    if (!searchTerm) return list;
    const q = searchTerm.toLowerCase();
    return list.filter((r) => {
      const state = (r.state_name || '').toLowerCase();
      const cell = String(r.cell_no ?? '').toLowerCase();
      const tt = (r.training_type || '').toLowerCase();
      const mode = (r.training_mode || '').toLowerCase();
      const modeTrim = String(r.training_mode || '').trim().toLowerCase();
      const isOnlineMode = modeTrim === 'online' || modeTrim.includes('online');
      const venueRaw = String(r.training_venue ?? r.training_Venue ?? r.venue ?? '').toLowerCase();
      const venueSearchText = isOnlineMode ? 'online mode' : venueRaw;
      const district = String(r.training_district_name ?? r.training_District_Name ?? '').toLowerCase();
      return (
        state.includes(q) ||
        cell.includes(q) ||
        tt.includes(q) ||
        mode.includes(q) ||
        venueRaw.includes(q) ||
        venueSearchText.includes(q) ||
        district.includes(q)
      );
    });
  }, [beeTrainingsRaw, searchTerm]);

  const beeBuildingsQueryParams = useMemo(() => {
    const p = {};
    if (filters.cellId) p.cellId = filters.cellId;
    if (filters.quarterId) p.quarterId = filters.quarterId;
    if (filters.statusId) p.statusId = filters.statusId;
    if (filters.week) p.week = filters.week;
    if (filters.buildingType) p.buildingType = filters.buildingType;
    if (filters.taskId) p.taskId = filters.taskId;
    if (filters.subtaskId) p.subtaskId = filters.subtaskId;
    return p;
  }, [
    filters.cellId,
    filters.quarterId,
    filters.statusId,
    filters.week,
    filters.buildingType,
    filters.taskId,
    filters.subtaskId
  ]);

  const { data: beeBuildingsData, isLoading: isLoadingBeeBuildings, isFetching: isFetchingBeeBuildings } = useQuery(
    ['bee-buildings-table', beeBuildingsQueryParams],
    async () => {
      const { data } = await axios.get('/api/dashboard/bee-buildings-table', { params: beeBuildingsQueryParams });
      return data;
    },
    {
      enabled:
        (user?.role === 'BEE-Admin' || user?.role === 'Super-Admin') && taskDetailsTab === 'buildings'
    }
  );

  const beeBuildingsFetchPending =
    isLoadingBeeBuildings || (isFetchingBeeBuildings && beeBuildingsData === undefined);
  const beeBuildingsRaw = Array.isArray(beeBuildingsData) ? beeBuildingsData : [];

  const beeBuildingsFiltered = useMemo(() => {
    const list = beeBuildingsRaw;
    if (!searchTerm) return list;
    const q = searchTerm.toLowerCase();
    return list.filter((r) => {
      const state = (r.state_name || '').toLowerCase();
      const cell = String(r.cell_no ?? '').toLowerCase();
      const name = (r.building_name || '').toLowerCase();
      const addr = (r.building_address || '').toLowerCase();
      const typ = (r.building_typology || '').toLowerCase();
      const task = (r.task_description || '').toLowerCase();
      const sub = (r.subtask_description || '').toLowerCase();
      return (
        state.includes(q) ||
        cell.includes(q) ||
        name.includes(q) ||
        addr.includes(q) ||
        typ.includes(q) ||
        task.includes(q) ||
        sub.includes(q)
      );
    });
  }, [beeBuildingsRaw, searchTerm]);

  // Helper function to calculate ISO week number from a date
  const calculateISOWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return weekNo;
  };

  // Helper function to get week string (YYYY-W##) from task_date
  const getWeekFromTaskDate = (taskDate) => {
    if (!taskDate) return null;
    
    // Parse date string (handle both YYYY-MM-DD and Date objects)
    let date;
    if (typeof taskDate === 'string') {
      const dateParts = taskDate.split('T')[0].split('-');
      if (dateParts.length !== 3) return null;
      date = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
    } else {
      date = new Date(taskDate);
    }
    
    if (isNaN(date.getTime())) return null;
    
    // Get Monday of the week containing the date
    const dayOfWeek = date.getDay();
    const daysToMonday = dayOfWeek === 0 ? 1 : -(dayOfWeek - 1);
    const monday = new Date(date);
    monday.setDate(date.getDate() + daysToMonday);
    monday.setHours(0, 0, 0, 0);
    
    const year = monday.getFullYear();
    const weekNumber = calculateISOWeekNumber(monday);
    
    return `${year}-W${String(weekNumber).padStart(2, '0')}`;
  };

  // Use weeks from database (already sorted)
  const uniqueWeeks = allWeeks || [];

  // Helper function to expand tasks with buildings into separate rows (for Task 2 and Task 4)
  const expandTasksWithBuildings = (tasks) => {
    const expanded = [];
    const taskGroups = {};
    
    // Group tasks by cell/task/subtask combination
    tasks.forEach(task => {
      const cellKey = `${task.state_name || ''}_${task.cell_no || ''}`;
      const taskNo = task.task_no || task.Task_No;
      const subtaskNo = task.subtask_no || task.Subtask_No;
      const key = `${cellKey}_${taskNo}_${subtaskNo}`;
      
      if (!taskGroups[key]) {
        taskGroups[key] = [];
      }
      taskGroups[key].push(task);
    });
    
    // Expand each group
    Object.keys(taskGroups).forEach(key => {
      const groupTasks = taskGroups[key];
      const taskNo = Number(groupTasks[0].task_no || groupTasks[0].Task_No);
      const subtaskNo = groupTasks[0].subtask_no || groupTasks[0].Subtask_No;
      
      // For Task 2 and Task 4, expand buildings into separate rows
      if (taskNo === 2 || taskNo === 4) {
        // First, group by quarter to separate buildings by quarter
        const quarterGroups = {};
        const tasksWithoutQuarter = [];
        
        groupTasks.forEach(task => {
          const quarterId = task.quarter_id || task.Quarter_ID;
          if (quarterId) {
            if (!quarterGroups[quarterId]) {
              quarterGroups[quarterId] = [];
            }
            quarterGroups[quarterId].push(task);
          } else {
            tasksWithoutQuarter.push(task);
          }
        });
        
        // Process each quarter group separately
        const quarterKeys = Object.keys(quarterGroups).sort((a, b) => {
          // Sort quarters by ID (assuming lower ID = earlier quarter)
          return Number(a) - Number(b);
        });
        
        quarterKeys.forEach((quarterId) => {
          const quarterTasks = quarterGroups[quarterId];
          
          // Within each quarter, group by building
          const buildingGroups = {};
          const buildingsWithoutId = [];
          
          quarterTasks.forEach(task => {
            const buildingId = task.building_id || task.Building_ID;
            if (buildingId) {
              if (!buildingGroups[buildingId]) {
                buildingGroups[buildingId] = [];
              }
              buildingGroups[buildingId].push(task);
            } else if (task.building_name) {
              // If no building_id but has building_name, use name as key
              const buildingName = task.building_name;
              if (!buildingGroups[buildingName]) {
                buildingGroups[buildingName] = [];
              }
              buildingGroups[buildingName].push(task);
            } else {
              // Tasks without buildings in this quarter
              buildingsWithoutId.push(task);
            }
          });
          
          // Create a row for each unique building in this quarter
          const buildingKeys = Object.keys(buildingGroups);
          
          if (buildingKeys.length > 0) {
            // Sort buildings by building_id or name for consistent ordering
            buildingKeys.sort((a, b) => {
              // Try to sort by ID if both are numeric
              const aNum = parseInt(a);
              const bNum = parseInt(b);
              if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
              }
              // Otherwise sort alphabetically
              return String(a).localeCompare(String(b));
            });
            
            buildingKeys.forEach((buildingKey, buildingIdx) => {
              const buildingTasks = buildingGroups[buildingKey];
              // Use the most recent task for this building in this quarter
              const taskForRow = buildingTasks.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || a.Submission_Date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || b.Submission_Date || 0);
                return dateB - dateA;
              })[0];
              
              // Explicitly preserve all important fields to prevent data corruption
              expanded.push({
                ...taskForRow,
                // Explicitly preserve these fields to ensure they're not lost
                remarks: taskForRow.remarks || taskForRow.Remarks,
                Remarks: taskForRow.Remarks || taskForRow.remarks,
                document_path: taskForRow.document_path || taskForRow.Document_Path,
                Document_Path: taskForRow.Document_Path || taskForRow.document_path,
                submission_date: taskForRow.submission_date || taskForRow.Submission_Date,
                Submission_Date: taskForRow.Submission_Date || taskForRow.submission_date,
                saved_date: taskForRow.saved_date || taskForRow.Saved_Date,
                Saved_Date: taskForRow.Saved_Date || taskForRow.saved_date,
                task_date: taskForRow.task_date || taskForRow.Task_Date,
                Task_Date: taskForRow.Task_Date || taskForRow.task_date,
                buildingRowIndex: buildingIdx,
                totalBuildingRows: buildingKeys.length + (buildingsWithoutId.length > 0 ? 1 : 0),
                isFirstSubtaskRow: buildingIdx === 0 // First building row in this quarter is the first subtask row
              });
            });
          }
          
          // Add row for tasks without buildings in this quarter (if any)
          if (buildingsWithoutId.length > 0) {
            const latestNoBuilding = buildingsWithoutId.sort((a, b) => {
              const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || a.Submission_Date || 0);
              const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || b.Submission_Date || 0);
              return dateB - dateA;
            })[0];
            
            // Explicitly preserve all important fields
            expanded.push({
              ...latestNoBuilding,
              remarks: latestNoBuilding.remarks || latestNoBuilding.Remarks,
              Remarks: latestNoBuilding.Remarks || latestNoBuilding.remarks,
              document_path: latestNoBuilding.document_path || latestNoBuilding.Document_Path,
              Document_Path: latestNoBuilding.Document_Path || latestNoBuilding.document_path,
              submission_date: latestNoBuilding.submission_date || latestNoBuilding.Submission_Date,
              Submission_Date: latestNoBuilding.Submission_Date || latestNoBuilding.submission_date,
              saved_date: latestNoBuilding.saved_date || latestNoBuilding.Saved_Date,
              Saved_Date: latestNoBuilding.Saved_Date || latestNoBuilding.saved_date,
              task_date: latestNoBuilding.task_date || latestNoBuilding.Task_Date,
              Task_Date: latestNoBuilding.Task_Date || latestNoBuilding.task_date,
              buildingRowIndex: buildingKeys.length,
              totalBuildingRows: buildingKeys.length + 1,
              isFirstSubtaskRow: buildingKeys.length === 0 // Only first if no buildings before it
            });
          }
        });
        
        // Handle tasks without quarter (if any)
        if (tasksWithoutQuarter.length > 0) {
          // Group by building for tasks without quarter
          const buildingGroups = {};
          const buildingsWithoutId = [];
          
          tasksWithoutQuarter.forEach(task => {
            const buildingId = task.building_id || task.Building_ID;
            if (buildingId) {
              if (!buildingGroups[buildingId]) {
                buildingGroups[buildingId] = [];
              }
              buildingGroups[buildingId].push(task);
            } else if (task.building_name) {
              const buildingName = task.building_name;
              if (!buildingGroups[buildingName]) {
                buildingGroups[buildingName] = [];
              }
              buildingGroups[buildingName].push(task);
            } else {
              buildingsWithoutId.push(task);
            }
          });
          
          const buildingKeys = Object.keys(buildingGroups);
          
          if (buildingKeys.length > 0) {
            buildingKeys.sort((a, b) => {
              const aNum = parseInt(a);
              const bNum = parseInt(b);
              if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
              }
              return String(a).localeCompare(String(b));
            });
            
            buildingKeys.forEach((buildingKey, buildingIdx) => {
              const buildingTasks = buildingGroups[buildingKey];
              const taskForRow = buildingTasks.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || a.Submission_Date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || b.Submission_Date || 0);
                return dateB - dateA;
              })[0];
              
              // Explicitly preserve all important fields
              expanded.push({
                ...taskForRow,
                remarks: taskForRow.remarks || taskForRow.Remarks,
                Remarks: taskForRow.Remarks || taskForRow.remarks,
                document_path: taskForRow.document_path || taskForRow.Document_Path,
                Document_Path: taskForRow.Document_Path || taskForRow.document_path,
                submission_date: taskForRow.submission_date || taskForRow.Submission_Date,
                Submission_Date: taskForRow.Submission_Date || taskForRow.submission_date,
                saved_date: taskForRow.saved_date || taskForRow.Saved_Date,
                Saved_Date: taskForRow.Saved_Date || taskForRow.saved_date,
                task_date: taskForRow.task_date || taskForRow.Task_Date,
                Task_Date: taskForRow.Task_Date || taskForRow.task_date,
                buildingRowIndex: buildingIdx,
                totalBuildingRows: buildingKeys.length + (buildingsWithoutId.length > 0 ? 1 : 0),
                isFirstSubtaskRow: buildingIdx === 0
              });
            });
          }
          
          if (buildingsWithoutId.length > 0) {
            const latestNoBuilding = buildingsWithoutId.sort((a, b) => {
              const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || a.Submission_Date || 0);
              const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || b.Submission_Date || 0);
              return dateB - dateA;
            })[0];
            
            // Explicitly preserve all important fields
            expanded.push({
              ...latestNoBuilding,
              remarks: latestNoBuilding.remarks || latestNoBuilding.Remarks,
              Remarks: latestNoBuilding.Remarks || latestNoBuilding.remarks,
              document_path: latestNoBuilding.document_path || latestNoBuilding.Document_Path,
              Document_Path: latestNoBuilding.Document_Path || latestNoBuilding.document_path,
              submission_date: latestNoBuilding.submission_date || latestNoBuilding.Submission_Date,
              Submission_Date: latestNoBuilding.Submission_Date || latestNoBuilding.submission_date,
              saved_date: latestNoBuilding.saved_date || latestNoBuilding.Saved_Date,
              Saved_Date: latestNoBuilding.Saved_Date || latestNoBuilding.saved_date,
              task_date: latestNoBuilding.task_date || latestNoBuilding.Task_Date,
              Task_Date: latestNoBuilding.Task_Date || latestNoBuilding.task_date,
              buildingRowIndex: buildingKeys.length,
              totalBuildingRows: buildingKeys.length + 1,
              isFirstSubtaskRow: buildingKeys.length === 0
            });
          }
        }
        
        // If no buildings at all, add one row
        if (quarterKeys.length === 0 && tasksWithoutQuarter.length === 0 && groupTasks.length > 0) {
          const firstTask = groupTasks[0];
          // Explicitly preserve all important fields
          expanded.push({
            ...firstTask,
            remarks: firstTask.remarks || firstTask.Remarks,
            Remarks: firstTask.Remarks || firstTask.remarks,
            document_path: firstTask.document_path || firstTask.Document_Path,
            Document_Path: firstTask.Document_Path || firstTask.document_path,
            submission_date: firstTask.submission_date || firstTask.Submission_Date,
            Submission_Date: firstTask.Submission_Date || firstTask.submission_date,
            saved_date: firstTask.saved_date || firstTask.Saved_Date,
            Saved_Date: firstTask.Saved_Date || firstTask.saved_date,
            task_date: firstTask.task_date || firstTask.Task_Date,
            Task_Date: firstTask.Task_Date || firstTask.task_date,
            buildingRowIndex: 0,
            totalBuildingRows: 1,
            isFirstSubtaskRow: true
          });
        }
      } else {
        // For Task 3, pass through all entries (they will be processed by expandTasksWithTrainings)
        // For other tasks, use the most recent entry
        if (taskNo === 3) {
          // Pass through all Task 3 entries unchanged
          groupTasks.forEach(task => {
            expanded.push({
              ...task,
              buildingRowIndex: 0,
              totalBuildingRows: 1
            });
          });
        } else {
          // For other tasks (not 2, 3, or 4), use the most recent entry
          const sortedTasks = [...groupTasks].sort((a, b) => {
            const dateA = new Date(a.submission_date || a.Saved_Date || 0);
            const dateB = new Date(b.submission_date || b.Saved_Date || 0);
            return dateB - dateA;
          });
          expanded.push({
            ...sortedTasks[0],
            buildingRowIndex: 0,
            totalBuildingRows: 1
          });
        }
      }
    });
    
    return expanded;
  };

  // Helper function to expand Task 3 trainings into separate rows (for BEE Admin Dashboard)
  const expandTasksWithTrainings = (tasks) => {
    const expanded = [];
    const taskGroups = {};
    
    // Group tasks by cell/task/subtask combination
    tasks.forEach(task => {
      const cellKey = `${task.state_name || ''}_${task.cell_no || ''}`;
      const taskNo = task.task_no || task.Task_No;
      const subtaskNo = task.subtask_no || task.Subtask_No;
      const key = `${cellKey}_${taskNo}_${subtaskNo}`;
      
      if (!taskGroups[key]) {
        taskGroups[key] = [];
      }
      taskGroups[key].push(task);
    });
    
    // Expand each group
    Object.keys(taskGroups).forEach(key => {
      const groupTasks = taskGroups[key];
      const taskNo = Number(groupTasks[0].task_no || groupTasks[0].Task_No);
      const subtaskNo = groupTasks[0].subtask_no || groupTasks[0].Subtask_No;
      
      // For Task 3, expand trainings into separate rows (for subtasks 2, 3, 4, 5)
      if (taskNo === 3) {
        const subtaskNoStr = String(subtaskNo || '').toLowerCase();
        const shouldExpand = subtaskNoStr.startsWith('2') || subtaskNoStr.startsWith('3') || subtaskNoStr.startsWith('4') || subtaskNoStr.startsWith('5');
        
        if (shouldExpand) {
          // First, group by quarter to separate trainings by quarter
          const quarterGroups = {};
          const tasksWithoutQuarter = [];
          
          groupTasks.forEach(task => {
            const quarterId = task.quarter_id || task.Quarter_ID;
            if (quarterId) {
              if (!quarterGroups[quarterId]) {
                quarterGroups[quarterId] = [];
              }
              quarterGroups[quarterId].push(task);
            } else {
              tasksWithoutQuarter.push(task);
            }
          });
          
          // Process each quarter group separately
          const quarterKeys = Object.keys(quarterGroups).sort((a, b) => {
            // Sort quarters by ID (assuming lower ID = earlier quarter)
            return Number(a) - Number(b);
          });
          
          quarterKeys.forEach((quarterId) => {
            const quarterTasks = quarterGroups[quarterId];
            
            // Within each quarter, group by training
            const trainingGroups = {};
            const trainingsWithoutId = [];
            
            quarterTasks.forEach(task => {
              const trainingId = task.training_id || task.Training_ID;
              if (trainingId) {
                if (!trainingGroups[trainingId]) {
                  trainingGroups[trainingId] = [];
                }
                trainingGroups[trainingId].push(task);
              } else {
                // Tasks without trainings in this quarter
                trainingsWithoutId.push(task);
              }
            });
            
            // Show ALL training entries for this quarter (not just latest per training_id)
            const allTrainingEntries = [];
            Object.keys(trainingGroups).forEach(trainingId => {
              const entries = trainingGroups[trainingId];
              // Sort entries by date (most recent first) but include ALL entries
              entries.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
                return dateB - dateA;
              });
              // Add ALL entries, not just the latest one
              entries.forEach(entry => {
                allTrainingEntries.push({
                  ...entry,
                  trainingGroupId: trainingId
                });
              });
            });
            
            // Sort all trainings by training_id, then by submission date
            allTrainingEntries.sort((a, b) => {
              const aId = parseInt(a.trainingGroupId);
              const bId = parseInt(b.trainingGroupId);
              if (!isNaN(aId) && !isNaN(bId) && aId !== bId) {
                return aId - bId;
              }
              // If same training_id, sort by date (most recent first)
              const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
              const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
              return dateB - dateA;
            });
            
            const totalTrainingRows = allTrainingEntries.length + (trainingsWithoutId.length > 0 ? 1 : 0);
            
            if (allTrainingEntries.length > 0) {
              allTrainingEntries.forEach((taskForRow, trainingIdx) => {
                expanded.push({
                  ...taskForRow,
                  // Normalize: Each row contains all its own data
                  task_no: taskNo,
                  Task_No: taskNo,
                  subtask_no: subtaskNo,
                  Subtask_No: subtaskNo,
                  subtask_description: groupTasks[0].subtask_description || groupTasks[0].Subtask_Description,
                  Subtask_Description: groupTasks[0].subtask_description || groupTasks[0].Subtask_Description,
                  // Remove rowSpan flags - each row is independent
                  isFirstTaskRow: false,
                  isFirstSubtaskRow: false
                });
              });
            }
            
            // Add row for tasks without trainings in this quarter (if any)
            if (trainingsWithoutId.length > 0) {
              const latestNoTraining = trainingsWithoutId.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
                return dateB - dateA;
              })[0];
              
              expanded.push({
                ...latestNoTraining,
                trainingRowIndex: allTrainingEntries.length,
                totalTrainingRows: totalTrainingRows,
                isFirstSubtaskRow: allTrainingEntries.length === 0 // Only first if no trainings before it
              });
            }
          });
          
          // Handle tasks without quarter (if any)
          if (tasksWithoutQuarter.length > 0) {
            // Group by training for tasks without quarter
            const trainingGroups = {};
            const trainingsWithoutId = [];
            
            tasksWithoutQuarter.forEach(task => {
              const trainingId = task.training_id || task.Training_ID;
              if (trainingId) {
                if (!trainingGroups[trainingId]) {
                  trainingGroups[trainingId] = [];
                }
                trainingGroups[trainingId].push(task);
              } else {
                trainingsWithoutId.push(task);
              }
            });
            
            // Create a row for each unique training (latest entry per training_id)
            const uniqueTrainingEntries = [];
            Object.keys(trainingGroups).forEach(trainingId => {
              const entries = trainingGroups[trainingId];
              // Sort entries by date (most recent first) and take only the latest one
              entries.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
                return dateB - dateA;
              });
              // Take only the latest entry (first one after sorting)
              uniqueTrainingEntries.push({
                ...entries[0],
                trainingGroupId: trainingId,
                totalInGroup: entries.length
              });
            });
            
            // Sort unique trainings by training_id
            uniqueTrainingEntries.sort((a, b) => {
              const aId = parseInt(a.trainingGroupId);
              const bId = parseInt(b.trainingGroupId);
              if (!isNaN(aId) && !isNaN(bId)) {
                return aId - bId;
              }
              return 0;
            });
            
            const totalTrainingRowsNoQuarter = uniqueTrainingEntries.length + (trainingsWithoutId.length > 0 ? 1 : 0);
            
            if (uniqueTrainingEntries.length > 0) {
              uniqueTrainingEntries.forEach((taskForRow, trainingIdx) => {
                expanded.push({
                  ...taskForRow,
                  trainingRowIndex: trainingIdx,
                  totalTrainingRows: totalTrainingRowsNoQuarter,
                  isFirstSubtaskRow: trainingIdx === 0
                });
              });
            }
            
            if (trainingsWithoutId.length > 0) {
              const latestNoTraining = trainingsWithoutId.sort((a, b) => {
                const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
                const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
                return dateB - dateA;
              })[0];
              
              expanded.push({
                ...latestNoTraining,
                trainingRowIndex: uniqueTrainingEntries.length,
                totalTrainingRows: totalTrainingRowsNoQuarter,
                isFirstSubtaskRow: uniqueTrainingEntries.length === 0
              });
            }
          }
          
          // If no trainings at all, add one row
          if (quarterKeys.length === 0 && tasksWithoutQuarter.length === 0 && groupTasks.length > 0) {
            expanded.push({
              ...groupTasks[0],
              trainingRowIndex: 0,
              totalTrainingRows: 1
            });
          }
        } else {
          // For Task 3 subtasks that don't need expansion, just add the latest entry
          const latest = groupTasks.sort((a, b) => {
            const dateA = new Date(a.submission_date || a.Saved_Date || a.saved_date || 0);
            const dateB = new Date(b.submission_date || b.Saved_Date || b.saved_date || 0);
            return dateB - dateA;
          })[0];
          
          expanded.push({
            ...latest,
            trainingRowIndex: 0,
            totalTrainingRows: 1
          });
        }
      } else {
        // For non-Task 3 tasks, just add them as-is (they were already processed by expandTasksWithBuildings)
        groupTasks.forEach(task => {
          expanded.push(task);
        });
      }
    });
    
    return expanded;
  };

  // Helper function to group tasks hierarchically for table display
  const groupTasksHierarchically = (tasks, previousPageLastCellKey = null) => {
    if (!tasks || tasks.length === 0) return [];
    
    // First expand tasks with buildings, then expand tasks with trainings
    const expandedWithBuildings = expandTasksWithBuildings(tasks);
    const expandedTasks = expandTasksWithTrainings(expandedWithBuildings);
    
    // Group by cell (state_name + cell_no), then by task_no
    const cellGroups = {};
    expandedTasks.forEach(task => {
      const cellKey = `${task.state_name || ''}_${task.cell_no || ''}`;
      if (!cellGroups[cellKey]) {
        cellGroups[cellKey] = {
          stateName: task.state_name,
          cellNo: task.cell_no,
          tasks: {}
        };
      }
      const taskNo = task.task_no || task.Task_No;
      if (!cellGroups[cellKey].tasks[taskNo]) {
        cellGroups[cellKey].tasks[taskNo] = [];
      }
      cellGroups[cellKey].tasks[taskNo].push(task);
    });
    
    // Convert to flat array with rowSpan information
    const result = [];
    let previousCellKey = previousPageLastCellKey; // Start with previous page's last cell
    
    Object.keys(cellGroups).forEach(cellKey => {
      const cellGroup = cellGroups[cellKey];
      let cellRowSpan = 0;
      
      // Calculate total rows for this cell (accounting for building expansions)
      Object.keys(cellGroup.tasks).forEach(taskNo => {
        // Group by subtask to count building rows
        const subtaskGroups = {};
        cellGroup.tasks[taskNo].forEach(task => {
          const subtaskNo = task.subtask_no || task.Subtask_No;
          if (!subtaskGroups[subtaskNo]) {
            subtaskGroups[subtaskNo] = [];
          }
          subtaskGroups[subtaskNo].push(task);
        });
        Object.keys(subtaskGroups).forEach(subtaskNo => {
          cellRowSpan += subtaskGroups[subtaskNo].length;
        });
      });
      
      // Check if this is a new cell (different from previous cell key)
      // If the first cell in current page is same as last cell from previous page, it's a continuation
      const isNewCell = previousCellKey !== cellKey;
      previousCellKey = cellKey;
      
      // Add tasks with their subtasks
      let isFirstRowInCell = true;
      let rowCountInCell = 0; // Track row count to identify first row
      Object.keys(cellGroup.tasks).sort((a, b) => Number(a) - Number(b)).forEach(taskNo => {
        const allSubtasks = cellGroup.tasks[taskNo];
        
        // Group by subtask to handle building expansions
        const subtaskGroups = {};
        allSubtasks.forEach(subtask => {
          const subtaskNo = subtask.subtask_no || subtask.Subtask_No;
          if (!subtaskGroups[subtaskNo]) {
            subtaskGroups[subtaskNo] = [];
          }
          subtaskGroups[subtaskNo].push(subtask);
        });
        
        let taskRowIndex = 0;
        Object.keys(subtaskGroups).sort((a, b) => {
          const aMatch = String(a).match(/^(\d+)([a-z]?)$/i);
          const bMatch = String(b).match(/^(\d+)([a-z]?)$/i);
          if (!aMatch || !bMatch) return String(a).localeCompare(String(b));
          const aNum = parseInt(aMatch[1], 10);
          const bNum = parseInt(bMatch[1], 10);
          if (aNum !== bNum) return aNum - bNum;
          return (aMatch[2] || '').localeCompare(bMatch[2] || '');
        }).forEach(subtaskNo => {
          const subtaskRows = subtaskGroups[subtaskNo];
          const totalRowsForSubtask = subtaskRows.length;
          
          subtaskRows.forEach((subtask, idx) => {
            // Always mark the first row of the cell as isFirstCellRow: true
            // This ensures the Cell Name column is rendered even when filtering by cell
            const shouldBeFirstCellRow = rowCountInCell === 0;
            
            result.push({
              ...subtask,
              isFirstCellRow: shouldBeFirstCellRow,
              isFirstTaskRow: taskRowIndex === 0,
              cellRowSpan: cellRowSpan,
              taskRowSpan: allSubtasks.length,
              cellKey: cellKey,
              taskNo: Number(taskNo),
              isFirstSubtaskRow: idx === 0,
              subtaskRowSpan: totalRowsForSubtask
            });
            taskRowIndex++;
            rowCountInCell++;
            isFirstRowInCell = false;
          });
        });
      });
    });
    
    return result;
  };

  // Helper function to check if training columns should be shown (Task 3, Subtasks 2, 3, 4)
  const shouldShowTrainingColumns = (task) => {
    const taskNo = task.task_no || task.Task_No;
    const subtaskNo = task.subtask_no || task.Subtask_No;
    
    // Check if it's Task 3 and Subtasks 2, 3, 4, or 5 (Training-A, Training-B, Training-C)
    if (taskNo === 3) {
      const subtaskNoStr = String(subtaskNo || '').toLowerCase();
      // Handle both numeric (2, 3, 4, 5) and alphanumeric (2a, 2b, 3a, 5a, etc.)
      return subtaskNoStr.startsWith('2') || subtaskNoStr.startsWith('3') || subtaskNoStr.startsWith('4') || subtaskNoStr.startsWith('5');
    }
    return false;
  };

  // Function to get training counts per cell (only for Task 3)
  const getTrainingCounts = (cellKey) => {
    const trainingCounts = {};
    let totalTrainings = 0;
    
    tasksForTaskDetailsView.forEach(task => {
      const taskCellKey = `${task.state_name || ''}_${task.cell_no || ''}`;
      const taskNo = task.task_no || task.Task_No;
      
      // Only count trainings for Task 3
      if (taskCellKey === cellKey && taskNo === 3 && task.training_type && task.training_type.trim() !== '') {
        totalTrainings++; // Count all trainings regardless of type
        const trainingType = task.training_type;
        // Extract training type (e.g., "Training-A", "Training-B", etc.)
        const match = trainingType.match(/training-([a-z])/i);
        if (match) {
          const trainingLetter = match[1].toUpperCase();
          const key = `Training-${trainingLetter}`;
          trainingCounts[key] = (trainingCounts[key] || 0) + 1;
        } else {
          // If it doesn't match the pattern, use the full training type name
          trainingCounts[trainingType] = (trainingCounts[trainingType] || 0) + 1;
        }
      }
    });
    
    return {
      byType: trainingCounts,
      total: totalTrainings
    };
  };

  // Calculate maximum task number from all tasks (not just filtered)
  const maxTaskNo = useMemo(() => {
    if (!dropdownData?.tasks?.length) return 0;
    return Math.max(...(dropdownData.tasks || []).map(t => t.Task_No || t.task_no || 0));
  }, [dropdownData]);

  // Task-aware pagination: Group by task number first, then paginate by complete tasks
  const { taskGroups, paginatedTasks, totalPages: calculatedTotalPages, startRow, endRow } = useMemo(() => {
    // Group tasks by task number
    const groups = {};
    tasksForTaskDetailsView.forEach(task => {
      const taskNo = task.task_no || task.Task_No;
      if (!taskNo) return; // Skip tasks without task number
      
      if (!groups[taskNo]) {
        groups[taskNo] = {
          taskNo: Number(taskNo),
          tasks: []
        };
      }
      groups[taskNo].tasks.push(task);
    });
    
    // Convert to array and sort by task number
    const sortedTaskGroups = Object.values(groups).sort((a, b) => {
      return a.taskNo - b.taskNo;
    });
    
    // Each task gets its own page (page 1 = Task 1, page 2 = Task 2, etc.)
    const totalPages = Math.max(1, sortedTaskGroups.length);
    
    // Get tasks for current page (one task per page)
    const currentTaskGroup = sortedTaskGroups[currentPage - 1];
    const paginatedTasks = currentTaskGroup ? currentTaskGroup.tasks : [];
    
    // Calculate start and end row numbers for display
    let startRow = 1;
    let endRow = paginatedTasks.length;
    if (currentPage > 1) {
      // Calculate rows before current page
      let rowsBefore = 0;
      for (let i = 0; i < currentPage - 1; i++) {
        rowsBefore += sortedTaskGroups[i].tasks.length;
      }
      startRow = rowsBefore + 1;
      endRow = rowsBefore + paginatedTasks.length;
    }
    
    return {
      taskGroups: sortedTaskGroups,
      paginatedTasks,
      totalPages,
      startRow,
      endRow
    };
  }, [tasksForTaskDetailsView, currentPage]);
  
  // Get the last task from previous page to check if task continues
  const previousPageLastTask = useMemo(() => {
    if (currentPage > 1 && taskGroups[currentPage - 2]) {
      const previousTaskGroup = taskGroups[currentPage - 2];
      return previousTaskGroup.tasks[previousTaskGroup.tasks.length - 1];
    }
    return null;
  }, [taskGroups, currentPage]);
  
  const previousPageLastCellKey = previousPageLastTask 
    ? `${previousPageLastTask.state_name || ''}_${previousPageLastTask.cell_no || ''}`
    : null;
  
  // Group paginated tasks hierarchically
  const hierarchicalTasks = groupTasksHierarchically(paginatedTasks, previousPageLastCellKey);
  
  // Use calculated total pages
  const totalPages = calculatedTotalPages;

  // Check if Task 3 is present in the current displayed rows (needed to show training columns)
  // Only show training columns when Task 3 is in the current view
  const hasTask3 = useMemo(() => {
    return hierarchicalTasks.some(task => {
      const taskNo = task.task_no || task.Task_No;
      return taskNo === 3;
    });
  }, [hierarchicalTasks]);

  // Check if any task in the CURRENT DISPLAYED ROWS has training type (only for Task 3, Subtasks 2, 3, 4) AND actually has training data
  // Only show columns if Task 3 is present AND there's actual training data
  const hasTrainingType = useMemo(() => {
    // Only show training columns if Task 3 is in the current view
    if (!hasTask3) return false;
    
    // Check if there's actual training data in Task 3, Subtasks 2, 3, 4
    return hierarchicalTasks.some(task => {
      if (!shouldShowTrainingColumns(task)) return false;
      const trainingType = task.training_type || task.Training_Type;
      return trainingType && 
             trainingType !== null && 
             trainingType !== undefined && 
             trainingType !== '' &&
             String(trainingType).trim() !== '';
    });
  }, [hierarchicalTasks, hasTask3]);

  // Helper function to check if building columns should be shown (Task 2 and Task 4)
  const shouldShowBuildingColumns = (task) => {
    const taskNo = task.task_no || task.Task_No;
    // Handle both string and number comparisons
    const taskNum = Number(taskNo);
    return taskNum === 2 || taskNum === 4;
  };

  // Check if Task 2 or Task 4 is present (show building columns for these tasks, similar to training columns for Task 3)
  const hasBuildingData = useMemo(() => {
    return hierarchicalTasks.some(task => {
      const taskNo = task.task_no || task.Task_No;
      // Handle both string and number comparisons
      const taskNum = Number(taskNo);
      return taskNum === 2 || taskNum === 4;
    });
  }, [hierarchicalTasks]);

  // Check if Tasks 2, 3, or 4 are present (for Summary column)
  const hasTask234 = useMemo(() => {
    return hierarchicalTasks.some(task => {
      const taskNo = task.task_no || task.Task_No;
      return taskNo === 2 || taskNo === 3 || taskNo === 4;
    });
  }, [hierarchicalTasks]);

  // Check if Task 1 is present (for Task 1 specific columns)
  const hasTask1 = useMemo(() => {
    return hierarchicalTasks.some(task => {
      const taskNo = task.task_no || task.Task_No;
      return taskNo === 1;
    });
  }, [hierarchicalTasks]);

  const tableScrollBottomRef = useRef(null);
  const tableScrollTopRef = useRef(null);
  const tableScrollSpacerRef = useRef(null);
  const tableScrollBottomMirrorRef = useRef(null);
  const tableScrollBottomMirrorSpacerRef = useRef(null);
  const tableScrollSyncLock = useRef(false);

  const beeTrainingsScrollBottomRef = useRef(null);
  const beeTrainingsScrollTopRef = useRef(null);
  const beeTrainingsScrollSpacerRef = useRef(null);
  const beeTrainingsScrollBottomMirrorRef = useRef(null);
  const beeTrainingsScrollBottomMirrorSpacerRef = useRef(null);
  const beeTrainingsScrollSyncLock = useRef(false);

  const beeBuildingsScrollBottomRef = useRef(null);
  const beeBuildingsScrollTopRef = useRef(null);
  const beeBuildingsScrollSpacerRef = useRef(null);
  const beeBuildingsScrollBottomMirrorRef = useRef(null);
  const beeBuildingsScrollBottomMirrorSpacerRef = useRef(null);
  const beeBuildingsScrollSyncLock = useRef(false);

  const handleTableScrollSync = useCallback((e) => {
    const source = e.currentTarget;
    const bottom = tableScrollBottomRef.current;
    const top = tableScrollTopRef.current;
    const mirror = tableScrollBottomMirrorRef.current;
    if (!bottom || tableScrollSyncLock.current) return;
    tableScrollSyncLock.current = true;
    const x = source.scrollLeft;
    if (source !== bottom) bottom.scrollLeft = x;
    if (top && source !== top) top.scrollLeft = x;
    if (mirror && source !== mirror) mirror.scrollLeft = x;
    requestAnimationFrame(() => {
      tableScrollSyncLock.current = false;
    });
  }, []);

  const handleTableScrollSyncFromTop = handleTableScrollSync;
  const handleTableScrollSyncFromBottom = handleTableScrollSync;

  useEffect(() => {
    if (beeTasksFetchPending || tasksForTaskDetailsView.length === 0) return;

    const bottom = tableScrollBottomRef.current;
    const spacer = tableScrollSpacerRef.current;
    const top = tableScrollTopRef.current;
    const mirror = tableScrollBottomMirrorRef.current;
    const mirrorSpacer = tableScrollBottomMirrorSpacerRef.current;
    if (!bottom || !spacer) return;

    const tableEl = bottom.querySelector('table');
    let pendingRaf = null;

    const applySpacer = () => {
      const w = `${bottom.scrollWidth}px`;
      spacer.style.width = w;
      if (mirrorSpacer) mirrorSpacer.style.width = w;
      const x = bottom.scrollLeft;
      if (top) top.scrollLeft = x;
      if (mirror) mirror.scrollLeft = x;
    };

    const ro = new ResizeObserver(() => {
      if (pendingRaf != null) cancelAnimationFrame(pendingRaf);
      pendingRaf = requestAnimationFrame(() => {
        pendingRaf = null;
        ro.disconnect();
        applySpacer();
        ro.observe(bottom);
        if (tableEl) ro.observe(tableEl);
      });
    });

    applySpacer();
    const rafId = requestAnimationFrame(() => applySpacer());
    ro.observe(bottom);
    if (tableEl) ro.observe(tableEl);

    return () => {
      if (pendingRaf != null) cancelAnimationFrame(pendingRaf);
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [
    beeTasksFetchPending,
    tasksForTaskDetailsView.length,
    hierarchicalTasks,
    currentPage,
    hasTask1,
    hasTrainingType,
    hasBuildingData,
    hasTask234,
    taskDetailsTab
  ]);

  const handleBeeTrainingsScrollSync = useCallback((e) => {
    const source = e.currentTarget;
    const bottom = beeTrainingsScrollBottomRef.current;
    const top = beeTrainingsScrollTopRef.current;
    const mirror = beeTrainingsScrollBottomMirrorRef.current;
    if (!bottom || beeTrainingsScrollSyncLock.current) return;
    beeTrainingsScrollSyncLock.current = true;
    const x = source.scrollLeft;
    if (source !== bottom) bottom.scrollLeft = x;
    if (top && source !== top) top.scrollLeft = x;
    if (mirror && source !== mirror) mirror.scrollLeft = x;
    requestAnimationFrame(() => {
      beeTrainingsScrollSyncLock.current = false;
    });
  }, []);

  const handleBeeTrainingsScrollFromTop = handleBeeTrainingsScrollSync;
  const handleBeeTrainingsScrollFromBottom = handleBeeTrainingsScrollSync;

  useEffect(() => {
    if (taskDetailsTab !== 'trainings' || beeTrainingsFetchPending || beeTrainingsFiltered.length === 0) return;

    const bottom = beeTrainingsScrollBottomRef.current;
    const spacer = beeTrainingsScrollSpacerRef.current;
    const top = beeTrainingsScrollTopRef.current;
    const mirror = beeTrainingsScrollBottomMirrorRef.current;
    const mirrorSpacer = beeTrainingsScrollBottomMirrorSpacerRef.current;
    if (!bottom || !spacer) return;

    const tableEl = bottom.querySelector('table');
    let pendingRaf = null;

    const applySpacer = () => {
      const w = `${bottom.scrollWidth}px`;
      spacer.style.width = w;
      if (mirrorSpacer) mirrorSpacer.style.width = w;
      const x = bottom.scrollLeft;
      if (top) top.scrollLeft = x;
      if (mirror) mirror.scrollLeft = x;
    };

    const ro = new ResizeObserver(() => {
      if (pendingRaf != null) cancelAnimationFrame(pendingRaf);
      pendingRaf = requestAnimationFrame(() => {
        pendingRaf = null;
        ro.disconnect();
        applySpacer();
        ro.observe(bottom);
        if (tableEl) ro.observe(tableEl);
      });
    });

    applySpacer();
    const rafId = requestAnimationFrame(() => applySpacer());
    ro.observe(bottom);
    if (tableEl) ro.observe(tableEl);

    return () => {
      if (pendingRaf != null) cancelAnimationFrame(pendingRaf);
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [taskDetailsTab, beeTrainingsFetchPending, beeTrainingsFiltered.length]);

  const handleBeeBuildingsScrollSync = useCallback((e) => {
    const source = e.currentTarget;
    const bottom = beeBuildingsScrollBottomRef.current;
    const top = beeBuildingsScrollTopRef.current;
    const mirror = beeBuildingsScrollBottomMirrorRef.current;
    if (!bottom || beeBuildingsScrollSyncLock.current) return;
    beeBuildingsScrollSyncLock.current = true;
    const x = source.scrollLeft;
    if (source !== bottom) bottom.scrollLeft = x;
    if (top && source !== top) top.scrollLeft = x;
    if (mirror && source !== mirror) mirror.scrollLeft = x;
    requestAnimationFrame(() => {
      beeBuildingsScrollSyncLock.current = false;
    });
  }, []);

  const handleBeeBuildingsScrollFromTop = handleBeeBuildingsScrollSync;
  const handleBeeBuildingsScrollFromBottom = handleBeeBuildingsScrollSync;

  useEffect(() => {
    if (taskDetailsTab !== 'buildings' || beeBuildingsFetchPending || beeBuildingsFiltered.length === 0) return;

    const bottom = beeBuildingsScrollBottomRef.current;
    const spacer = beeBuildingsScrollSpacerRef.current;
    const top = beeBuildingsScrollTopRef.current;
    const mirror = beeBuildingsScrollBottomMirrorRef.current;
    const mirrorSpacer = beeBuildingsScrollBottomMirrorSpacerRef.current;
    if (!bottom || !spacer) return;

    const tableEl = bottom.querySelector('table');
    let pendingRaf = null;

    const applySpacer = () => {
      const w = `${bottom.scrollWidth}px`;
      spacer.style.width = w;
      if (mirrorSpacer) mirrorSpacer.style.width = w;
      const x = bottom.scrollLeft;
      if (top) top.scrollLeft = x;
      if (mirror) mirror.scrollLeft = x;
    };

    const ro = new ResizeObserver(() => {
      if (pendingRaf != null) cancelAnimationFrame(pendingRaf);
      pendingRaf = requestAnimationFrame(() => {
        pendingRaf = null;
        ro.disconnect();
        applySpacer();
        ro.observe(bottom);
        if (tableEl) ro.observe(tableEl);
      });
    });

    applySpacer();
    const rafId = requestAnimationFrame(() => applySpacer());
    ro.observe(bottom);
    if (tableEl) ro.observe(tableEl);

    return () => {
      if (pendingRaf != null) cancelAnimationFrame(pendingRaf);
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [taskDetailsTab, beeBuildingsFetchPending, beeBuildingsFiltered.length]);

  // Building Type options from Task 2 and Task 4 subtasks
  const buildingTypeOptions = useMemo(() => {
    if (!dropdownData?.subtasks) return [];
    const options = [];

    // Task 2, Subtask 2: ECSBC Commercial
    const task2Subtask2 = dropdownData.subtasks.find(s =>
      (s.task_id === 2 || s.Task_ID === 2) &&
      (s.subtask_no === 2 || s.Subtask_No === 2 || String(s.subtask_no || s.Subtask_No || '').startsWith('2'))
    );
    if (task2Subtask2) {
      options.push({
        value: task2Subtask2.subtask_id || task2Subtask2.Subtask_ID,
        label: 'ECSBC Commercial'
      });
    }

    // Task 2, Subtask 3: ECSBC Residential
    const task2Subtask3 = dropdownData.subtasks.find(s =>
      (s.task_id === 2 || s.Task_ID === 2) &&
      (s.subtask_no === 3 || s.Subtask_No === 3 || String(s.subtask_no || s.Subtask_No || '').startsWith('3'))
    );
    if (task2Subtask3) {
      options.push({
        value: task2Subtask3.subtask_id || task2Subtask3.Subtask_ID,
        label: 'ECSBC Residential'
      });
    }

    // Task 4, Subtask 1: Star Rating- Commercial Projects
    const task4Subtask1 = dropdownData.subtasks.find(s =>
      (s.task_id === 4 || s.Task_ID === 4) &&
      (s.subtask_no === 1 || s.Subtask_No === 1 || String(s.subtask_no || s.Subtask_No || '').startsWith('1'))
    );
    if (task4Subtask1) {
      options.push({
        value: task4Subtask1.subtask_id || task4Subtask1.Subtask_ID,
        label: 'Star Rating- Commercial Projects'
      });
    }

    // Task 4, Subtask 2: Star Rating- Residential Projects
    const task4Subtask2 = dropdownData.subtasks.find(s =>
      (s.task_id === 4 || s.Task_ID === 4) &&
      (s.subtask_no === 2 || s.Subtask_No === 2 || String(s.subtask_no || s.Subtask_No || '').startsWith('2'))
    );
    if (task4Subtask2) {
      options.push({
        value: task4Subtask2.subtask_id || task4Subtask2.Subtask_ID,
        label: 'Star Rating- Residential Projects'
      });
    }

    // Task 4, Subtask 3: Shunya labeling
    const task4Subtask3 = dropdownData.subtasks.find(s =>
      (s.task_id === 4 || s.Task_ID === 4) &&
      (s.subtask_no === 3 || s.Subtask_No === 3 || String(s.subtask_no || s.Subtask_No || '').startsWith('3'))
    );
    if (task4Subtask3) {
      options.push({
        value: task4Subtask3.subtask_id || task4Subtask3.Subtask_ID,
        label: 'Shunya labeling'
      });
    }

    return options;
  }, [dropdownData]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handler for search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  
  // Handler for clearing search
  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };
  
  // Handler for page navigation
  const handlePagePrev = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };
  
  const handlePageNext = (totalPages) => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      cellId: '',
      taskId: '',
      subtaskId: '',
      quarterId: '',
      statusId: '',
      buildingType: '',
      trainingId: '',
      week: ''
    });
    setSearchTerm('');
    setTaskDetailsTab('all');
    setCurrentPage(1);
  };

  // Helper function to sort subtasks properly (handles numeric and alphanumeric like "1", "2a", "2b", "10", "11")
  const sortSubtasks = (subtasks) => {
    return [...subtasks].sort((a, b) => {
      // First sort by task_id
      const aTaskId = a.task_id || a.Task_ID || 0;
      const bTaskId = b.task_id || b.Task_ID || 0;
      if (aTaskId !== bTaskId) {
        return aTaskId - bTaskId;
      }
      
      // Then sort by subtask_no (handling alphanumeric)
      const aNo = String(a.subtask_no || a.Subtask_No || '');
      const bNo = String(b.subtask_no || b.Subtask_No || '');
      
      // Extract numeric part and letter part (if any)
      const aMatch = aNo.match(/^(\d+)([a-z]?)$/i);
      const bMatch = bNo.match(/^(\d+)([a-z]?)$/i);
      
      if (!aMatch || !bMatch) {
        // Fallback to string comparison if format doesn't match
        return aNo.localeCompare(bNo);
      }
      
      const aNum = parseInt(aMatch[1], 10);
      const bNum = parseInt(bMatch[1], 10);
      const aLetter = aMatch[2] || '';
      const bLetter = bMatch[2] || '';
      
      // First compare by numeric part
      if (aNum !== bNum) {
        return aNum - bNum;
      }
      
      // If numeric parts are equal, compare by letter part
      return aLetter.localeCompare(bLetter);
    });
  };

  const subtaskNoMatchesBeeBuildingsScope = (taskNo, subNoRaw) => {
    const no = String(subNoRaw ?? '').trim();
    if (!no) return false;
    if (taskNo === 2) return /^[23]([^0-9]|$)/.test(no);
    if (taskNo === 4) return /^[12]([^0-9]|$)/.test(no);
    return false;
  };

  const tasksForBeeDashboardTaskFilter = useMemo(() => {
    const list = dropdownData?.tasks || [];
    if (taskDetailsTab !== 'buildings') return list;
    return list.filter((t) => {
      const n = Number(t.task_no ?? t.Task_No);
      return n === 2 || n === 4;
    });
  }, [dropdownData, taskDetailsTab]);

  // Get filtered subtasks based on selected task (Buildings tab: only Task 2 / 4 building subtasks)
  const filteredSubtasks = useMemo(() => {
    if (!dropdownData) return [];
    if (taskDetailsTab === 'buildings') {
      const all = dropdownData.subtasks || [];
      let subtasks = all.filter((s) => {
        const tid = Number(s.task_id ?? s.Task_ID);
        if (tid !== 2 && tid !== 4) return false;
        const sno = s.subtask_no ?? s.Subtask_No;
        return subtaskNoMatchesBeeBuildingsScope(tid, sno);
      });
      if (filters.taskId) {
        const taskIdNum = parseInt(filters.taskId, 10);
        subtasks = subtasks.filter(
          (s) => Number(s.task_id ?? s.Task_ID) === taskIdNum
        );
      }
      return sortSubtasks(subtasks);
    }
    let subtasks = filters.taskId
      ? dropdownData?.subtasks?.filter(subtask => 
          (subtask.task_id === parseInt(filters.taskId)) || 
          (subtask.Task_ID === parseInt(filters.taskId))
        ) || []
      : dropdownData?.subtasks || [];
    
    return sortSubtasks(subtasks);
  }, [dropdownData, filters.taskId, taskDetailsTab]);

  useEffect(() => {
    if (taskDetailsTab !== 'buildings' || !dropdownData?.tasks?.length) return;
    if (filters.taskId) {
      const task = dropdownData.tasks.find((t) => t.task_id === parseInt(filters.taskId, 10));
      if (task) {
        const tn = Number(task.task_no ?? task.Task_No);
        if (tn !== 2 && tn !== 4) {
          setFilters((prev) => ({ ...prev, taskId: '', subtaskId: '' }));
        }
      }
    }
    if (filters.subtaskId && dropdownData.subtasks?.length) {
      const sid = parseInt(filters.subtaskId, 10);
      const meta = dropdownData.subtasks.find((s) => (s.subtask_id || s.Subtask_ID) === sid);
      if (!meta) {
        setFilters((prev) => ({ ...prev, subtaskId: '' }));
        return;
      }
      const tid = Number(meta.task_id ?? meta.Task_ID);
      const sno = String(meta.subtask_no ?? meta.Subtask_No ?? '').trim();
      const ok =
        (tid === 2 && /^[23]([^0-9]|$)/.test(sno)) || (tid === 4 && /^[12]([^0-9]|$)/.test(sno));
      if (!ok) setFilters((prev) => ({ ...prev, subtaskId: '' }));
    }
  }, [taskDetailsTab, filters.taskId, filters.subtaskId, dropdownData]);

  // Get task info for display
  const getTaskInfo = (subtask) => {
    const taskId = subtask.task_id || subtask.Task_ID;
    const task = dropdownData?.tasks?.find(t => t.task_id === taskId);
    return task ? `Task ${task.task_no}` : '';
  };

  // Helper function to strip HTML tags and convert to plain text
  const stripHtmlTags = (html) => {
    if (!html) return '';
    // Create a temporary div element
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Get text content and replace multiple spaces/newlines with single space
    let text = tmp.textContent || tmp.innerText || '';
    // Clean up whitespace
    text = text.replace(/\s+/g, ' ').trim();
    return text;
  };

  // Helper function to calculate summary for Tasks 2, 3, and 4
  const calculateSummary = (task, allTasks) => {
    const taskNo = task.task_no || task.Task_No;
    const subtaskNo = task.subtask_no || task.Subtask_No;
    const cellKey = `${task.state_name || ''}_${task.cell_no || ''}`;
    
    // Only calculate summary for Tasks 2, 3, and 4
    if (taskNo !== 2 && taskNo !== 3 && taskNo !== 4) {
      return null;
    }
    
    // Filter tasks for the same cell
    const cellTasks = allTasks.filter(t => 
      `${t.state_name || ''}_${t.cell_no || ''}` === cellKey
    );
    
    if (taskNo === 2) {
      // Task 2: Count commercial projects for subtasks 2 and 3
      const subtaskNoStr = String(subtaskNo || '').toLowerCase();
      
      if (subtaskNoStr.startsWith('2')) {
        // Subtask 2: Total No. of ECSBC Commercial Projects
        // Count unique buildings in Subtask 2
        const uniqueBuildings = new Set();
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          // Must be Task 2, Subtask 2
          if (tTaskNo === 2 && tSubtaskNo.startsWith('2')) {
            const buildingId = t.building_id || t.Building_ID;
            if (buildingId) {
              uniqueBuildings.add(buildingId);
            } else if (t.building_name) {
              // Use building name as fallback identifier
              uniqueBuildings.add(t.building_name);
            }
          }
        });
        const count = uniqueBuildings.size;
        return count > 0 ? `Total ECSBC Commercial Building(s): ${count}` : '-';
      } else if (subtaskNoStr.startsWith('3')) {
        // Subtask 3: Total No. of ECSBC Residential Projects
        // Count unique buildings in Subtask 3
        const uniqueBuildings = new Set();
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          if (tTaskNo === 2 && tSubtaskNo.startsWith('3')) {
            const buildingId = t.building_id || t.Building_ID;
            if (buildingId) {
              uniqueBuildings.add(buildingId);
            } else if (t.building_name) {
              // Use building name as fallback identifier
              uniqueBuildings.add(t.building_name);
            }
          }
        });
        const count = uniqueBuildings.size;
        return count > 0 ? `Total ECSBC Residential Building(s): ${count}` : '-';
      }
    } else if (taskNo === 3) {
      // Task 3: Count unique trainings for subtasks 2, 3, 4, and 5 (Training-A, Training-B, Training-C)
      const subtaskNoStr = String(subtaskNo || '').toLowerCase();
      
      if (subtaskNoStr.startsWith('2') || subtaskNoStr.startsWith('3') || subtaskNoStr.startsWith('4') || subtaskNoStr.startsWith('5')) {
        // Count ALL training entries in this subtask (not just unique training IDs)
        const matchingTasks = [];
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          // Must be Task 3, same subtask
          if (tTaskNo === 3 && tSubtaskNo.startsWith(subtaskNoStr.charAt(0))) {
            const trainingId = t.training_id || t.Training_ID;
            // Only count tasks that have a training_id (actual training entries)
            if (trainingId) {
              matchingTasks.push({
                TaskDetails_ID: t.id || t.ID,
                Training_ID: trainingId,
                Subtask_No: tSubtaskNo,
                Quarter_ID: t.quarter_id || t.Quarter_ID
              });
            }
          }
        });
        // Count all matching training entries, not just unique training IDs
        const count = matchingTasks.length;
        
        // Get the actual training type from the task data
        let trainingTypeName = '';
        const taskWithTraining = cellTasks.find(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          return tTaskNo === 3 && tSubtaskNo.startsWith(subtaskNoStr.charAt(0)) && (t.training_type || t.Training_Type);
        });
        
        if (taskWithTraining && (taskWithTraining.training_type || taskWithTraining.Training_Type)) {
          trainingTypeName = taskWithTraining.training_type || taskWithTraining.Training_Type;
        } else {
          // Fallback to training type names if training type is not available
          if (subtaskNoStr.startsWith('2')) {
            trainingTypeName = 'Awareness Training';
          } else if (subtaskNoStr.startsWith('3')) {
            trainingTypeName = 'Training-A';
          } else if (subtaskNoStr.startsWith('4')) {
            trainingTypeName = 'Training-B';
          } else if (subtaskNoStr.startsWith('5')) {
            trainingTypeName = 'Training-C';
          }
        }
        
        return count > 0 ? `Total ${trainingTypeName}: ${count}` : '-';
      }
    } else if (taskNo === 4) {
      // Task 4: Count star ratings and Shunya labelling
      const subtaskNoStr = String(subtaskNo || '').toLowerCase();
      
      if (subtaskNoStr === '1' || subtaskNoStr.startsWith('1')) {
        // Subtask 1: Total star rating commercial Buildings
        // Count unique buildings in Subtask 1
        const uniqueBuildings = new Set();
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          // Must be Task 4, Subtask 1
          if (tTaskNo === 4 && (tSubtaskNo === '1' || tSubtaskNo.startsWith('1'))) {
            const buildingId = t.building_id || t.Building_ID;
            if (buildingId) {
              uniqueBuildings.add(buildingId);
            } else if (t.building_name) {
              // Use building name as fallback identifier
              uniqueBuildings.add(t.building_name);
            }
          }
        });
        const count = uniqueBuildings.size;
        return count > 0 ? `Total Star Rating Commercial Buildings: ${count}` : '-';
      } else if (subtaskNoStr === '2' || subtaskNoStr.startsWith('2')) {
        // Subtask 2: Total star rating Residential Buildings
        // Count unique buildings in Subtask 2
        const uniqueBuildings = new Set();
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          if (tTaskNo === 4 && (tSubtaskNo === '2' || tSubtaskNo.startsWith('2'))) {
            const buildingId = t.building_id || t.Building_ID;
            if (buildingId) {
              uniqueBuildings.add(buildingId);
            } else if (t.building_name) {
              // Use building name as fallback identifier
              uniqueBuildings.add(t.building_name);
            }
          }
        });
        const count = uniqueBuildings.size;
        return count > 0 ? `Total Star Rating Residential Buildings: ${count}` : '-';
      } else if (subtaskNoStr === '3' || subtaskNoStr.startsWith('3')) {
        // Subtask 3: Total Shunya labelling - count unique buildings
        const uniqueBuildings = new Set();
        cellTasks.forEach(t => {
          const tTaskNo = t.task_no || t.Task_No;
          const tSubtaskNo = String(t.subtask_no || t.Subtask_No || '').toLowerCase();
          if (tTaskNo === 4 && (tSubtaskNo === '3' || tSubtaskNo.startsWith('3'))) {
            const buildingId = t.building_id || t.Building_ID;
            if (buildingId) {
              uniqueBuildings.add(buildingId);
            } else if (t.building_name) {
              // Use building name as fallback identifier
              uniqueBuildings.add(t.building_name);
            }
          }
        });
        const count = uniqueBuildings.size;
        return count > 0 ? `Total Shunya Labelling (Subtask 3): ${count}` : '-';
      }
    }
    
    return null;
  };

  // Helper function to strip HTML tags while preserving line breaks
  const stripHtmlTagsPreserveLines = (html) => {
    if (!html) return '';
    // Create a temporary div element
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Get text content (this preserves line breaks from block elements)
    let text = tmp.textContent || tmp.innerText || '';
    // Clean up: replace multiple spaces with single space, but preserve newlines
    text = text.replace(/[ \t]+/g, ' '); // Replace multiple spaces/tabs with single space
    text = text.replace(/\n\s*\n/g, '\n'); // Replace multiple newlines with single newline
    return text.trim();
  };

  // Safe React component to render remarks with highlighted stakeholder name
  const RemarksWithStakeholder = ({ remarks }) => {
    if (!remarks) return null;
    
    // Strip HTML tags from remarks
    const cleanRemarks = stripHtmlTagsPreserveLines(remarks);
    
    // Check if remarks contain stakeholder name prefix
    if (cleanRemarks.includes('STAKEHOLDER_NAME:')) {
      // Split by newlines to handle the format
      const lines = cleanRemarks.split('\n');
      const firstLine = lines[0];
      
      if (firstLine.startsWith('STAKEHOLDER_NAME:')) {
        const stakeholderName = firstLine.replace('STAKEHOLDER_NAME:', '').trim();
        const remainingRemarks = lines.slice(1).join('\n').replace(/^\n+/, '');
        
        return (
          <>
            <div className="stakeholder-badge-container">
              <span className="stakeholder-badge">
                <span className="stakeholder-label">Stakeholder:</span>
                <span className="stakeholder-name">{stakeholderName}</span>
          </span>
            </div>
            {remainingRemarks && (
              <div className="whitespace-pre-wrap">{remainingRemarks}</div>
            )}
          </>
        );
      }
    }
    
    // If no stakeholder name, return remarks as is (without HTML tags)
    return <div className="whitespace-pre-wrap">{cleanRemarks}</div>;
  };

  // Export to Excel in hierarchical format with proper formatting, colors, and indentation
  // Helper function to add data to a worksheet for BEE Admin Dashboard
  const addDataToWorksheetBEEAdmin = (
    worksheet,
    hierarchicalTasks,
    tasksForSummary,
    hasTrainingType,
    hasBuildingData,
    hasTask234,
    hasTask1,
    hideNotificationExportCols = false
  ) => {
    // Track merged cell ranges
    const cellRanges = [];
    const taskRanges = [];
    let currentCellStartRow = 2;
    let currentTaskStartRow = 2;
    let previousCellKey = null;
    let previousTaskKey = null;
    
    // Track merged cell ranges for subtasks (Task 2, 3, 4)
    const subtaskRanges = [];
    let currentSubtaskStartRow = 2;
    let previousSubtaskKey = null;
    
    // Track merged cell ranges for quarters (Task 2, 3, 4)
    const quarterRanges = [];
    let currentQuarterStartRow = 2;
    let previousQuarterKey = null;
    
    // Track merged cell ranges for summary (Task 2, 3, 4)
    const summaryRanges = [];
    let currentSummaryStartRow = 2;
    let previousSummaryKey = null;

    // Add data rows
    hierarchicalTasks.forEach((task, index) => {
      const rowIndex = index + 2; // +2 because row 1 is header
      const cellKey = `${task.state_name || ''}_${task.cell_no || ''}`;
      const taskKey = `${cellKey}_${task.task_no || task.Task_No || ''}`;
      const taskNo = task.task_no || task.Task_No;
      const subtaskKey = `${taskKey}_${task.subtask_no || task.Subtask_No || ''}`;
      const quarterKey = `${subtaskKey}_${task.quarter_id || task.Quarter_ID || task.quarter_name || ''}`;
      const shouldMergeSubtaskSummary = taskNo === 2 || taskNo === 3 || taskNo === 4;

      // Check if we need to merge previous cell
      if (previousCellKey && previousCellKey !== cellKey) {
        if (currentCellStartRow < rowIndex) {
          cellRanges.push({ start: currentCellStartRow, end: rowIndex - 1 });
        }
        currentCellStartRow = rowIndex;
      } else if (previousCellKey === null) {
        currentCellStartRow = rowIndex;
      }

      // Check if we need to merge previous task
      if (previousTaskKey && previousTaskKey !== taskKey) {
        if (currentTaskStartRow < rowIndex) {
          taskRanges.push({ start: currentTaskStartRow, end: rowIndex - 1 });
        }
        currentTaskStartRow = rowIndex;
      } else if (previousTaskKey === null || previousTaskKey !== taskKey) {
        currentTaskStartRow = rowIndex;
      }
      
      // Track subtask ranges for Task 2, 3, 4
      if (shouldMergeSubtaskSummary) {
        if (previousSubtaskKey && previousSubtaskKey !== subtaskKey) {
          if (currentSubtaskStartRow < rowIndex) {
            subtaskRanges.push({ start: currentSubtaskStartRow, end: rowIndex - 1 });
            summaryRanges.push({ start: currentSummaryStartRow, end: rowIndex - 1 });
          }
          // Also merge the last quarter group when subtask changes
          if (currentQuarterStartRow < rowIndex) {
            quarterRanges.push({ start: currentQuarterStartRow, end: rowIndex - 1 });
          }
          currentSubtaskStartRow = rowIndex;
          currentSummaryStartRow = rowIndex;
          currentQuarterStartRow = rowIndex;
        } else if (previousSubtaskKey === null || previousSubtaskKey !== subtaskKey) {
          currentSubtaskStartRow = rowIndex;
          currentSummaryStartRow = rowIndex;
          currentQuarterStartRow = rowIndex;
        }
        
        // Track quarter ranges within the same subtask
        if (previousQuarterKey && previousQuarterKey !== quarterKey && previousSubtaskKey === subtaskKey) {
          if (currentQuarterStartRow < rowIndex) {
            quarterRanges.push({ start: currentQuarterStartRow, end: rowIndex - 1 });
          }
          currentQuarterStartRow = rowIndex;
        } else if (previousQuarterKey === null || (previousQuarterKey !== quarterKey && previousSubtaskKey === subtaskKey)) {
          currentQuarterStartRow = rowIndex;
        }
      }

      // Prepare row data - combine numbers with descriptions
      let cellName = task.isFirstCellRow ? (task.state_name || '') : '';
        // Add Cell No to cell name
        if (task.isFirstCellRow && task.cell_no) {
          cellName = cellName ? `${cellName} (Cell ${task.cell_no})` : `Cell ${task.cell_no}`;
        }
      
      // Combine Task No with Task Description
      const taskDescription = task.isFirstTaskRow 
        ? ((task.task_no || task.Task_No) ? `Task ${task.task_no || task.Task_No}: ${task.task_description || ''}` : (task.task_description || ''))
        : '';
      
      // Combine Subtask No with Subtask Description
      // For Task 2, 3, 4: only show subtask description on first row of subtask group
      const subtaskDescription = (shouldMergeSubtaskSummary && !task.isFirstSubtaskRow) 
        ? '' 
        : ((task.subtask_no || task.Subtask_No) 
          ? `Subtask ${task.subtask_no || task.Subtask_No}: ${task.subtask_description || ''}`
          : (task.subtask_description || ''));

      // For Task 2, 3, 4: only show quarter on first row of quarter group within same subtask
      const quarterName = (shouldMergeSubtaskSummary && previousQuarterKey && previousQuarterKey === quarterKey && previousSubtaskKey === subtaskKey)
        ? ''
        : (task.quarter_name || '');

      const rowData = [
        index + 1,
        cellName,
        ...(hideNotificationExportCols ? [] : [taskDescription, subtaskDescription]),
        quarterName,
        task.status || '',
        ...(hasTask1 ? [
          (taskNo === 1 && task.is_notified !== null && task.is_notified !== undefined) ? (task.is_notified ? 'Yes' : 'No') : '',
          (taskNo === 1 && task.notification_date) ? new Date(task.notification_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '',
          (taskNo === 1 && task.incorporation_in_by_laws !== null && task.incorporation_in_by_laws !== undefined) ? (task.incorporation_in_by_laws ? 'Yes' : 'No') : ''
        ] : []),
        ...(hasTrainingType ? [
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '') ? (task.training_type || '') : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '') ? (task.training_mode || '') : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && task.training_start_date) ? (
            task.training_end_date && task.training_end_date !== task.training_start_date
              ? `${new Date(task.training_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${new Date(task.training_end_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
              : new Date(task.training_start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
          ) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_district_name || task.training_district)) ? (task.training_district_name || task.training_district || '') : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_total_attendees !== null && task.training_total_attendees !== undefined)) ? (task.training_total_attendees || 0) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_government_sector_attendees !== null && task.training_government_sector_attendees !== undefined)) ? (task.training_government_sector_attendees || 0) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_total_women !== null && task.training_total_women !== undefined)) ? (task.training_total_women || 0) : '',
          (shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_architects_count !== null && task.training_architects_count !== undefined)) ? (task.training_architects_count || 0) : ''
        ] : []),
        ...(hasBuildingData ? [
          (shouldShowBuildingColumns(task) && task.building_name) ? (task.building_name || '') : '',
          (shouldShowBuildingColumns(task) && task.building_address) ? (task.building_address || '') : '',
          (shouldShowBuildingColumns(task) && task.building_typology) ? (task.building_typology || '') : '',
          (shouldShowBuildingColumns(task) && task.building_status) ? (task.building_status || '') : '',
          (shouldShowBuildingColumns(task) && task.building_nodal_contact_name) ? (task.building_nodal_contact_name || '') : '',
          (shouldShowBuildingColumns(task) && task.building_nodal_email) ? (task.building_nodal_email || '') : '',
          (shouldShowBuildingColumns(task) && task.building_nodal_department) ? (task.building_nodal_department || '') : '',
          (shouldShowBuildingColumns(task) && task.building_epi_value_proposed !== null && task.building_epi_value_proposed !== undefined) ? parseFloat(task.building_epi_value_proposed).toFixed(4) : '',
          (shouldShowBuildingColumns(task) && task.building_epi_ratio !== null && task.building_epi_ratio !== undefined) ? parseFloat(task.building_epi_ratio).toFixed(4) : ''
        ] : []),
        ...(hideNotificationExportCols
          ? []
          : [
              (task.task_date || task.Task_Date)
                ? new Date(task.task_date || task.Task_Date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })
                : ''
            ]),
        ...(hasTask234 ? [(shouldMergeSubtaskSummary && task.isFirstSubtaskRow) ? (calculateSummary(task, tasksForSummary) || '-') : (shouldMergeSubtaskSummary ? '' : (calculateSummary(task, tasksForSummary) || '-'))] : []),
        (() => {
          // Format remarks for Excel with highlighted stakeholder name
          let remarksText = stripHtmlTags(task.remarks || '');
          if (remarksText.includes('STAKEHOLDER_NAME:')) {
            const lines = remarksText.split('\n');
            const firstLine = lines[0];
            if (firstLine.startsWith('STAKEHOLDER_NAME:')) {
              const stakeholderName = firstLine.replace('STAKEHOLDER_NAME:', '').trim();
              const remainingRemarks = lines.slice(1).join('\n').replace(/^\n+/, '');
              // Format with stakeholder name highlighted (using bold formatting in Excel)
              remarksText = `STAKEHOLDER: ${stakeholderName}${remainingRemarks ? '\n\n' + remainingRemarks : ''}`;
            }
          }
          return remarksText;
        })(),
        ...(hideNotificationExportCols
          ? []
          : [
              task.document_path || task.Document_Path || '',
              task.submission_date
                ? new Date(task.submission_date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })
                : ''
            ])
      ];

      const row = worksheet.addRow(rowData);
      // Calculate dynamic row height based on content length and column widths
      let maxLines = 1;
      rowData.forEach((cellValue, colIndex) => {
        const cellText = String(cellValue || '');
        if (cellText.length > 0) {
          // Get column width (default estimates based on column index)
          const col = worksheet.getColumn(colIndex + 1);
          const colWidth = col.width || (colIndex === 0 ? 8 : colIndex === 1 ? 30 : colIndex === 2 ? 50 : colIndex === 3 ? 50 : colIndex === 4 ? 15 : colIndex === 5 ? 15 : 20);
          // Estimate characters per line: Excel column width is roughly in character units
          const charsPerLine = Math.floor(colWidth * 0.85); // Use 85% of width for text
          const estimatedLines = Math.ceil(cellText.length / Math.max(charsPerLine, 15));
          maxLines = Math.max(maxLines, estimatedLines);
        }
      });
      // Excel row height: ~15-18 points per line, minimum 25, add extra for padding
      row.height = Math.max(25, maxLines * 20);
      row.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };

      // Apply colors based on row type
      if (task.isFirstCellRow) {
        // Cell header rows - light blue background
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE3F2FD' } // Light blue
        };
        row.font = { bold: true, size: 11 };
          const cellNameCell = row.getCell(2);
          cellNameCell.font = { bold: true, size: 11, color: { argb: 'FF1565C0' } }; // Dark blue text for cell name
          cellNameCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
      } else if (task.isFirstTaskRow) {
        // Task rows - light green background
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE8F5E9' } // Light green
        };
        if (!hideNotificationExportCols) {
          row.getCell(3).font = { bold: true, size: 11 }; // Bold task description (with task no)
        }
      } else {
        // Subtask rows - white/light gray background
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFFFF' } // White
        };
        if (!hideNotificationExportCols) {
          row.getCell(4).alignment = { vertical: 'top', horizontal: 'left', wrapText: true, indent: 1 };
        }
      }

      // Color code status column
      const statusColNum = hideNotificationExportCols ? 4 : 6;
      const statusCell = row.getCell(statusColNum);
      if (task.status === 'Completed' || task.status === 'completed') {
        statusCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF4CAF50' } // Green
        };
        statusCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      } else if (task.status === 'In Progress' || task.status === 'In-Progress') {
        statusCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF2196F3' } // Blue
        };
        statusCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      } else if (task.status === 'On Hold') {
        statusCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF9800' } // Orange
        };
        statusCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      }

      // Color code training mode column if training type exists and should show
      if (hasTrainingType && shouldShowTrainingColumns(task) && task.training_type) {
        const baseBeforeTraining = hideNotificationExportCols ? 4 : 6;
        const trainingModeColIndex = baseBeforeTraining + (hasTask1 ? 3 : 0) + 2; // Training Mode after Training Type
        const trainingModeCell = row.getCell(trainingModeColIndex);
        if (task.training_mode) {
          if (task.training_mode.toLowerCase() === 'online') {
            trainingModeCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FF4CAF50' } // Green for online
            };
            trainingModeCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          } else {
            trainingModeCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FF757575' } // Gray for offline
            };
            trainingModeCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          }
        }
      }

      // Calculate remarks column index for BEE Admin Dashboard (1-based Excel columns)
      let remarksColIndex = 1; // S. No.
      remarksColIndex += 1; // Cell Name (State)
      if (!hideNotificationExportCols) {
        remarksColIndex += 1; // Task Description
        remarksColIndex += 1; // Subtask Description
      }
      remarksColIndex += 1; // Quarter
      remarksColIndex += 1; // Status
      if (hasTrainingType) remarksColIndex += 8; // Training columns - columns 6-13 (including District)
      if (hasBuildingData) remarksColIndex += 6; // Building columns
      if (!hideNotificationExportCols) remarksColIndex += 1; // Task Completion Date
      if (hasTask234) remarksColIndex += 1; // Summary column
      // Remarks is the next column

      // Format remarks cell to highlight stakeholder name
      const remarksCell = row.getCell(remarksColIndex);
      const remarksValue = remarksCell.value;
      if (remarksValue && typeof remarksValue === 'string' && remarksValue.includes('STAKEHOLDER:')) {
        // Use rich text to highlight stakeholder name
        const lines = remarksValue.split('\n');
        const firstLine = lines[0];
        if (firstLine.startsWith('STAKEHOLDER:')) {
          const stakeholderName = firstLine.replace('STAKEHOLDER:', '').trim();
          const remainingText = lines.slice(1).join('\n').replace(/^\n+/, '');
          
          // Create rich text with highlighted stakeholder name
          remarksCell.value = {
            richText: [
              {
                text: 'STAKEHOLDER: ',
                font: { bold: true, color: { argb: 'FF92400E' } }
              },
              {
                text: stakeholderName,
                font: { bold: true, color: { argb: 'FF78350F' }, size: 12 }
              },
              ...(remainingText ? [
                { text: '\n\n' },
                { text: remainingText, font: { color: { argb: 'FF000000' } } }
              ] : [])
            ]
          };
          
          // Also apply yellow background to the cell
          remarksCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFEF3C7' } // Light yellow
          };
        }
      }

      // Add borders and enable text wrapping for all cells
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          right: { style: 'thin', color: { argb: 'FFCCCCCC' } }
        };
        // Enable text wrapping for all cells
        if (!cell.alignment) {
          cell.alignment = {};
        }
        cell.alignment.wrapText = true;
        cell.alignment.vertical = 'top';
        cell.alignment.horizontal = 'left';
      });

      previousCellKey = cellKey;
      previousTaskKey = taskKey;
      if (shouldMergeSubtaskSummary) {
        previousSubtaskKey = subtaskKey;
        previousSummaryKey = subtaskKey;
        previousQuarterKey = quarterKey;
      }
    });

    // Merge the last cell and task groups
    const lastRow = hierarchicalTasks.length + 1;
    if (currentCellStartRow < lastRow) {
      cellRanges.push({ start: currentCellStartRow, end: lastRow });
    }
    if (currentTaskStartRow < lastRow) {
      taskRanges.push({ start: currentTaskStartRow, end: lastRow });
    }
    
    // Merge the last subtask group (Task 2, 3, 4)
    const lastTask = hierarchicalTasks[hierarchicalTasks.length - 1];
    const lastTaskNo = lastTask?.task_no || lastTask?.Task_No;
    if ((lastTaskNo === 2 || lastTaskNo === 3 || lastTaskNo === 4) && currentSubtaskStartRow < lastRow) {
      subtaskRanges.push({ start: currentSubtaskStartRow, end: lastRow });
      summaryRanges.push({ start: currentSummaryStartRow, end: lastRow });
    }
    
    // Merge the last quarter group (Task 2, 3, 4)
    if ((lastTaskNo === 2 || lastTaskNo === 3 || lastTaskNo === 4) && currentQuarterStartRow < lastRow) {
      quarterRanges.push({ start: currentQuarterStartRow, end: lastRow });
    }

    // Apply all cell merges (Cell Name column ? column B after S. No.)
      cellRanges.forEach(range => {
        if (range.end > range.start) {
          worksheet.mergeCells(`B${range.start}:B${range.end}`);
        }
      });

    // Apply all task merges (Task Description column � column C when Task column is present)
    if (!hideNotificationExportCols) {
      taskRanges.forEach(range => {
        if (range.end > range.start) {
          worksheet.mergeCells(`C${range.start}:C${range.end}`);
        }
      });
    }

    // Calculate Summary column index for BEE Admin Dashboard (0-based A=0)
    let summaryColIndex = 2; // S. No. + Cell Name
    if (!hideNotificationExportCols) {
      summaryColIndex += 1; // Task Description
      summaryColIndex += 1; // Subtask Description
    }
    summaryColIndex += 1; // Quarter
    summaryColIndex += 1; // Status
    if (hasTrainingType) summaryColIndex += 8; // Training columns
    if (hasBuildingData) summaryColIndex += 6; // Building columns
    if (!hideNotificationExportCols) summaryColIndex += 1; // Task Date
    // Summary column is at summaryColIndex
    
    // Apply subtask merges for Task 2, 3, 4 (Subtask column � column D when present)
    if (!hideNotificationExportCols) {
      subtaskRanges.forEach(range => {
        if (range.end > range.start) {
          worksheet.mergeCells(`D${range.start}:D${range.end}`);
          const cell = worksheet.getCell(`D${range.start}`);
          cell.alignment = { ...cell.alignment, vertical: 'top', horizontal: 'left', wrapText: true };
        }
      });
    }

    // Apply quarter merges for Task 2, 3, 4 (Quarter column � E normally, C when Task/Subtask columns omitted)
    const quarterMergeCol = hideNotificationExportCols ? 'C' : 'E';
    quarterRanges.forEach(range => {
      if (range.end > range.start) {
        worksheet.mergeCells(`${quarterMergeCol}${range.start}:${quarterMergeCol}${range.end}`);
        const cell = worksheet.getCell(`${quarterMergeCol}${range.start}`);
        cell.alignment = { ...cell.alignment, vertical: 'top', horizontal: 'left', wrapText: true };
      }
    });
    
    // Apply summary merges for Task 2, 3, 4
    const summaryColLetter = String.fromCharCode(65 + summaryColIndex); // Convert to letter (A=65)
    summaryRanges.forEach(range => {
      if (range.end > range.start) {
        worksheet.mergeCells(`${summaryColLetter}${range.start}:${summaryColLetter}${range.end}`);
        // Set alignment for merged summary cells
        const cell = worksheet.getCell(`${summaryColLetter}${range.start}`);
        cell.alignment = { ...cell.alignment, vertical: 'top', horizontal: 'left', wrapText: true };
      }
    });
  };

  const exportToCSV = async () => {
    if (!tasksForTaskDetailsView.length) return;

    // Create a new workbook
    const workbook = new ExcelJS.Workbook();

    // Check if cell filter is active - if yes, create separate sheets per task
    const isCellFilterActive = filters.cellId && filters.cellId !== '';

    // Group tasks by task number if no filters are active OR if cell filter is active
    if (!hasActiveFilters || isCellFilterActive) {
      // Group tasks by task number
      const tasksByTaskNo = {};
      tasksForTaskDetailsView.forEach(task => {
        const taskNo = task.task_no || task.Task_No;
        if (!tasksByTaskNo[taskNo]) {
          tasksByTaskNo[taskNo] = [];
        }
        tasksByTaskNo[taskNo].push(task);
      });

      // Sort task numbers
      const sortedTaskNos = Object.keys(tasksByTaskNo).sort((a, b) => Number(a) - Number(b));

      // Create a sheet for each task
      sortedTaskNos.forEach(taskNo => {
        const taskData = tasksByTaskNo[taskNo];
        const hierarchicalTasksForSheet = groupTasksHierarchically(taskData, null);

        // Check which columns to show for this task
        const hasTrainingType = taskData.some(task => {
          const tTaskNo = task.task_no || task.Task_No;
          const tTaskNum = Number(tTaskNo);
          if (tTaskNum !== 3) return false;
          const subtaskNo = task.subtask_no || task.Subtask_No;
          const subtaskNoStr = String(subtaskNo || '').toLowerCase();
          if (!subtaskNoStr.startsWith('2') && !subtaskNoStr.startsWith('3') && !subtaskNoStr.startsWith('4')) return false;
          const trainingType = task.training_type || task.Training_Type;
          return trainingType && String(trainingType).trim() !== '';
        });

        const hasBuildingData = taskData.some(task => {
          const tTaskNo = task.task_no || task.Task_No;
          const tTaskNum = Number(tTaskNo);
          return tTaskNum === 2 || tTaskNum === 4;
        });

        const hasTask234 = taskData.some(task => {
          const tTaskNo = task.task_no || task.Task_No;
          return tTaskNo === 2 || tTaskNo === 3 || tTaskNo === 4;
        });

        const hasTask1 = taskData.some(task => {
          const tTaskNo = task.task_no || task.Task_No;
          return tTaskNo === 1;
        });

        // Create worksheet for this task
        const worksheet = workbook.addWorksheet(`Task ${taskNo}`);

        // Define headers
        const headers = [
          'S. No.',
          'Cell Name (State)',
          ...(taskDetailsTab === 'notification'
            ? []
            : ['Task Description', 'Subtask Description']),
          'Quarter',
          taskDetailsTab === 'notification'
            ? 'status of Notification of ECSBC- Buildings'
            : 'Status',
          ...(hasTask1 ? ['Is Notified?', 'Date of Notification', 'Incorporation in Building By-Laws'] : []),
          ...(hasTrainingType ? ['Training Type', 'Training Mode', 'Date of Training', 'District', 'Total Participants', 'Govt. Officials', 'Female Participants', 'No. of Architects'] : []),
          ...(hasBuildingData ? ['Building Name', 'Building Address', 'Typology', 'Building Status', 'Nodal Contact Name', 'Nodal Contact Email', 'Department', 'EPI Value Proposed', 'EPI Ratio'] : []),
          ...(taskDetailsTab === 'notification' ? [] : ['Task Completion Date']),
          ...(hasTask234 ? ['Summary'] : []),
          'Remarks',
          ...(taskDetailsTab === 'notification' ? [] : ['Attachments', 'Submission Date'])
        ];

        // Add header row with styling
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
        headerRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1E40AF' } // Dark blue
        };
        headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        headerRow.height = 25;
        
        // Set column widths
        worksheet.getColumn(1).width = 8; // S. No.
        worksheet.getColumn(2).width = 30; // Cell Name (State) with Cell No
        let colIndex;
        if (taskDetailsTab === 'notification') {
          worksheet.getColumn(3).width = 15; // Quarter
          worksheet.getColumn(4).width = 36; // Notification / status
          colIndex = 5;
        } else {
          worksheet.getColumn(3).width = 50; // Task Description with Task No
          worksheet.getColumn(4).width = 50; // Subtask Description with Subtask No
          worksheet.getColumn(5).width = 15; // Quarter
          worksheet.getColumn(6).width = 15; // Status
          colIndex = 7;
        }
        if (hasTask1) {
          worksheet.getColumn(colIndex++).width = 15; // Is Notified?
          worksheet.getColumn(colIndex++).width = 20; // Date of Notification
          worksheet.getColumn(colIndex++).width = 30; // Incorporation in Building By-Laws
        }
        if (hasTrainingType) {
          worksheet.getColumn(colIndex++).width = 20; // Training Type
          worksheet.getColumn(colIndex++).width = 15; // Training Mode
          worksheet.getColumn(colIndex++).width = 20; // Date of Training
          worksheet.getColumn(colIndex++).width = 18; // District
          worksheet.getColumn(colIndex++).width = 18; // Total Participants
          worksheet.getColumn(colIndex++).width = 18; // Govt. Officials
          worksheet.getColumn(colIndex++).width = 20; // Female Participants
          worksheet.getColumn(colIndex++).width = 18; // No. of Architects
        }
        if (hasBuildingData) {
          worksheet.getColumn(colIndex++).width = 25; // Building Name
          worksheet.getColumn(colIndex++).width = 30; // Building Address
          worksheet.getColumn(colIndex++).width = 15; // Typology
          worksheet.getColumn(colIndex++).width = 18; // Building Status
          worksheet.getColumn(colIndex++).width = 20; // Nodal Contact Name
          worksheet.getColumn(colIndex++).width = 25; // Nodal Contact Email
          worksheet.getColumn(colIndex++).width = 20; // Department
          worksheet.getColumn(colIndex++).width = 18; // EPI Value Proposed
          worksheet.getColumn(colIndex++).width = 15; // EPI Ratio
        }
        if (taskDetailsTab !== 'notification') {
          worksheet.getColumn(colIndex++).width = 18; // Task Completion Date
        }
        if (hasTask234) {
          worksheet.getColumn(colIndex++).width = 40; // Summary
        }
        worksheet.getColumn(colIndex++).width = 30; // Remarks
        if (taskDetailsTab !== 'notification') {
          worksheet.getColumn(colIndex++).width = 20; // Attachments
          worksheet.getColumn(colIndex++).width = 18; // Submission Date
        }

        // Add data to worksheet - always include cell name in export for documentation
        addDataToWorksheetBEEAdmin(
          worksheet,
          hierarchicalTasksForSheet,
          taskData,
          hasTrainingType,
          hasBuildingData,
          hasTask234,
          hasTask1,
          taskDetailsTab === 'notification'
        );

        // Freeze header row
        worksheet.views = [{ state: 'frozen', ySplit: 1 }];
      });
    } else {
      // Filters are active - use single sheet (existing behavior)
      const hierarchicalTasksForExport = groupTasksHierarchically(tasksForTaskDetailsView, null);

      // Check if Task 3 is present in all filtered data (for training columns in export)
      const hasTrainingTypeForExport = tasksForTaskDetailsView.some(task => {
        const taskNo = task.task_no || task.Task_No;
        const taskNum = Number(taskNo);
        if (taskNum !== 3) return false;
        const subtaskNo = task.subtask_no || task.Subtask_No;
        const subtaskNoStr = String(subtaskNo || '').toLowerCase();
        if (!subtaskNoStr.startsWith('2') && !subtaskNoStr.startsWith('3') && !subtaskNoStr.startsWith('4')) return false;
        const trainingType = task.training_type || task.Training_Type;
        return trainingType && String(trainingType).trim() !== '';
      });

      // Check if Task 2 or Task 4 is present in all filtered data (for building columns in export)
      const hasBuildingDataForExport = tasksForTaskDetailsView.some(task => {
        const taskNo = task.task_no || task.Task_No;
        const taskNum = Number(taskNo);
        return taskNum === 2 || taskNum === 4;
      });

      // Check if Tasks 2, 3, or 4 are present (for Summary column in export)
      const hasTask234ForExport = tasksForTaskDetailsView.some(task => {
        const taskNo = task.task_no || task.Task_No;
        return taskNo === 2 || taskNo === 3 || taskNo === 4;
      });

      const hasTask1ForExport = tasksForTaskDetailsView.some(task => {
        const taskNo = task.task_no || task.Task_No;
        return taskNo === 1;
      });

      // Create a new workbook and worksheet
      const worksheet = workbook.addWorksheet('Task Tracker');

      // Define headers - combined columns
      const headers = [
        'S. No.',
        'Cell Name (State)',
        ...(taskDetailsTab === 'notification'
          ? []
          : ['Task Description', 'Subtask Description']),
        'Quarter',
        taskDetailsTab === 'notification'
          ? 'status of Notification of ECSBC- Buildings'
          : 'Status',
        ...(hasTask1ForExport ? ['Is Notified?', 'Date of Notification', 'Incorporation in Building By-Laws'] : []),
        ...(hasTrainingTypeForExport ? ['Training Type', 'Training Mode', 'Date of Training', 'District', 'Total Participants', 'Govt. Officials', 'Female Participants', 'No. of Architects'] : []),
        ...(hasBuildingDataForExport ? ['Building Name', 'Building Address', 'Typology', 'Building Status', 'Nodal Contact Name', 'Nodal Contact Email', 'Department', 'EPI Value Proposed', 'EPI Ratio'] : []),
        ...(taskDetailsTab === 'notification' ? [] : ['Task Completion Date']),
        ...(hasTask234ForExport ? ['Summary'] : []),
        'Remarks',
        ...(taskDetailsTab === 'notification' ? [] : ['Attachments', 'Submission Date'])
      ];

    // Add header row with styling
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1E40AF' } // Dark blue
    };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    headerRow.height = 25;
    
    // Set column widths - combined columns
    worksheet.getColumn(1).width = 8; // S. No.
    worksheet.getColumn(2).width = 30; // Cell Name (State) with Cell No
    let colIndex;
    if (taskDetailsTab === 'notification') {
      worksheet.getColumn(3).width = 15; // Quarter
      worksheet.getColumn(4).width = 36; // Notification / status
      colIndex = 5;
    } else {
      worksheet.getColumn(3).width = 50; // Task Description with Task No
      worksheet.getColumn(4).width = 50; // Subtask Description with Subtask No
      worksheet.getColumn(5).width = 15; // Quarter
      worksheet.getColumn(6).width = 15; // Status
      colIndex = 7;
    }
    if (hasTask1ForExport) {
      worksheet.getColumn(colIndex++).width = 15; // Is Notified?
      worksheet.getColumn(colIndex++).width = 20; // Date of Notification
      worksheet.getColumn(colIndex++).width = 30; // Incorporation in Building By-Laws
    }
    if (hasTrainingTypeForExport) {
      worksheet.getColumn(colIndex++).width = 20; // Training Type
      worksheet.getColumn(colIndex++).width = 15; // Training Mode
      worksheet.getColumn(colIndex++).width = 20; // Date of Training
      worksheet.getColumn(colIndex++).width = 18; // District
      worksheet.getColumn(colIndex++).width = 18; // Total Participants
      worksheet.getColumn(colIndex++).width = 18; // Govt. Officials
      worksheet.getColumn(colIndex++).width = 20; // Female Participants
      worksheet.getColumn(colIndex++).width = 18; // No. of Architects
    }
    if (hasBuildingDataForExport) {
      worksheet.getColumn(colIndex++).width = 25; // Building Name
      worksheet.getColumn(colIndex++).width = 30; // Building Address
      worksheet.getColumn(colIndex++).width = 15; // Typology
      worksheet.getColumn(colIndex++).width = 18; // Building Status
      worksheet.getColumn(colIndex++).width = 20; // Nodal Contact Name
      worksheet.getColumn(colIndex++).width = 25; // Nodal Contact Email
      worksheet.getColumn(colIndex++).width = 20; // Department
      worksheet.getColumn(colIndex++).width = 18; // EPI Value Proposed
      worksheet.getColumn(colIndex++).width = 15; // EPI Ratio
    }
    if (taskDetailsTab !== 'notification') {
      worksheet.getColumn(colIndex++).width = 18; // Task Completion Date
    }
    if (hasTask234ForExport) {
      worksheet.getColumn(colIndex++).width = 40; // Summary
    }
    worksheet.getColumn(colIndex++).width = 30; // Remarks
    if (taskDetailsTab !== 'notification') {
      worksheet.getColumn(colIndex++).width = 20; // Attachments
      worksheet.getColumn(colIndex++).width = 18; // Submission Date
    }

      // Add data to worksheet
      addDataToWorksheetBEEAdmin(
        worksheet,
        hierarchicalTasksForExport,
        tasksForTaskDetailsView,
        hasTrainingTypeForExport,
        hasBuildingDataForExport,
        hasTask234ForExport,
        hasTask1ForExport,
        taskDetailsTab === 'notification'
      );

      // Freeze header row
      worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    }

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const dateStr = new Date().toISOString().split('T')[0];
    link.setAttribute(
      'download',
      taskDetailsTab === 'notification'
        ? `bee-work-tracker-task1-subtask2b-notification-${dateStr}.xlsx`
        : `bee-work-tracker-${dateStr}.xlsx`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatBeeTrainingOrganisedDate = (start, end) => {
    if (!start && !end) return '-';
    const fmt = (d) => {
      if (!d) return '';
      const x = new Date(d);
      if (Number.isNaN(x.getTime())) return String(d);
      return x.toLocaleDateString('en-GB');
    };
    const a = fmt(start);
    const b = fmt(end);
    if (a && b && a !== b) return `${a} - ${b}`;
    return a || b || '-';
  };

  const formatBeeTrainingVenueCell = (row) => {
    const mode = String(row.training_mode || '').trim().toLowerCase();
    const rawVenue = row.training_venue ?? row.training_Venue ?? row.venue;
    const venueLower = String(rawVenue ?? '').trim().toLowerCase();
    if (mode === 'online' || mode.includes('online')) return 'Online Mode';
    if (venueLower === 'online' || venueLower === 'online mode') return 'Online Mode';
    const s = rawVenue != null ? String(rawVenue).trim() : '';
    return s !== '' ? s : '-';
  };

  const formatBeeTrainingDistrictCell = (row) => {
    const name = row.training_district_name ?? row.training_District_Name;
    if (name != null && String(name).trim() !== '') return String(name).trim();
    const id = row.training_district_id ?? row.training_district;
    if (id != null && id !== '') return String(id);
    return '-';
  };

  const exportTrainingsToExcel = async () => {
    if (!beeTrainingsFiltered.length) return;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Trainings');
    const headers = [
      'Sl. No.',
      'State/UT Name',
      'Type of Training',
      'Mode',
      'District',
      'Training Venue',
      'Organised on Date',
      'No. of Participants',
      'Nos. of Architects',
      'Female Participants',
      'Govt. Officials'
    ];
    const headerRow = sheet.addRow(headers);
    headerRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1E40AF' }
    };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    beeTrainingsFiltered.forEach((row, idx) => {
      sheet.addRow([
        idx + 1,
        row.state_name || '',
        row.training_type || '',
        row.training_mode || '',
        formatBeeTrainingDistrictCell(row),
        formatBeeTrainingVenueCell(row),
        formatBeeTrainingOrganisedDate(row.training_start_date, row.training_end_date),
        row.total_attendees ?? 0,
        row.architects_count ?? 0,
        row.total_women ?? 0,
        row.government_sector_attendees ?? 0
      ]);
    });
    const widths = [8, 28, 36, 14, 20, 22, 24, 18, 18, 18, 18];
    widths.forEach((w, i) => {
      sheet.getColumn(i + 1).width = w;
    });
    sheet.views = [{ state: 'frozen', ySplit: 1 }];
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const dateStr = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `bee-work-tracker-task3-trainings-${dateStr}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatBuildingEpiCell = (val) => {
    if (val === null || val === undefined || val === '') return 'Not available';
    const n = Number(val);
    if (!Number.isNaN(n)) return String(n);
    return String(val);
  };

  const exportBuildingsToExcel = async () => {
    if (!beeBuildingsFiltered.length) return;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Buildings');
    const headers = [
      'Sl. No.',
      'State',
      'Task',
      'Subtask',
      'Name of the Building',
      'Address of the Building',
      'Typology',
      'Status (Completed / Under Construction / Design Stage)',
      'EPI Value'
    ];
    const headerRow = sheet.addRow(headers);
    headerRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFB45309' }
    };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    beeBuildingsFiltered.forEach((row, idx) => {
      const taskNo = row.task_no ?? '';
      const taskDesc = row.task_description || '';
      const subNo = row.subtask_no ?? '';
      const subDesc = row.subtask_description || '';
      sheet.addRow([
        idx + 1,
        row.state_name || '',
        taskNo !== '' ? `Task ${taskNo}: ${taskDesc}` : taskDesc,
        subNo !== '' ? `Subtask ${subNo}: ${subDesc}` : subDesc,
        row.building_name || '',
        row.building_address || '',
        row.building_typology || '',
        row.building_status || '',
        formatBuildingEpiCell(row.epi_value)
      ]);
    });
    const widths = [8, 24, 36, 36, 28, 40, 18, 28, 14];
    widths.forEach((w, i) => {
      sheet.getColumn(i + 1).width = w;
    });
    sheet.views = [{ state: 'frozen', ySplit: 1 }];
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const dateStr = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `bee-work-tracker-buildings-task2-task4-${dateStr}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Completed': 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-sm',
      'In Progress': 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm',
      'In-Progress': 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-sm', // Handle hyphenated version
      'On Hold': 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm',
      'On-Hold': 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm', // Handle hyphenated version
      'Cancelled': 'bg-gradient-to-r from-red-500 to-rose-500 text-white shadow-sm'
    };
    return statusColors[status] || 'bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-sm';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Completed': <CheckCircle2 className="h-3 w-3" />,
      'In Progress': <Clock className="h-3 w-3" />,
      'In-Progress': <Clock className="h-3 w-3" />, // Handle hyphenated version
      'On Hold': <AlertCircle className="h-3 w-3" />,
      'On-Hold': <AlertCircle className="h-3 w-3" />, // Handle hyphenated version
      'Cancelled': <X className="h-3 w-3" />
    };
    return icons[status] || null;
  };

      if (user?.role === 'ECSBC-Professional' || user?.role === 'Nodal Officer SDA') {
    return <EPDashboard />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 min-w-0 max-w-full">
      {/* Enhanced Header Section */}
      <div className="bg-blue-600 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-white/20 rounded-lg">
                <Activity className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                ECSBC Cell Tracker BEE Dashboard
              </h1>
            </div>
            <p className="text-blue-100 text-sm ml-11">
              Comprehensive project monitoring and management system
            </p>
          </div>
          <div className="hidden lg:flex items-center gap-4">
            <div className="text-right bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <div className="text-xs text-blue-100 font-medium">
                {taskDetailsTab === 'trainings'
                  ? 'Training records'
                  : taskDetailsTab === 'buildings'
                    ? 'Building records'
                    : 'Total Tasks'}
              </div>
              <div className="text-lg font-bold text-white">
                {taskDetailsTab === 'trainings'
                  ? beeTrainingsFiltered.length
                  : taskDetailsTab === 'buildings'
                    ? beeBuildingsFiltered.length
                    : tasksForTaskDetailsView.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link to="/cell-status" className="group">
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors">
                <MapPin className="h-8 w-8 text-blue-600" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">Cell</div>
                <div className="text-sm text-gray-500">Wise Status</div>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Cell Wise Status</h3>
            <p className="text-sm text-gray-600 mb-4">View detailed status and progress for each cell</p>
            <div className="flex items-center text-blue-600 text-sm font-medium">
              View Details
              <ArrowLeft className="h-4 w-4 ml-1 transform rotate-180" />
            </div>
          </div>
        </Link>

        <Link to="/task-status" className="group">
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-full group-hover:bg-green-200 transition-colors">
                <Target className="h-8 w-8 text-green-600" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-green-600">Task</div>
                <div className="text-sm text-gray-500">Wise Status</div>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Task Wise Status</h3>
            <p className="text-sm text-gray-600 mb-4">Monitor task completion across all cells</p>
            <div className="flex items-center text-green-600 text-sm font-medium">
              View Details
              <ArrowLeft className="h-4 w-4 ml-1 transform rotate-180" />
            </div>
          </div>
        </Link>

        <Link to="/quarter-status" className="group">
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-full group-hover:bg-purple-200 transition-colors">
                <BarChart3 className="h-8 w-8 text-purple-600" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-purple-600">Quarter</div>
                <div className="text-sm text-gray-500">Wise Status</div>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Quarter Wise Status</h3>
            <p className="text-sm text-gray-600 mb-4">Analyze quarterly progress and trends</p>
            <div className="flex items-center text-purple-600 text-sm font-medium">
              View Details
              <ArrowLeft className="h-4 w-4 ml-1 transform rotate-180" />
            </div>
          </div>
        </Link>

        <Link to="/overall-status" className="group">
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-100 rounded-full group-hover:bg-orange-200 transition-colors">
                <Activity className="h-8 w-8 text-orange-600" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-orange-600">Overall</div>
                <div className="text-sm text-gray-500">Status</div>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Overall Status</h3>
            <p className="text-sm text-gray-600 mb-4">Get comprehensive system overview</p>
            <div className="flex items-center text-orange-600 text-sm font-medium">
              View Details
              <ArrowLeft className="h-4 w-4 ml-1 transform rotate-180" />
            </div>
          </div>
        </Link>
      </div>

      {/* Filterable Table Section */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 min-w-0 max-w-full">
        {/* Filters Header with Gradient */}
        <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-blue-50 p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <Filter className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Task Details & Filters
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white rounded-xl hover:bg-gray-50 transition-all shadow-sm hover:shadow-md border border-gray-200"
                >
                  <X className="h-4 w-4" />
                  Clear Filters
                </button>
              )}
              <button
                onClick={() => {
                  if (taskDetailsTab === 'trainings') exportTrainingsToExcel();
                  else if (taskDetailsTab === 'buildings') exportBuildingsToExcel();
                  else exportToCSV();
                }}
                disabled={
                  taskDetailsTab === 'trainings'
                    ? beeTrainingsFetchPending || !beeTrainingsFiltered.length
                    : taskDetailsTab === 'buildings'
                      ? beeBuildingsFetchPending || !beeBuildingsFiltered.length
                      : beeTasksFetchPending || !tasksForTaskDetailsView.length
                }
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg transform hover:scale-105 disabled:hover:scale-100"
              >
                <Download className="h-4 w-4" />
                Export Excel
              </button>
            </div>
          </div>

          {/* Tabs: all tasks vs notification task (Task 1 / Subtask 2b) */}
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setTaskDetailsTab('all');
                setCurrentPage(1);
              }}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all border-2 ${
                taskDetailsTab === 'all'
                  ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50/50'
              }`}
            >
              <ListOrdered className="h-4 w-4 shrink-0" />
              All tasks
            </button>
            <button
              type="button"
              onClick={() => {
                setTaskDetailsTab('notification');
                setCurrentPage(1);
              }}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all border-2 ${
                taskDetailsTab === 'notification'
                  ? 'border-violet-600 bg-violet-600 text-white shadow-md'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-violet-300 hover:bg-violet-50/50'
              }`}
            >
              <Bell className="h-4 w-4 shrink-0" />
              Notification Task
            </button>
            <button
              type="button"
              onClick={() => {
                setTaskDetailsTab('trainings');
                setCurrentPage(1);
              }}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all border-2 ${
                taskDetailsTab === 'trainings'
                  ? 'border-teal-600 bg-teal-600 text-white shadow-md'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-teal-300 hover:bg-teal-50/50'
              }`}
            >
              <GraduationCap className="h-4 w-4 shrink-0" />
              Trainings
            </button>
            <button
              type="button"
              onClick={() => {
                setTaskDetailsTab('buildings');
                setCurrentPage(1);
              }}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all border-2 ${
                taskDetailsTab === 'buildings'
                  ? 'border-amber-600 bg-amber-600 text-white shadow-md'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-amber-300 hover:bg-amber-50/50'
              }`}
            >
              <Building2 className="h-4 w-4 shrink-0" />
              Buildings
            </button>
          </div>

          {/* Enhanced Search Bar */}
          <div className="mb-6">
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
              <input
                type="text"
                placeholder="Search by cell name, cell number, task, status, or quarter..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
          </div>

          {/* Enhanced Filter Row */}
          <div
  className="
    grid
    grid-cols-1
    sm:grid-cols-2
    md:grid-cols-4
    xl:grid-cols-8
    gap-4
    items-start
  "
>
            {/* Cell Filter */}
  <div className="group min-w-0">
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1 whitespace-nowrap">
                <MapPin className="h-3.5 w-3.5" />
                Cell
              </label>
              <select
                value={filters.cellId}
                onChange={(e) => handleFilterChange('cellId', e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg text-sm font-medium"
              >
                <option value="">All Cells</option>
                {dropdownData?.cells?.map((cell) => (
                  <option key={cell.cell_id} value={cell.cell_id}>
                    {cell.State_UT || cell.state_ut || 'State'} - Cell {cell.cell_no}
                  </option>
                ))}
              </select>
            </div>

            {taskDetailsTab !== 'notification' && (
            <>
            {/* Task Filter */}
  <div className="group min-w-0">
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1 whitespace-nowrap">
                <Target className="h-3.5 w-3.5" />
                Task
              </label>
    <Listbox
                value={filters.taskId}
      onChange={(value) => {
        const newTaskId = value;
        setFilters(prev => ({ ...prev, taskId: newTaskId, subtaskId: '' }));
                  setCurrentPage(1);
                }}
              >
      <div className="relative">
        <Listbox.Button
          className="
            w-full
            px-4 py-2.5
            border-2 border-gray-200
            rounded-xl
            focus:ring-2 focus:ring-blue-500
            focus:border-blue-500
            bg-white
            shadow-sm
            hover:shadow-md
            focus:shadow-lg
            text-sm font-medium
            text-left
            flex items-center justify-between
            transition-all
          "
        >
          <span className="block truncate flex-1 min-w-0 pr-2">
            {filters.taskId
              ? (() => {
                  const task = tasksForBeeDashboardTaskFilter.find(t => t.task_id === parseInt(filters.taskId, 10));
                  if (!task) return 'All Tasks';
                  return `Task ${task.task_no}: ${task.task_description || ''}`;
                })()
              : 'All Tasks'}
          </span>
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
        </Listbox.Button>
        <Transition
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options 
            className="absolute z-[9999] mt-1 w-full min-w-[400px] max-w-[600px] max-h-96 overflow-auto rounded-xl bg-white py-1 text-sm shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none border-2 border-gray-200 modal-width-responsive"
          >
            <Listbox.Option
              value=""
              className={({ active }) =>
                `relative cursor-pointer select-none py-2 px-4 ${
                  active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                }`
              }
            >
              {({ selected }) => (
                <>
                  <span className={`block ${selected ? 'font-medium' : 'font-normal'}`}>
                    All Tasks
                  </span>
                  {selected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </>
              )}
            </Listbox.Option>
            {tasksForBeeDashboardTaskFilter.map((task) => {
              const text = `Task ${task.task_no}: ${task.task_description || ''}`;
              return (
                <Listbox.Option
                  key={task.task_id}
                  value={String(task.task_id)}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 px-4 ${
                      active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block break-words whitespace-normal ${selected ? 'font-medium' : 'font-normal'}`}
                        style={{ 
                          wordWrap: 'break-word', 
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: '1.5'
                        }}
                      >
                        {text}
                      </span>
                      {selected && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                          <Check className="h-4 w-4" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              );
            })}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
            </div>

            {/* Subtask Filter */}
  <div className="group min-w-0">
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1 whitespace-nowrap">
                <FileText className="h-3.5 w-3.5" />
                Subtask
              </label>
    <Listbox
                value={filters.subtaskId}
      onChange={(value) => handleFilterChange('subtaskId', value)}
    >
      <div className="relative">
        <Listbox.Button
          className="
            w-full
            px-4 py-2.5
            border-2 border-gray-200
            rounded-xl
            focus:ring-2 focus:ring-blue-500
            focus:border-blue-500
            bg-white
            shadow-sm
            hover:shadow-md
            focus:shadow-lg
            text-sm font-medium
            text-left
            flex items-center justify-between
            transition-all
          "
        >
          <span className="block truncate flex-1 min-w-0 pr-2">
            {filters.subtaskId
              ? (() => {
                  const subtask = filteredSubtasks.find(s => 
                    (s.subtask_id || s.Subtask_ID) === parseInt(filters.subtaskId)
                  );
                  if (!subtask) return 'All Subtasks';
                  const subtaskNo = subtask.subtask_no || subtask.Subtask_No || '';
                  const subtaskDesc = subtask.subtask_description || subtask.Subtask_Description || '';
                  const taskInfo = getTaskInfo(subtask);
                  return taskInfo ? `${taskInfo} - Subtask ${subtaskNo}: ${subtaskDesc}` : `Subtask ${subtaskNo}: ${subtaskDesc}`;
                })()
              : 'All Subtasks'}
          </span>
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
        </Listbox.Button>
        <Transition
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options 
            className="absolute z-[9999] mt-1 w-full min-w-[400px] max-w-[600px] max-h-96 overflow-auto rounded-xl bg-white py-1 text-sm shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none border-2 border-gray-200 modal-width-responsive"
          >
            <Listbox.Option
              value=""
              className={({ active }) =>
                `relative cursor-pointer select-none py-2 px-4 ${
                  active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                }`
              }
              >
              {({ selected }) => (
                <>
                  <span className={`block ${selected ? 'font-medium' : 'font-normal'}`}>
                    All Subtasks
                  </span>
                  {selected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </>
              )}
            </Listbox.Option>
                {filteredSubtasks.map((subtask) => {
                  const subtaskNo = subtask.subtask_no || subtask.Subtask_No || '';
                  const subtaskDesc = subtask.subtask_description || subtask.Subtask_Description || '';
                  const taskInfo = getTaskInfo(subtask);
              const text = taskInfo ? `${taskInfo} - Subtask ${subtaskNo}: ${subtaskDesc}` : `Subtask ${subtaskNo}: ${subtaskDesc}`;
                  return (
                <Listbox.Option
                  key={subtask.subtask_id || subtask.Subtask_ID}
                  value={String(subtask.subtask_id || subtask.Subtask_ID)}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 px-4 ${
                      active ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block break-words whitespace-normal ${selected ? 'font-medium' : 'font-normal'}`}
                        style={{ 
                          wordWrap: 'break-word', 
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: '1.5'
                        }}
                      >
                        {text}
                      </span>
                      {selected && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                          <Check className="h-4 w-4" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
                  );
                })}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
            </div>
            </>
            )}

            {/* Quarter Filter */}
  <div className="group min-w-0">
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1 whitespace-nowrap">
                <Calendar className="h-3.5 w-3.5" />
                Quarter
              </label>
              <select
                value={filters.quarterId}
                onChange={(e) => handleFilterChange('quarterId', e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg text-sm font-medium"
              >
                <option value="">All Quarters</option>
                {dropdownData?.quarters?.map((quarter) => (
                  <option key={quarter.id} value={quarter.id}>
                    {quarter.quarter_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
  <div className="group min-w-0">
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1 whitespace-nowrap">
                <Activity className="h-3.5 w-3.5" />
                Status
              </label>
              <select
                value={filters.statusId}
                onChange={(e) => handleFilterChange('statusId', e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg text-sm font-medium"
              >
      <option value="">All Status</option>
                {dropdownData?.statuses?.map((status) => (
                  <option key={status.status_id} value={status.status_id}>
                    {status.status}
                  </option>
                ))}
              </select>
            </div>

  {/* Building Type Filter */}
  <div className="group min-w-0">
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1 whitespace-nowrap">
      <Building2 className="h-3.5 w-3.5" />
      Building Type
              </label>
              <select
      value={filters.buildingType}
      onChange={(e) => handleFilterChange('buildingType', e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg text-sm font-medium"
              >
      <option value="">All Building Types</option>
      {buildingTypeOptions.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
              </select>
            </div>

            {/* Training Type Filter */}
  <div className="group min-w-0">
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1 whitespace-nowrap">
                <Building2 className="h-3.5 w-3.5" />
                Training Type
              </label>
              <select
                value={filters.trainingId}
                onChange={(e) => handleFilterChange('trainingId', e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg text-sm font-medium"
              >
                <option value="">All Training Types</option>
                {dropdownData?.trainings?.map((training) => (
        <option
          key={training.training_id || training.Training_ID}
          value={training.training_id || training.Training_ID}
        >
          {training.training_type || training.Training_Type}
          {training.training_mode ? ` (${training.training_mode})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Week Filter */}
  <div className="group min-w-0">
    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1 whitespace-nowrap">
                <Calendar className="h-3.5 w-3.5" />
                Week
              </label>
              <select
                value={filters.week}
                onChange={(e) => handleFilterChange('week', e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm hover:shadow-md focus:shadow-lg text-sm font-medium"
              >
                <option value="">All Weeks</option>
                {uniqueWeeks.map((week) => {
                  const [year, weekNum] = week.split('-W');
                  return (
                    <option key={week} value={week}>
                      Week {weekNum}, {year}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        {/* Enhanced Pagination - Outside scrollable container */}
        {((taskDetailsTab === 'trainings' && !beeTrainingsFetchPending && beeTrainingsFiltered.length > 0) ||
          (taskDetailsTab === 'buildings' && !beeBuildingsFetchPending && beeBuildingsFiltered.length > 0) ||
          (taskDetailsTab !== 'trainings' &&
            taskDetailsTab !== 'buildings' &&
            !beeTasksFetchPending &&
            tasksForTaskDetailsView.length > 0)) && (
          <div className="px-6 py-5 bg-gradient-to-r from-gray-50 to-blue-50 border-b-2 border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4 mb-0 rounded-t-lg shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              {taskDetailsTab === 'trainings' ? (
                <div className="text-xs font-semibold text-teal-700 bg-teal-100 px-3 py-1 rounded-lg">
                  Task 3 ? Trainings ({beeTrainingsFiltered.length} records)
                </div>
              ) : taskDetailsTab === 'buildings' ? (
                <div className="text-xs font-semibold text-amber-800 bg-amber-100 px-3 py-1 rounded-lg">
                  Task 2 (subtasks 2-3) and Task 4 (subtasks 1-2) � Buildings ({beeBuildingsFiltered.length}{' '}
                  records)
                </div>
              ) : (
                <>
              {taskGroups[currentPage - 1] && (
                <div className="text-xs font-semibold text-purple-600 bg-purple-100 px-3 py-1 rounded-lg">
                  Task {taskGroups[currentPage - 1].taskNo} ({paginatedTasks.length} entries)
                </div>
              )}
                </>
              )}
            </div>
            {taskDetailsTab !== 'trainings' && taskDetailsTab !== 'buildings' && totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                  onClick={handlePagePrev}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-blue-500 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md disabled:hover:shadow-sm"
              >
                Previous
              </button>
              {taskGroups[currentPage - 1] && (
                <div className="px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-md">
                    Task {taskGroups[currentPage - 1].taskNo} of {maxTaskNo || totalPages}
                </div>
              )}
              <button
                  onClick={() => handlePageNext(totalPages)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-purple-500 hover:text-purple-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md disabled:hover:shadow-sm"
              >
                Next
              </button>
            </div>
            )}
            {taskDetailsTab !== 'trainings' &&
              taskDetailsTab !== 'buildings' &&
              totalPages === 1 &&
              taskGroups[currentPage - 1] && (
              <div className="px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-md">
                Task {taskGroups[currentPage - 1].taskNo} of {maxTaskNo || totalPages}
              </div>
            )}
          </div>
        )}

        {/* Table: dual horizontal scroll (top + bottom) kept in sync */}
        <div className="w-full min-w-0 max-w-full table-container rounded-b-2xl">
          {taskDetailsTab === 'trainings' ? (
            beeTrainingsFetchPending ? (
              <div className="flex flex-col items-center justify-center py-16 min-h-[220px]">
                <Loader2 className="h-10 w-10 animate-spin text-teal-600" aria-hidden />
                <p className="mt-4 text-gray-600 font-medium">Loading trainings...</p>
              </div>
            ) : beeTrainingsFiltered.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-4">
                  <GraduationCap className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No training records found</h3>
                <p className="text-gray-500 mb-4">Try adjusting filters or search (Task 3, subtasks 2-5)</p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <X className="h-4 w-4" />
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              <div className="dashboard-table-scroll-viewport w-full min-w-0 rounded-b-2xl bg-white">
                <div
                  ref={beeTrainingsScrollTopRef}
                  className="dashboard-table-scroll dashboard-table-scroll--top w-full min-w-0 max-w-full border-b border-gray-200 bg-white"
                  onScroll={handleBeeTrainingsScrollSync}
                >
                  <div ref={beeTrainingsScrollSpacerRef} className="h-px pointer-events-none" aria-hidden />
                </div>
                <div
                  ref={beeTrainingsScrollBottomRef}
                  className="dashboard-table-scroll-body w-full min-w-0 max-w-full bg-white"
                  onScroll={handleBeeTrainingsScrollSync}
                >
                  <table
                    className="min-w-[1440px]"
                    style={{ tableLayout: 'auto', borderCollapse: 'collapse', width: 'max-content', minWidth: '1440px' }}
                  >
                    <thead className="bg-gradient-to-r from-gray-50 to-teal-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-200 w-14">
                          <div className="flex items-center justify-center gap-2">
                            <ListOrdered className="h-4 w-4 text-gray-600" />
                            Sl. No.
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[12rem]">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-blue-600" />
                            State/UT Name
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 table-col-ep-subtask">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-indigo-600" />
                            Type of Training
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-200">
                          Mode
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[9rem] max-w-[14rem] w-[11rem]">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-teal-600 shrink-0" />
                            <span className="whitespace-normal break-words leading-tight">District</span>
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[10rem] max-w-[18rem] w-[14rem]">
                          <span className="whitespace-normal break-words leading-tight">Training Venue</span>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-200 min-w-[9rem]">
                          Organised on Date
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-200">
                          No. of Participants
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-200">
                          Nos. of Architects
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-200">
                          Female Participants
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                          Govt. Officials
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {beeTrainingsFiltered.map((row, idx) => (
                        <tr
                          key={row.task_detail_id ?? idx}
                          className="hover:bg-gradient-to-r hover:from-teal-50/50 hover:to-blue-50/40 transition-colors"
                        >
                          <td className="px-4 py-4 text-center text-sm font-semibold text-gray-600 tabular-nums border-r border-gray-200">
                            {idx + 1}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 align-top">
                            {row.state_name || '-'}
                            <div className="text-xs text-gray-500 mt-0.5">Cell {row.cell_no ?? '-'}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800 border-r border-gray-200 align-top table-col-ep-subtask word-break-safe whitespace-normal break-words">
                            {row.training_type || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 border-r border-gray-200 whitespace-nowrap">
                            {row.training_mode || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800 border-r border-gray-200 align-top min-w-[9rem] max-w-[14rem] w-[11rem] word-break-safe whitespace-normal break-words">
                            {formatBeeTrainingDistrictCell(row)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800 border-r border-gray-200 align-top min-w-[10rem] max-w-[18rem] w-[14rem] word-break-safe whitespace-normal break-words">
                            {formatBeeTrainingVenueCell(row)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 border-r border-gray-200 whitespace-nowrap">
                            {formatBeeTrainingOrganisedDate(row.training_start_date, row.training_end_date)}
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-gray-800 border-r border-gray-200 tabular-nums">
                            {row.total_attendees ?? '-'}
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-gray-800 border-r border-gray-200 tabular-nums">
                            {row.architects_count ?? '-'}
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-gray-800 border-r border-gray-200 tabular-nums">
                            {row.total_women ?? '-'}
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-gray-800 tabular-nums">
                            {row.government_sector_attendees ?? '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div
                  ref={beeTrainingsScrollBottomMirrorRef}
                  className="dashboard-table-scroll dashboard-table-scroll--bottom w-full min-w-0 max-w-full bg-white"
                  onScroll={handleBeeTrainingsScrollSync}
                >
                  <div ref={beeTrainingsScrollBottomMirrorSpacerRef} className="h-px pointer-events-none" aria-hidden />
                </div>
              </div>
            )
          ) : taskDetailsTab === 'buildings' ? (
            beeBuildingsFetchPending ? (
              <div className="flex flex-col items-center justify-center py-16 min-h-[220px]">
                <Loader2 className="h-10 w-10 animate-spin text-amber-600" aria-hidden />
                <p className="mt-4 text-gray-600 font-medium">Loading buildings...</p>
              </div>
            ) : beeBuildingsFiltered.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-4">
                  <Building2 className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No building records found</h3>
                <p className="text-gray-500 mb-4">
                  Task 2 (subtasks 2-3) and Task 4 (subtasks 1-2) with linked buildings � try filters or search
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <X className="h-4 w-4" />
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              <div className="dashboard-table-scroll-viewport w-full min-w-0 rounded-b-2xl bg-white">
                <div
                  ref={beeBuildingsScrollTopRef}
                  className="dashboard-table-scroll dashboard-table-scroll--top w-full min-w-0 max-w-full border-b border-gray-200 bg-white"
                  onScroll={handleBeeBuildingsScrollSync}
                >
                  <div ref={beeBuildingsScrollSpacerRef} className="h-px pointer-events-none" aria-hidden />
                </div>
                <div
                  ref={beeBuildingsScrollBottomRef}
                  className="dashboard-table-scroll-body w-full min-w-0 max-w-full bg-white"
                  onScroll={handleBeeBuildingsScrollSync}
                >
                  <table
                    className="min-w-[1040px]"
                    style={{ tableLayout: 'auto', borderCollapse: 'collapse', width: 'max-content', minWidth: '1040px' }}
                  >
                    <thead className="bg-gradient-to-r from-gray-50 to-amber-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-200 w-14">
                          <div className="flex items-center justify-center gap-2">
                            <ListOrdered className="h-4 w-4 text-gray-600" />
                            Sl. No.
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[9rem] max-w-[11rem] w-[10rem]">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-blue-600" />
                            State
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 table-col-ep-task">
                          <div className="flex items-center gap-2">
                            <Target className="h-4 w-4 text-green-600" />
                            Task
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 table-col-bee-building-subtask">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-purple-600" />
                            Subtask
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 table-col-building-name">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-indigo-600 shrink-0" />
                            <span className="whitespace-normal break-words leading-tight">Name of the Building</span>
                          </div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 table-col-building-address">
                          <span className="whitespace-normal break-words leading-tight">Address of the Building</span>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 table-col-building-meta">
                          Typology
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 table-col-building-meta">
                          Status
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                          EPI Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {beeBuildingsFiltered.map((row, idx) => (
                        <tr
                          key={row.task_detail_id ?? idx}
                          className="hover:bg-gradient-to-r hover:from-amber-50/50 hover:to-blue-50/40 transition-colors"
                        >
                          <td className="px-4 py-4 text-center text-sm font-semibold text-gray-600 tabular-nums border-r border-gray-200">
                            {idx + 1}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 border-r border-gray-200 align-top min-w-[9rem] max-w-[11rem] w-[10rem] word-break-safe whitespace-normal break-words">
                            {row.state_name || '-'}
                            <div className="text-xs text-gray-500 mt-0.5">Cell {row.cell_no ?? '-'}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800 border-r border-gray-200 align-top table-col-ep-task word-break-safe whitespace-normal break-words">
                            {row.task_no != null && row.task_no !== ''
                              ? `Task ${row.task_no}: ${row.task_description || ''}`
                              : row.task_description || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800 border-r border-gray-200 align-top table-col-bee-building-subtask word-break-safe whitespace-normal break-words">
                            {row.subtask_no != null && row.subtask_no !== ''
                              ? `Subtask ${row.subtask_no}: ${row.subtask_description || ''}`
                              : row.subtask_description || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 border-r border-gray-200 align-top table-col-building-name word-break-safe whitespace-normal break-words">
                            {row.building_name || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 border-r border-gray-200 align-top table-col-building-address word-break-safe whitespace-normal break-words">
                            {row.building_address || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800 border-r border-gray-200 align-top table-col-building-meta word-break-safe whitespace-normal break-words">
                            {row.building_typology || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-800 border-r border-gray-200 align-top table-col-building-meta word-break-safe whitespace-normal break-words">
                            {row.building_status || '-'}
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-gray-800 tabular-nums">
                            {formatBuildingEpiCell(row.epi_value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div
                  ref={beeBuildingsScrollBottomMirrorRef}
                  className="dashboard-table-scroll dashboard-table-scroll--bottom w-full min-w-0 max-w-full bg-white"
                  onScroll={handleBeeBuildingsScrollSync}
                >
                  <div ref={beeBuildingsScrollBottomMirrorSpacerRef} className="h-px pointer-events-none" aria-hidden />
                </div>
              </div>
            )
          ) : beeTasksFetchPending ? (
            <div className="flex flex-col items-center justify-center py-16 min-h-[220px]">
              <Loader2 className="h-10 w-10 animate-spin text-blue-600" aria-hidden />
              <p className="mt-4 text-gray-600 font-medium">Loading tasks...</p>
            </div>
          ) : tasksForTaskDetailsView.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-4">
                <Search className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No tasks found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your filters or search criteria</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <X className="h-4 w-4" />
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="dashboard-table-scroll-viewport w-full min-w-0 rounded-b-2xl bg-white">
              <div
                ref={tableScrollTopRef}
                className="dashboard-table-scroll dashboard-table-scroll--top w-full min-w-0 max-w-full border-b border-gray-200 bg-white"
                onScroll={handleTableScrollSync}
              >
                <div ref={tableScrollSpacerRef} className="h-px pointer-events-none" aria-hidden />
              </div>
              <div
                ref={tableScrollBottomRef}
                className="dashboard-table-scroll-body w-full min-w-0 max-w-full bg-white"
                onScroll={handleTableScrollSync}
              >
                <table
                  className={taskDetailsTab === 'notification' ? 'min-w-[1100px]' : 'min-w-[1600px]'}
                  style={{
                    tableLayout: 'auto',
                    borderCollapse: 'collapse',
                    width: 'max-content',
                    minWidth: taskDetailsTab === 'notification' ? '1100px' : '1600px'
                  }}
                >
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap w-14 min-w-[3.5rem] border-r border-gray-200">
                        <div className="flex items-center justify-center gap-2">
                          <ListOrdered className="h-4 w-4 text-gray-600" />
                          S. No.
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-blue-600" />
                          Cell Name
                        </div>
                      </th>
                      {taskDetailsTab !== 'notification' && (
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap table-col-ep-task">
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-green-600" />
                          Task
                        </div>
                      </th>
                      )}
                      {taskDetailsTab !== 'notification' && (
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap table-col-ep-subtask">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-purple-600" />
                          Subtask
                        </div>
                      </th>
                      )}
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-orange-600" />
                          Quarter
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider align-top">
                        <div className={`flex gap-2 ${taskDetailsTab === 'notification' ? 'items-start' : 'items-center'}`}>
                          <Activity className={`h-4 w-4 text-red-600 shrink-0 ${taskDetailsTab === 'notification' ? 'mt-0.5' : ''}`} />
                          <span className="leading-tight">
                            {taskDetailsTab === 'notification'
                              ? 'status of Notification of ECSBC- Buildings'
                              : 'Status'}
                          </span>
                        </div>
                      </th>
                      {hasTask1 && (
                        <>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-blue-600" />
                              Is Notified?
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-green-600" />
                              Date of Notification
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-purple-600" />
                              Incorporation in Building By-Laws
                            </div>
                          </th>
                        </>
                      )}
                      {hasTrainingType && (
                        <>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-teal-600" />
                              Training Type
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-blue-600" />
                              Date of Training
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-green-600" />
                              District
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-indigo-600" />
                              Total Participants
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                            <div className="flex items-center gap-2 whitespace-normal leading-tight">
                              <Users className="h-4 w-4 text-blue-600 shrink-0" />
                              Govt. Officials
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-pink-600" />
                              Female Participants
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-amber-600" />
                              No. of Architects
                            </div>
                          </th>
                        </>
                      )}
                      {hasBuildingData && (
                        <>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-indigo-600" />
                              Building Name
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-blue-600" />
                              Building Address
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-purple-600" />
                              Typology
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Activity className="h-4 w-4 text-orange-600" />
                              Building Status
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-green-600" />
                              Nodal Contact Name
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <MessageSquare className="h-4 w-4 text-teal-600" />
                              Nodal Contact Email
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-purple-600" />
                              Department
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="h-4 w-4 text-blue-600" />
                              EPI Value Proposed
                            </div>
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Activity className="h-4 w-4 text-green-600" />
                              EPI Ratio
                            </div>
                          </th>
                        </>
                      )}
                      {taskDetailsTab !== 'notification' && (
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-violet-600" />
                          Task Completion Date
                        </div>
                      </th>
                      )}
                      {hasTask234 && (
                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap border-r border-gray-300">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-indigo-600" />
                            Summary
                          </div>
                        </th>
                      )}
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 text-cyan-600" />
                          Remarks
                        </div>
                      </th>
                      {taskDetailsTab !== 'notification' && (
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Paperclip className="h-4 w-4 text-amber-600" />
                          Attachments
                        </div>
                      </th>
                      )}
                      {taskDetailsTab !== 'notification' && (
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-pink-600" />
                          Submission Date
                        </div>
                      </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {(() => {
                      // Pre-calculate cell row spans for all cells to ensure accuracy
                      const cellRowSpanMap = {};
                      hierarchicalTasks.forEach(task => {
                        const cellKey = `${task.state_name || ''}_${task.cell_no || ''}`;
                        if (!cellRowSpanMap[cellKey]) {
                          cellRowSpanMap[cellKey] = 0;
                        }
                        cellRowSpanMap[cellKey]++;
                      });
                      
                      return hierarchicalTasks.map((task, index) => {
                        const taskNo = task.task_no || task.Task_No;
                        const subtaskNo = task.subtask_no || task.Subtask_No;
                        const cellKey = `${task.state_name || ''}_${task.cell_no || ''}`;
                        
                        return (
                        <tr 
                            key={`${task.id || task.ID || task.taskdetail_id || index}-${cellKey}-${taskNo}-${subtaskNo}-${task.quarter_id || task.Quarter_ID || ''}`} 
                          className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 group cursor-pointer"
                        >
                            <td className="px-4 py-5 text-center align-middle border-r border-gray-200 text-sm font-semibold text-gray-600 tabular-nums w-14 min-w-[3.5rem]">
                              {startRow + index}
                            </td>
                            {/* Cell Name Column - Always render, no rowSpan */}
                            <td 
                              className="px-6 py-5 whitespace-nowrap align-middle border-r border-gray-200" 
                              style={{ verticalAlign: 'middle' }}
                            >
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                                <MapPin className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <div className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{task.state_name || '-'}</div>
                                <div className="text-xs text-gray-500 font-medium">Cell {task.cell_no || ''}</div>
                              </div>
                            </div>
                          </td>
                          
                          {taskDetailsTab !== 'notification' && (
                          <>
                          {/* Task Column - Always render, no rowSpan */}
                          <td 
                            className="px-6 py-5 align-top border-r border-gray-200 table-col-ep-task word-break-safe whitespace-normal break-words"
                          >
                          <div className="max-w-full">
                            {taskNo ? (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded border border-green-200">
                                  Task {taskNo}
                                </span>
                              </div>
                            ) : null}
                            <div className="text-sm font-medium text-gray-900 whitespace-normal break-words group-hover:text-blue-600 transition-colors">
                              {task.task_description || '-'}
                            </div>
                          </div>
                        </td>
                          
                          {/* Subtask Column - Always render, no rowSpan */}
                          <td 
                            className="px-6 py-5 border-r border-gray-200 align-top table-col-ep-subtask word-break-safe whitespace-normal break-words"
                          >
                            <div className="max-w-full">
                            {subtaskNo ? (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                                  Subtask {subtaskNo}
                                </span>
                              </div>
                            ) : null}
                                  <div className="text-sm text-gray-700 whitespace-normal break-words group-hover:text-purple-600 transition-colors">
                              {task.subtask_description || task.Subtask_Description || '-'}
                            </div>
                          </div>
                        </td>
                          </>
                          )}
                          
                          {/* Quarter Column - Always render, no rowSpan */}
                          <td className="px-6 py-5 whitespace-nowrap border-r border-gray-200">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-lg border border-orange-200">
                              <Calendar className="h-3.5 w-3.5 text-orange-600" />
                              <span className="text-sm font-semibold text-orange-700">{task.quarter_name || task.Quarter_Name || '-'}</span>
                            </div>
                          </td>
                          
                          {/* Status Column - Always render, no rowSpan */}
                          <td 
                            className="px-6 py-5 whitespace-nowrap border-r border-gray-200"
                          >
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg ${getStatusColor(task.status)}`}>
                            {getStatusIcon(task.status)}
                            {task.status || '-'}
                          </span>
                        </td>
                          
                          {/* Task 1 Specific Columns - Always render, no rowSpan */}
                          {hasTask1 && (
                            <>
                              {taskNo === 1 ? (
                                <>
                                  <td className="px-6 py-5 whitespace-nowrap border-r border-gray-200">
                                    {task.is_notified !== null && task.is_notified !== undefined ? (
                                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                        task.is_notified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                      }`}>
                                        {task.is_notified ? 'Yes' : 'No'}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 text-sm">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-5 whitespace-nowrap border-r border-gray-200">
                                    {task.notification_date ? (
                                      <span className="text-sm text-gray-700">
                                        {new Date(task.notification_date).toLocaleDateString('en-GB', { 
                                          day: '2-digit', 
                                          month: '2-digit', 
                                          year: 'numeric' 
                                        })}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 text-sm">-</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-5 whitespace-nowrap border-r border-gray-200">
                                    {task.incorporation_in_by_laws !== null && task.incorporation_in_by_laws !== undefined ? (
                                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                        task.incorporation_in_by_laws ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                      }`}>
                                        {task.incorporation_in_by_laws ? 'Yes' : 'No'}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 text-sm">-</span>
                                    )}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="px-6 py-5 whitespace-nowrap border-r border-gray-200">
                                    <span className="text-gray-400 text-sm">-</span>
                                  </td>
                                  <td className="px-6 py-5 whitespace-nowrap border-r border-gray-200">
                                    <span className="text-gray-400 text-sm">-</span>
                                  </td>
                                  <td className="px-6 py-5 whitespace-nowrap border-r border-gray-200">
                                    <span className="text-gray-400 text-sm">-</span>
                                  </td>
                                </>
                              )}
                            </>
                          )}
                          
                          {/* Training Type and Participants Columns - Only for Task 3, Subtasks 2, 3, 4 with actual training data */}
                        {hasTrainingType && (
                          <>
                              <td className="px-6 py-5 border-r border-gray-200">
                                {shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' ? (
                                  <div className="flex flex-col gap-1">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg border border-teal-200 text-sm font-medium">
                                  <Building2 className="h-3.5 w-3.5" />
                                  {task.training_type}
                                </span>
                                    {task.training_mode && (
                                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-semibold ${
                                        task.training_mode.toLowerCase() === 'online' 
                                          ? 'bg-green-100 text-green-700 border border-green-300' 
                                          : 'bg-gray-100 text-gray-700 border border-gray-300'
                                      }`}>
                                        <div className={`w-2 h-2 rounded-full ${
                                          task.training_mode.toLowerCase() === 'online' 
                                            ? 'bg-green-500' 
                                            : 'bg-gray-500'
                                        }`}></div>
                                        {task.training_mode}
                                      </span>
                              )}
                                  </div>
                                ) : null}
                            </td>
                              <td className="px-6 py-5 border-r border-gray-200">
                                {shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && task.training_start_date ? (
                                  <div className="text-sm text-gray-700">
                                    {new Date(task.training_start_date).toLocaleDateString('en-GB', { 
                                      day: '2-digit', 
                                      month: '2-digit', 
                                      year: 'numeric' 
                                    })}
                                    {task.training_end_date && task.training_end_date !== task.training_start_date && (
                                      <span className="text-gray-500 block"> - {new Date(task.training_end_date).toLocaleDateString('en-GB', { 
                                        day: '2-digit', 
                                        month: '2-digit', 
                                        year: 'numeric' 
                                      })}</span>
                                    )}
                                  </div>
                                ) : null}
                              </td>
                              <td className="px-6 py-5 whitespace-normal break-words align-middle border-r border-gray-200 table-col-150">
                                {shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' ? (
                                  (() => {
                                    // Prefer name from API; if missing, resolve ID via dropdown districts
                                    const nameFromApi = task.training_district_name;
                                    const idFromApi = task.training_district;
                                    const cellStateId = task.cell_state_id;
                                    let resolvedName = nameFromApi;

                                    // Only use fallback lookup if name is missing AND district belongs to cell's state
                                    if (!resolvedName && idFromApi && cellStateId && dropdownData?.districts) {
                                      const match = dropdownData.districts.find(
                                        (d) => String(d.district_id) === String(idFromApi) && 
                                               String(d.state_id) === String(cellStateId)
                                      );
                                      resolvedName = match?.district_name || null;
                                    }

                                    return resolvedName ? (
                                      <span className="text-sm text-gray-700">{resolvedName}</span>
                              ) : (
                                    <span className="text-gray-400 text-sm">-</span>
                                    );
                                  })()
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>
                              <td className="px-6 py-5 border-r border-gray-200">
                                {shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_total_attendees !== null && task.training_total_attendees !== undefined) ? (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-200 text-sm font-medium">
                                    {task.training_total_attendees || 0}
                                  </span>
                                ) : null}
                              </td>
                              <td className="px-6 py-5 border-r border-gray-200">
                                {shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_government_sector_attendees !== null && task.training_government_sector_attendees !== undefined) ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg border border-blue-200 text-sm font-medium">
                                  {task.training_government_sector_attendees || 0}
                                </span>
                                ) : null}
                            </td>
                              <td className="px-6 py-5 border-r border-gray-200">
                                {shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_total_women !== null && task.training_total_women !== undefined) ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-pink-50 text-pink-700 rounded-lg border border-pink-200 text-sm font-medium">
                                  {task.training_total_women || 0}
                                </span>
                                ) : null}
                              </td>
                              <td className="px-6 py-5 border-r border-gray-200">
                                {shouldShowTrainingColumns(task) && task.training_type && task.training_type !== '' && (task.training_architects_count !== null && task.training_architects_count !== undefined) ? (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg border border-amber-200 text-sm font-medium">
                                    {task.training_architects_count || 0}
                                  </span>
                                ) : null}
                            </td>
                          </>
                        )}
                          
                          {/* Building Columns (conditional - Task 2 and Task 4) */}
                          {hasBuildingData && (
                            <>
                              <td className="px-6 py-5 whitespace-normal break-words align-middle border-r border-gray-200 table-col-200">
                                {shouldShowBuildingColumns(task) && task.building_name ? (
                                  <span className="text-sm font-medium text-gray-900">
                                    {task.building_name}
                                </span>
                              ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                              <td className="px-6 py-5 whitespace-normal break-words align-middle border-r border-gray-200 table-col-250">
                                {shouldShowBuildingColumns(task) && task.building_address ? (
                                  <span className="text-sm text-gray-700">
                                    {task.building_address}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                              <td className="px-6 py-5 whitespace-normal break-words align-middle border-r border-gray-200 table-col-150">
                                {shouldShowBuildingColumns(task) && task.building_typology ? (
                                  <span className="text-sm text-gray-700">
                                    {task.building_typology}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap align-middle border-r border-gray-200 table-col-150">
                                {shouldShowBuildingColumns(task) && task.building_status ? (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200">
                                    {task.building_status}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                              <td className="px-6 py-5 whitespace-normal break-words align-middle border-r border-gray-200 table-col-180">
                                {shouldShowBuildingColumns(task) && task.building_nodal_contact_name ? (
                                  <span className="text-sm text-gray-700">
                                    {task.building_nodal_contact_name}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                              <td className="px-6 py-5 whitespace-normal break-words align-middle border-r border-gray-200 table-col-200">
                                {shouldShowBuildingColumns(task) && task.building_nodal_email ? (
                                  <a 
                                    href={`mailto:${task.building_nodal_email}`}
                                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    {task.building_nodal_email}
                                  </a>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>
                              <td className="px-6 py-5 whitespace-normal break-words border-r border-gray-200">
                                {shouldShowBuildingColumns(task) && task.building_nodal_department ? (
                                  <span className="text-sm text-gray-700">
                                    {task.building_nodal_department}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap border-r border-gray-200">
                                {shouldShowBuildingColumns(task) && task.building_epi_value_proposed !== null && task.building_epi_value_proposed !== undefined ? (
                                  <span className="text-sm font-medium text-gray-700">
                                    {parseFloat(task.building_epi_value_proposed).toFixed(4)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap border-r border-gray-200">
                                {shouldShowBuildingColumns(task) && task.building_epi_ratio !== null && task.building_epi_ratio !== undefined ? (
                                  <span className="text-sm font-semibold text-green-700 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200">
                                    {parseFloat(task.building_epi_ratio).toFixed(4)}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                          </>
                        )}
                          
                          {/* Task Completion Date */}
                          {taskDetailsTab !== 'notification' && (
                          <td className="px-6 py-5 whitespace-nowrap align-middle border-r border-gray-200">
                            <span className="text-sm text-gray-700">
                              {task.task_date || task.Task_Date
                                ? new Date(task.task_date || task.Task_Date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                : <span className="text-gray-400">-</span>}
                            </span>
                          </td>
                          )}
                          
                          {/* Summary Column - Always render, no rowSpan */}
                          {hasTask234 ? (
                            <td className="px-6 py-5 whitespace-normal break-words align-middle border-r border-gray-200 table-col-200">
                              {(() => {
                                const taskNo = task.task_no || task.Task_No;
                                // Only show summary for Task 2, 3, or 4
                                if (taskNo === 2 || taskNo === 3 || taskNo === 4) {
                                  const summary = calculateSummary(task, tasksForTaskDetailsView);
                                  return summary ? (
                                    <span className="text-sm text-gray-700 font-medium">{summary}</span>
                                  ) : (
                                    <span className="text-gray-400 text-sm">-</span>
                                  );
                                }
                                // For Task 5 and others, show empty
                                return <span className="text-gray-400 text-sm">-</span>;
                              })()}
                            </td>
                          ) : null}
                          
                          {/* Remarks Column */}
                          <td className="px-6 py-5 whitespace-normal break-words align-middle border-r border-gray-200 table-col-300 table-col-max-500">
                            {(() => {
                              const remarks = task.remarks || task.Remarks || task.remarks_text || task.remarksText;
                              if (remarks && typeof remarks === 'string' && remarks.trim() !== '') {
                                // Check if it's actually a date (format: DD/MM/YYYY or YYYY-MM-DD)
                                const datePattern = /^\d{2}\/\d{2}\/\d{4}$|^\d{4}-\d{2}-\d{2}/;
                                if (datePattern.test(remarks.trim())) {
                                  // This is a date, not remarks - return empty
                                  return <span className="text-gray-400">-</span>;
                                }
                                return (
                                  <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none word-break-safe">
                                    <RemarksWithStakeholder remarks={remarks} />
                                  </div>
                                );
                              }
                              return <span className="text-gray-400">-</span>;
                            })()}
                        </td>
                          
                          {/* Attachments Column */}
                          {taskDetailsTab !== 'notification' && (
                          <td className="px-6 py-5 whitespace-nowrap border-r border-gray-200">
                          {(() => {
                            const documentPathRaw = task.document_path || task.Document_Path || task.documentPath;
                            // Check if this is actually summary text (contains "Total" or "Building(s)")
                            if (documentPathRaw && typeof documentPathRaw === 'string' && (documentPathRaw.includes('Total') || documentPathRaw.includes('Building(s)'))) {
                              // This is summary text, not a document path - return empty
                              return <span className="text-sm text-gray-400">-</span>;
                            }
                            const documentPaths = parseDocumentPaths(documentPathRaw);
                            if (documentPaths.length === 0) {
                              return <span className="text-sm text-gray-400">-</span>;
                            } else if (documentPaths.length === 1) {
                              const docPath = documentPaths[0].startsWith('/') ? documentPaths[0] : `/${documentPaths[0]}`;
                              return (
                            <a
                                  href={`${FILE_BASE_URL}${docPath}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg border border-amber-200 text-sm font-medium hover:bg-amber-100 hover:border-amber-300 transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <Paperclip className="h-3.5 w-3.5" />
                              <Download className="h-3.5 w-3.5" />
                              View
                            </a>
                              );
                            } else {
                              return (
                                <div className="flex flex-col gap-1">
                                  {documentPaths.map((path, idx) => {
                                    const docPath = path.startsWith('/') ? path : `/${path}`;
                                    return (
                                      <a
                                        key={idx}
                                        href={`${FILE_BASE_URL}${docPath}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-50 text-amber-700 rounded text-xs font-medium hover:bg-amber-100 transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                      >
                                        <Download className="h-3 w-3" />
                                        View {idx + 1}
                                      </a>
                                    );
                                  })}
                                </div>
                              );
                            }
                          })()}
                        </td>
                          )}
                          
                          {/* Submission Date Column */}
                          {taskDetailsTab !== 'notification' && (
                        <td className="px-6 py-5 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">
                              {(() => {
                                const submissionDate = task.submission_date || task.Submission_Date || task.submissionDate;
                                const savedDate = task.saved_date || task.Saved_Date || task.savedDate;
                                
                                // Check if submissionDate is actually remarks text (not a date)
                                if (submissionDate && typeof submissionDate === 'string') {
                                  const datePattern = /^\d{2}\/\d{2}\/\d{4}$|^\d{4}-\d{2}-\d{2}/;
                                  if (!datePattern.test(submissionDate.trim()) && submissionDate.length > 10) {
                                    // This looks like text, not a date - try savedDate instead
                                    if (savedDate && typeof savedDate === 'string') {
                                      const savedDatePattern = /^\d{2}\/\d{2}\/\d{4}$|^\d{4}-\d{2}-\d{2}/;
                                      if (savedDatePattern.test(savedDate.trim())) {
                                        return new Date(savedDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                      }
                                    }
                                    return '-';
                                  }
                                  if (datePattern.test(submissionDate.trim())) {
                                    return new Date(submissionDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                  }
                                }
                                
                                if (savedDate && typeof savedDate === 'string') {
                                  const datePattern = /^\d{2}\/\d{2}\/\d{4}$|^\d{4}-\d{2}-\d{2}/;
                                  if (datePattern.test(savedDate.trim())) {
                                    return new Date(savedDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
                                  }
                                }
                                
                                return '-';
                              })()}
                            </span>
                          </div>
                        </td>
                          )}
                      </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
              <div
                ref={tableScrollBottomMirrorRef}
                className="dashboard-table-scroll dashboard-table-scroll--bottom w-full min-w-0 max-w-full bg-white"
                onScroll={handleTableScrollSync}
              >
                <div ref={tableScrollBottomMirrorSpacerRef} className="h-px pointer-events-none" aria-hidden />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
